// ============================================================================
// Feuille de brouillon jugée par le NOYAU (moteur v18, refonte lot 4 —
// docs/moteur/REFONTE_MOTEUR.md).
// ============================================================================
// Une feuille dont le modèle porte `kernel` ne passe pas par l'ancien juge : chaque
// ligne de l'élève est vérifiée par le plugin algèbre du noyau (règles déclaratives,
// preuve exacte), et ce module traduit le résultat (`StepCheck`) en verdict de la
// feuille (`Verdict`) — mêmes genres, même économie HORA que l'ancien juge.
//
//   - une ligne se juge depuis les lignes DE CONFIANCE (départ, puis lignes validées,
//     la plus récente d'abord) : toutes sont équivalentes au départ, donc une
//     réfutation depuis l'une vaut pour toutes ;
//   - résoudre exige l'ÉQUIVALENCE : une ligne qui ajoute des solutions
//     (« 3x < 6 » → « x ≤ 2 », « x − 2 = 0 » → « x = 2 ou x = −2 ») est refusée ;
//   - un pas à la fois : une règle reconnue (une opération des deux côtés), ou un
//     calcul qui ne change rien d'autre ; un saut de plusieurs opérations est
//     « juste, mais trop d'un coup » ;
//   - utile = plus proche de la réponse que tout ce qui est déjà écrit (inconnue
//     plus isolée, ou même ligne mieux calculée) ;
//   - une condition non garantie par les hypothèses (diviser par m − 1 sans savoir
//     son signe) est affichée telle quelle ; « je ne sais pas » (`unknown`) a son
//     propre message et la ligne peut être gardée, sans HORA, sans servir de source.
// Le coup de pouce suit le corrigé de référence du modèle tant que l'élève y est,
// sinon la recherche du noyau depuis sa ligne la plus avancée.
// ============================================================================

import { checkSyntax, formatNumber, normalize, normalizeChips, type Chip } from './lexer';
import {
  integerBoundAnswer,
  usesKernelPoly,
  modelEngineVersion,
  KERNEL_V47_ENGINE_VERSION,
  usesKernelCondition,
  usesKernelTrig,
  type KernelHypothesis,
  type KernelForm,
  type KernelSpec,
  type ScratchModel,
  type TrapSpec,
} from './model';
import { lineAtoms, lineText, parseLine, parseText, roundTo, type Line } from './tree';
import {
  ALGEBRA_PACKS,
  checkAlgebraStatements,
  checkApproximation,
  type AngleDomain,
} from './kernel/plugins/algebra';
import { approximationOf } from './kernel/legacy';
import { searchAlgebra, type SearchResult } from './kernel/plugins/algebra/search';
import { loadRulePack, type LoadedRule } from './kernel/rules/loader';
import {
  compareTerms,
  evalFloat,
  normalize as normalForm,
  polyIsZero,
  rfSimplify,
  rfToTerm,
} from './kernel/normal';
import * as Q from './kernel/exact';
import {
  canon,
  mapStatement,
  parseStatement,
  relationsIn,
  statementKey,
  statementOfLine,
  statementSymbols,
  statementText,
  sub,
  substitute,
  symbolsOf,
  termKey,
  termText,
  type Rel,
  type Relation,
  type Statement,
  type Term,
} from './kernel/term';
import type { Condition, Goal, StepCheck } from './kernel/types';
import {
  commitSysLine,
  createSystemState,
  findSysMoves,
  isContradictionForm,
  isTrivialForm,
  makeSysLine,
  moveOrigin,
  preparesCombination,
  redundancyKey,
  redundancyOf,
  sysClosestSource,
  sysConstantOnlyWrong,
  sysFollows,
  sysGoalReached,
  sysSources,
  sysWouldReach,
  unknownsOf,
  type SysCommit,
  type SysLine,
  type SysMove,
  type SystemState,
} from './kernel-system';
import { linearForm, sameEquation } from './kernel/system';
import {
  COUNT_CARD,
  countChanged,
  countDebtOf,
  countStep,
  countWeight,
  extraArgs,
  hasDivision,
  type CountDebt,
} from './kernel-count';
import {
  familyIndex,
  inTrigDomain,
  piCoef,
  piText,
  solveEquality,
  trigDomainOf,
  type Family,
  type TrigDomain,
} from './kernel/trig';
import {
  hasFamilies,
  isValueLine,
  trigClauses,
  trigH,
  trigNodesOfRelation,
  trigRelForm,
  trigSolved,
  type TrigForm,
} from './kernel-trig';
import {
  auxKind,
  coefficientDebt,
  factorsOf,
  isSolvedSet,
  isZeroTerm,
  polyH,
  relForm,
  squareBase,
  cubeBase,
  trinomialOf,
  writtenDen,
  type PolyForm,
  type Trinomial,
} from './kernel-poly';
import {
  combinedNumeratorTerms,
  formFactorWhy,
  formShape,
  numeratorTermCount,
  unexpanded,
  type FormShape,
} from './kernel-form';
import {
  checkSignTable,
  expectedSignTable,
  isSignTableText,
  parseSignTable,
  signTableCandidates,
  type ExpectedSignTable,
} from './kernel-signs';

/* ------------------------------ règles ------------------------------ */

let RULES: readonly LoadedRule[] | null = null;
/** Les règles des paquets du plugin algèbre, chargées (et auto-testées) une fois. */
export function kernelRules(): readonly LoadedRule[] {
  if (!RULES) {
    RULES = ALGEBRA_PACKS.flatMap((p) => {
      const r = loadRulePack(p);
      if (!r.ok) throw new Error('paquet de règles du noyau invalide');
      return r.pack.rules;
    });
  }
  return RULES;
}
let RULES_BEFORE_47: readonly LoadedRule[] | null = null;
let RULES_BEFORE_49: readonly LoadedRule[] | null = null;
let RULES_49: readonly LoadedRule[] | null = null;
/**
 * Les règles d'une feuille : le paquet `log-core` (logarithme décimal, moteur v47) n'est
 * jamais proposé à une feuille d'avant (sinon « 10ˣ = 50 → x = log(50) » deviendrait un
 * outil direct et changerait le corrigé des feuilles publiées) ; le paquet
 * `condition-core` (moteur v49) seulement aux feuilles à conditions.
 */
export function kernelRulesFor(v47: boolean, v49 = false): readonly LoadedRule[] {
  if (v49) {
    if (!RULES_49) RULES_49 = kernelRules();
    return RULES_49;
  }
  if (v47) {
    if (!RULES_BEFORE_49)
      RULES_BEFORE_49 = kernelRules().filter((r) => !r.qid.startsWith('condition.'));
    return RULES_BEFORE_49;
  }
  if (!RULES_BEFORE_47)
    RULES_BEFORE_47 = kernelRules().filter(
      (r) => !r.qid.startsWith('log.') && !r.qid.startsWith('condition.'),
    );
  return RULES_BEFORE_47;
}
/** Identifiants qualifiés des règles connues (« inequality.divide_negative@1 »). */
export function kernelRuleIds(): string[] {
  return kernelRules().map((r) => r.qid);
}

/** Identifiants des pas du noyau qui ne sont pas des règles d'un paquet. */
export const KERNEL_STEP_IDS = {
  compute: 'kernel.compute@1',
  expand: 'kernel.expand@1',
  swap: 'kernel.swap@1',
  /** moteur v20 : arrondir la réponse exacte (« AC ≈ 7,8 ») */
  round: 'kernel.round@1',
  /** moteur v21 : retrouver un angle (« cos(Â) = 0,6 » → « Â = arccos(0,6) ») */
  inverse: 'trig.inverse@1',
  /** moteur v22 (systèmes) : combiner deux lignes, remplacer une inconnue isolée */
  combine: 'kernel.combine@1',
  substitute: 'kernel.substitute@1',
  /** moteur v22 : deux équations du système disent la même chose */
  redundant: 'kernel.redundant@1',
  /** moteur v27 : un seuil se conclut par le plus petit entier (« n ≥ 4 ») */
  integerBound: 'kernel.integer_bound@1',
  /** moteur v48 : tester un rang (seuil sans logarithme) */
  testValue: 'kernel.test_value@1',
  /** moteur v49 : écrire une condition (« x > 0 » pour ln(x)) */
  condition: 'kernel.condition@1',
  /** moteur v49 : tenir compte de la condition (« x < e » et « x > 0 » → « 0 < x < e ») */
  conditionKeep: 'kernel.condition_keep@1',
  /** moteur v33 : factoriser, faire apparaître un facteur commun ou des carrés */
  factor: 'kernel.factor@1',
} as const;

/**
 * Pas du second degré (moteur v23, méthode « factor ») : des outils du cours, comme les
 * règles d'un paquet (une question qui se conclut en UN de ces pas reste ouverte).
 */
export const POLY_STEP_IDS = {
  allOnOneSide: 'poly.all_on_one_side@1',
  factor: 'poly.factor@1',
  canonical: 'poly.canonical@1',
  zeroProduct: 'poly.zero_product@1',
  squareRoot: 'poly.square_root@1',
  squareCompare: 'poly.square_compare@1',
  cubeRoot: 'poly.cube_root@1',
  cubeCompare: 'poly.cube_compare@1',
  signTable: 'poly.sign_table@1',
  /** moteur v42 : le tableau de signes dressé sur la feuille, puis lu */
  signTableFill: 'poly.sign_table_fill@1',
  signTableRead: 'poly.sign_table_read@1',
  clauses: 'poly.clauses@1',
  clearDenominator: 'poly.clear_denominator@1',
  sameDenominator: 'poly.same_denominator@1',
  discriminant: 'poly.discriminant@1',
  discriminantSign: 'poly.discriminant_sign@1',
  root: 'poly.root@1',
  roots: 'poly.roots@1',
  trinomialSign: 'poly.trinomial_sign@1',
  keep: 'poly.keep@1',
} as const;

/**
 * Pas des équations trigonométriques (moteur v24, méthode « trig ») : des outils du
 * cours, comme les règles d'un paquet.
 */
export const TRIG_STEP_IDS = {
  divideCos: 'trig.divide_cos@1',
  associated: 'trig.associated@1',
  knownValue: 'trig.known_value@1',
  families: 'trig.families@1',
  circle: 'trig.circle@1',
  interval: 'trig.interval@1',
} as const;

/* ------------------------------ verdict ------------------------------ */

export interface KernelVerdict {
  kind:
    | 'empty'
    | 'open'
    | 'building'
    | 'ready'
    | 'pending'
    | 'syntax'
    | 'trap'
    | 'false'
    | 'unjustified'
    | 'useless'
    | 'step'
    | 'dup'
    | 'given';
  atoms: string[];
  title?: string;
  message?: string;
  badChip?: number;
  complete?: boolean;
  line?: Line;
  final?: boolean;
  hora?: number;
  trap?: { line: Line; title: string; why: string };
  rule?: { id: string; card: string; label: string; hora: number; why: string };
  /** le noyau ne sait pas vérifier ce passage (statut `unknown`) */
  unknown?: boolean;
  /** ce que la ligne apporte à la feuille (lu par `commitKernelLine`) */
  kern?: {
    s: Statement;
    trusted: boolean;
    sys?: SysCommit & { origin: number };
    /** moteur v23 : Δ ou une racine du trinôme (« x₁ = 2 ») */
    aux?: AuxValue;
    /** moteur v23 : une ligne sur Δ ou une racine (« Δ > 0 » compris) */
    auxLine?: boolean;
    /** moteur v47 : la ligne est écrite avec l'inconnue auxiliaire (« X ») */
    auxWritten?: boolean;
    /** moteur v49 : la ligne n'est juste qu'avec les conditions écrites (pas la réponse) */
    condDep?: boolean;
  };
  /** moteur v20 : la ligne d'arrondi de la réponse (sa valeur) */
  round?: number;
  /** moteur v48 : un rang testé (méthode « test ») et le verdict de l'inégalité */
  test?: { k: number; holds: boolean };
  /** moteur v49 : une condition écrite (indice dans `KernelSheetState.conds`) */
  cond?: number;
  /** moteur v42 : un tableau de signes juste (clé de la ligne étudiée) */
  table?: string;
  /** moteur v42 : la case fautive du tableau (ligne -1 : la ligne des x) */
  cell?: { row: number; col?: number };
}

/* ------------------------------ état ------------------------------ */

interface KLine {
  s: Statement;
  /** écriture exacte (ce que l'élève a écrit) */
  raw: string;
  /** forme canonique légère (nombres calculés) */
  K: string;
  /** chaque membre réduit, sens normalisé : deux écritures d'une MÊME ligne */
  NK: string | null;
  /** distance à la réponse (inconnue à isoler) */
  h: number;
  /** calculs encore à faire dans l'écriture (nombres à calculer, termes semblables) */
  calc: number;
  /** produits encore à développer */
  expand: number;
  /** calc + développements : ce qui reste à faire sans changer la ligne */
  debt: number;
  /** moteur v20 : calculs sur les racines et les valeurs exactes (compris dans calc) */
  rad: RadDebt;
  trusted: boolean;
  /** moteur v23 : une ligne sur Δ ou une racine (jamais une source, mais écrite) */
  aux?: boolean;
  /** moteur v27 : calculs de dénombrement (« k parmi n », factorielles) de la ligne écrite */
  cnt?: CountDebt;
  /**
   * moteur v27 : la ligne a-t-elle la FORME de la réponse ? (dénombrement à simplifier :
   * la valeur ou l'écriture attendue ; seuil : « n ≥ 4 », un entier). Définie, elle
   * décide seule si la ligne conclut.
   */
  answerForm?: boolean;
  /** moteur v33 : forme de l'écriture (développer, factoriser) */
  fm?: FormShape;
  /** moteur v47 : la ligne est écrite avec l'inconnue auxiliaire (« X² − 3X + 2 = 0 ») */
  auxWritten?: boolean;
  /** moteur v49 : juste seulement avec les conditions écrites (jamais la réponse) */
  condDep?: boolean;
}

/** Feuille qui dénombre (moteur v27) : la réponse attendue d'une simplification. */
export interface CountMode {
  /** membre de droite de la réponse « N = … » (objectif `simplify`) ; null sinon */
  answer: Term | null;
}
/** Ce que `kline` mesure en plus selon la feuille (moteur v27). */
interface KlineMode {
  /** moteur v33 : forme attendue (développer, factoriser) */
  form?: KernelForm | null;
  /** moteur v33 : écriture de la réponse (clé canonique), acceptée telle quelle */
  formAnswer?: string;
  count?: CountMode | null;
  /** seuil : la réponse est « n ≥ 4 » (inconnue entière face à un entier) */
  intBound?: boolean;
  /** moteur v47 : la réponse est large (« n ≥ 2501 ») : « n > 2500 » n'est pas fini */
  intLarge?: boolean;
  /** moteur v47 : mesures de la feuille v47 (termes en x regroupés, logarithmes à réunir) */
  v47?: boolean;
  /** moteur v47 : l'inconnue auxiliaire de la feuille (X = eˣ, X = ln(x)) */
  aux?: AuxMode | null;
  /** moteur v47 : la ligne est ÉCRITE avec l'inconnue auxiliaire (« X² − 3X + 2 = 0 ») */
  auxWritten?: boolean;
}

/** moteur v47 : l'inconnue auxiliaire d'une feuille d'algèbre (`kernel.auxiliary`) */
export interface AuxMode {
  name: string;
  of: 'exp' | 'ln';
}
/** eˣ ou ln(x) : ce que remplace l'inconnue auxiliaire */
function auxAtom(a: AuxMode, x: string): Term {
  return { k: 'fn', f: a.of === 'exp' ? 'exp' : 'ln', a: { k: 'sym', n: x } } as Term;
}
/** « X² − 3X + 2 = 0 » lue en x : X remplacée par eˣ (ou ln(x)) */
function fromAux(s: Statement, a: AuxMode, x: string): Statement {
  const atom = auxAtom(a, x);
  const sub = (t: Term): Term => {
    switch (t.k) {
      case 'sym':
        return t.n === a.name ? atom : t;
      case 'add':
      case 'mul':
        return { ...t, c: t.c.map(sub) } as Term;
      case 'div':
      case 'pow':
      case 'binom':
        return { ...t, a: sub(t.a), b: sub(t.b) } as Term;
      case 'fn':
        return { ...t, a: sub(t.a) };
      default:
        return t;
    }
  };
  return mapStatement(s, sub);
}
/** tous les eᵘ de la ligne sont des eˣ (« (eˣ)² − 3eˣ + 2 = 0 ») : la ligne est prête pour X */
function expAtomsOnly(s: Statement, x: string): boolean {
  let seen = false;
  let ok = true;
  const walk = (t: Term): void => {
    if (t.k === 'fn' && t.f === 'exp') {
      seen = true;
      if (!(t.a.k === 'sym' && t.a.n === x)) ok = false;
      return;
    }
    switch (t.k) {
      case 'add':
      case 'mul':
        t.c.forEach(walk);
        return;
      case 'div':
      case 'pow':
      case 'binom':
        walk(t.a);
        walk(t.b);
        return;
      case 'fn':
        walk(t.a);
        return;
      default:
        return;
    }
  };
  for (const r of relationsOf(s)) {
    walk(r.l);
    walk(r.r);
  }
  return seen && ok;
}
/** nombre de clauses « X = c » avec c ≤ 0 (« eˣ = −1 » : sans solution, à écarter) */
function impossibleExpClauses(s: Statement, X: string): number {
  let n = 0;
  for (const r of relationsOf(s)) if (impossibleExpClause(r, X)) n++;
  return n;
}
/** « X = c », « X ≤ c » ou « X < c » avec c ≤ 0 : impossible quand X = eˣ > 0 */
function impossibleExpClause(r: Relation, X: string): boolean {
  if (r.l.k !== 'sym' || r.l.n !== X) return false;
  if (r.rel !== '=' && r.rel !== '≤' && r.rel !== '<') return false;
  const v = reduced(r.r);
  return v?.k === 'num' && Q.sign(v.v) <= 0;
}
/** k quand t vaut k × x (k entier non nul) ; null sinon */
function integerMultipleOf(t: Term, x: string): bigint | null {
  const r = reduced(t);
  if (!r) return null;
  if (r.k === 'sym' && r.n === x) return 1n;
  if (r.k === 'mul' && r.c.length === 2) {
    const [a, b] = r.c;
    const num = a!.k === 'num' ? a : b!.k === 'num' ? b : null;
    const sym = a!.k === 'sym' ? a : b!.k === 'sym' ? b : null;
    if (num?.k === 'num' && sym?.k === 'sym' && sym.n === x && num.v.d === 1n && num.v.n !== 0n)
      return num.v.n;
  }
  return null;
}
/**
 * La ligne écrite en X (eˣ → X, e^(2x) → X², ln(x) → X) ; null si l'inconnue x y reste
 * ailleurs (« x = ln(2) » : la ligne est revenue à x).
 */
function auxStatement(s: Statement, a: AuxMode, x: string): Statement | null {
  const X: Term = { k: 'sym', n: a.name };
  const map = (t: Term): Term => {
    if (t.k === 'fn') {
      if (a.of === 'exp' && t.f === 'exp') {
        const k = integerMultipleOf(t.a, x);
        if (k !== null) {
          const m = k < 0n ? -k : k;
          const p =
            m === 1n ? X : ({ k: 'pow', a: X, b: { k: 'num', v: Q.rat(Number(m)) } } as Term);
          // e^(−x) = 1 ÷ X
          return k < 0n ? ({ k: 'div', a: { k: 'num', v: Q.rat(1) }, b: p } as Term) : p;
        }
      }
      if (a.of === 'ln' && t.f === 'ln') {
        const r = reduced(t.a);
        if (r?.k === 'sym' && r.n === x) return X;
      }
      return { ...t, a: map(t.a) };
    }
    switch (t.k) {
      case 'add':
      case 'mul':
        return { ...t, c: t.c.map(map) } as Term;
      case 'div':
      case 'pow':
      case 'binom':
        return { ...t, a: map(t.a), b: map(t.b) } as Term;
      default:
        return t;
    }
  };
  const m = mapStatement(s, map);
  return relationsOf(m).some((r) => has(r.l, x) || has(r.r, x)) ? null : m;
}

export interface KernelSheetState {
  spec: KernelSpec;
  x: string;
  goal: Goal;
  hyps: Condition[];
  rules: readonly LoadedRule[];
  start: KLine;
  answer: Statement;
  /** lignes écrites par l'élève (validées ou gardées), dans l'ordre */
  lines: KLine[];
  reached: boolean;
  traps: { line: Line; K: string; title: string; why: string }[];
  /** corrigé de référence (textes), calculé à la demande */
  ref: string[] | null;
  /** atomes des lignes connues bonnes : une ligne qui en est le début n'est pas encore jugée */
  known: string[][] | null;
  cache: Map<string, StepCheck>;
  /** moteur v20 : la question demande une valeur arrondie de la réponse */
  rounding: { places: number; hora: number } | null;
  /** valeur arrondie écrite (« AC ≈ 7,8 »), null tant qu'elle manque */
  rounded: number | null;
  /** moteur v21 : angles mesurés en degrés (null : aucune) */
  angles: Record<string, AngleDomain> | null;
  /** moteur v22 : système d'équations (null : une seule relation) */
  sys: SystemState | null;
  /** moteur v23 : méthode « factor » (second degré, produit, quotient, ensembles) */
  poly: boolean;
  /** moteur v23 : Δ et racines écrits (« Δ = 1 », « x₁ = 2 ») */
  aux: Map<string, AuxValue>;
  /** moteur v24 : équation trigonométrique (méthode « trig ») ; null sinon */
  trig: TrigMode | null;
  /** moteur v27 : feuille qui dénombre (« k parmi n », factorielles) ; null sinon */
  count: CountMode | null;
  /** moteur v27 : seuil qui se conclut par un entier (« n ≥ 4 ») */
  intBound: boolean;
  /** moteur v33 : écriture à développer ou à factoriser ; null sinon */
  form: KernelForm | null;
  /** moteur v33 : écriture de la réponse (clé canonique) */
  formAnswer?: string;
  /** moteur v42 : tableaux de signes dressés (clés des lignes étudiées) */
  tables: string[];
  /** moteur v47 : libellés lisibles (« Diviser par 0,4 ») — feuille en v47 ou plus */
  readable: boolean;
  /** moteur v47 : la réponse d'un seuil est large (« n ≥ 2501 ») */
  intLarge: boolean;
  /** moteur v47 : l'inconnue auxiliaire (X = eˣ, X = ln(x)) ; null sinon */
  auxiliary: AuxMode | null;
  /** moteur v48 : seuil par tests de valeurs (méthode « test ») */
  test: boolean;
  /** moteur v48 : rangs testés → l'inégalité est-elle vérifiée ? */
  tests: Map<number, boolean>;
  /** moteur v49 : les conditions de la feuille (`kernel.conditions`), écrites ou non */
  conds: SheetCondition[];
  /** moteur v49 : les conditions écrites, en hypothèses du noyau */
  condsOk: Condition[];
}

/** moteur v49 : une condition à écrire (« x > 0 » : ln(x) existe) */
export interface SheetCondition {
  s: Statement;
  /** clé de la ligne telle qu'écrite dans le modèle */
  K: string;
  /** la condition, en hypothèse du noyau (« x − 3 » positif ou nul) */
  cond: Condition;
  label: string;
  why: string;
  /** écrite par l'élève */
  ok: boolean;
}

/** Équation trigonométrique (moteur v24) : intervalle de l'énoncé, entiers des familles. */
export interface TrigMode {
  /** « −π < x ≤ π » (null : sur ℝ) */
  domain: Statement | null;
  dom: TrigDomain | null;
  /** l'intervalle est borné : les familles ne sont pas la réponse */
  bounded: boolean;
  /** les entiers des familles (« k ») */
  params: Set<string>;
  /** l'intervalle écrit pour l'élève */
  text: string;
}

/** Une grandeur auxiliaire du second degré écrite sur la feuille (moteur v23). */
export interface AuxValue {
  name: string;
  /** sa valeur exacte */
  value: Term;
  /** calculs encore à faire dans son écriture */
  debt: number;
}

function hypothesisCondition(h: KernelHypothesis): Condition {
  // un angle (moteur v21) est une mesure positive ; son domaine passe au noyau à part
  if (h.kind === 'acute' || h.kind === 'angle') return { kind: 'positive', of: [h.of] };
  return { kind: h.kind, of: [h.of] };
}
function anglesOf(spec: KernelSpec): Record<string, AngleDomain> | null {
  const out: Record<string, AngleDomain> = {};
  (spec.hypotheses ?? []).forEach((h) => {
    if (h.kind === 'acute' || h.kind === 'angle') out[h.of] = h.kind;
  });
  return Object.keys(out).length ? out : null;
}

/** « 60° » hors d'un cos : une mesure d'angle en degrés, soit 60 (moteur v21) */
function degreesOutside(t: Term): Term {
  switch (t.k) {
    case 'add':
    case 'mul':
      return { ...t, c: t.c.map(degreesOutside) };
    case 'div':
    case 'pow':
    case 'binom':
      return { ...t, a: degreesOutside(t.a), b: degreesOutside(t.b) };
    case 'fn':
      if (t.f === 'deg') return degreesOutside(t.a);
      return TRIG_FNS.has(t.f) ? t : { ...t, a: degreesOutside(t.a) };
    default:
      return t;
  }
}
/** Une ligne lue dans une feuille d'angles : les mesures écrites en degrés */
function inSheet(angles: Record<string, AngleDomain> | null, s: Statement): Statement {
  return angles ? mapStatement(s, degreesOutside) : s;
}
function approxIn(
  ks: KernelSheetState,
  ap: { l: Term; r: Term } | null,
): { l: Term; r: Term } | null {
  return ap && ks.angles ? { l: ap.l, r: degreesOutside(ap.r) } : ap;
}

function readStatement(text: string): Statement {
  const p = parseStatement(text);
  if (!p.ok) throw new Error(`ligne illisible pour le noyau : ${text}`);
  return p.s;
}

export function createKernelSheetState(model: ScratchModel): KernelSheetState {
  const spec = model.kernel!;
  const x = spec.goal.target;
  const angles = anglesOf(spec);
  const start = inSheet(angles, readStatement(spec.system?.equations[0] ?? spec.start));
  const traps = (model.traps ?? []).flatMap((t: TrapSpec) => {
    try {
      const line = parseText(t.line);
      const s = statementOfLine(line);
      if (!s.ok) return [];
      const read = inSheet(angles, s.s);
      const sa = spec.auxiliary
        ? fromAux(read, { name: spec.auxiliary.name, of: spec.auxiliary.of }, x)
        : read;
      return [{ line, K: keyOf(sa), title: t.title, why: t.why }];
    } catch {
      return [];
    }
  });
  const poly = usesKernelPoly(model);
  const trig = usesKernelTrig(model) ? trigModeOf(spec, x) : null;
  // écriture à factoriser (moteur v33) : la réponse de l'énoncé est acceptée telle quelle
  const formRight = spec.form ? valueSide(readStatement(spec.answer), x) : null;
  const formAnswer = formRight ? termKey(canon(formRight)) : undefined;
  const count = countModeOf(spec);
  const intBound = integerBoundAnswer(spec);
  // moteur v47 : libellés et mesures de la feuille v47 ; la réponse d'un seuil est large
  const v47 = modelEngineVersion(model) >= KERNEL_V47_ENGINE_VERSION;
  const intLarge = intBound && /[≤≥]/.test(spec.answer);
  const auxiliary: AuxMode | null = spec.auxiliary
    ? { name: spec.auxiliary.name, of: spec.auxiliary.of }
    : null;
  // moteur v49 : les conditions à écrire (« x > 0 » pour ln(x))
  const v49 = usesKernelCondition(model);
  const sys = spec.system
    ? createSystemState(
        spec.system.equations.map((t) => {
          const st = readStatement(t);
          return { s: st, K: keyOf(st), text: t };
        }),
        [...spec.system.unknowns],
        spec.system.outcome,
      )
    : null;
  return {
    spec,
    x,
    // un système se résout ligne à ligne : chaque passage d'une ligne est une résolution
    goal: { kind: spec.goal.kind === 'solve_system' ? 'solve' : spec.goal.kind, target: x },
    hyps: (spec.hypotheses ?? []).map(hypothesisCondition),
    rules: kernelRulesFor(v47, v49),
    start: kline(start, x, true, poly, trig, {
      count,
      intBound,
      form: spec.form ?? null,
      formAnswer,
      ...(v47 ? { v47: true, intLarge } : {}),
      ...(auxiliary ? { aux: auxiliary } : {}),
    }),
    answer: inSheet(angles, readStatement(spec.answer)),
    lines: [],
    reached: false,
    traps,
    ref: null,
    known: null,
    cache: new Map(),
    rounding:
      model.rounding && (!model.rounding.symbol || model.rounding.symbol === x)
        ? { places: model.rounding.places, hora: model.rounding.hora ?? 1 }
        : null,
    rounded: null,
    angles,
    sys,
    poly,
    aux: new Map(),
    trig,
    count,
    intBound,
    form: spec.form ?? null,
    ...(formAnswer ? { formAnswer } : {}),
    tables: [],
    readable: v47,
    intLarge,
    auxiliary,
    test: spec.method === 'test',
    tests: new Map(),
    conds: (spec.conditions ?? []).flatMap((t) => {
      const c = sheetCondition(readStatement(t), start);
      return c ? [c] : [];
    }),
    condsOk: [],
  };
}

/* ------------------------------ conditions (moteur v49) ------------------------------ */

/** « x − 3 ≥ 0 », « x ≥ 3 » → la condition du noyau : x − 3 positif ou nul */
function conditionOf(s: Statement): Condition | null {
  if (s.k !== 'rel' || s.rel === '=') return null;
  // « a > b », « a ≥ b » : a − b ; « a < b », « a ≤ b » : b − a
  const up = s.rel === '>' || s.rel === '≥';
  const [p, n] = up ? [s.l, s.r] : [s.r, s.l];
  const u = canon({ k: 'add', c: [p, { k: 'mul', c: [{ k: 'num', v: Q.rat(-1) }, n] }] });
  return {
    kind: s.rel === '<' || s.rel === '>' ? 'positive' : 'nonnegative',
    of: [termText(withDecimals(u))],
  };
}

/** Une condition de la feuille : la condition du noyau et ce qui la justifie. */
function sheetCondition(s: Statement, start: Statement): SheetCondition | null {
  const cond = conditionOf(s);
  if (!cond) return null;
  const parsed = parseStatement(cond.of[0]!);
  const uKey = parsed.ok && parsed.s.k === 'expr' ? termKey(canon(parsed.s.e)) : null;
  const same = (t: Term): boolean => uKey !== null && termKey(canon(t)) === uKey;
  const shown = statementText(s);
  let label = 'Condition';
  let why = `Les solutions doivent vérifier ${shown}.`;
  const fns: { f: string; a: Term }[] = [];
  const walk = (t: Term): void => {
    if (t.k === 'fn') fns.push({ f: t.f, a: t.a });
    switch (t.k) {
      case 'add':
      case 'mul':
        t.c.forEach(walk);
        return;
      case 'div':
      case 'pow':
      case 'binom':
        walk(t.a);
        walk(t.b);
        return;
      case 'fn':
        walk(t.a);
        return;
      default:
        return;
    }
  };
  relationsIn(start).forEach((r) => {
    walk(r.l);
    walk(r.r);
  });
  const ln = fns.find((f) => f.f === 'ln' && same(f.a));
  const sq = fns.find((f) => f.f === 'sqrt' && same(f.a));
  const rels = relationsIn(start);
  const sign = rels.find(
    (r) =>
      r.rel === '=' &&
      ((r.l.k === 'fn' && r.l.f === 'sqrt' && same(r.r)) ||
        (r.r.k === 'fn' && r.r.f === 'sqrt' && same(r.l))),
  );
  if (ln) {
    label = 'Condition d’existence';
    why = `ln(${fmt(ln.a)}) n’existe que si ${fmt(ln.a)} > 0 : les solutions vérifient ${shown}.`;
  } else if (sq && cond.kind === 'nonnegative') {
    label = 'Condition d’existence';
    why = `√(${fmt(sq.a)}) n’existe que si ${fmt(sq.a)} ≥ 0 : les solutions vérifient ${shown}.`;
  } else if (sign && cond.kind === 'nonnegative') {
    label = 'Une racine est positive';
    why = `Une racine carrée est positive ou nulle : l’égalité demande ${shown}.`;
  }
  return { s, K: keyOf(s), cond, label, why, ok: false };
}

/** la ligne dit-elle la même chose que la réponse, sans les conditions ? (moteur v49) */
function answerLike(ks: KernelSheetState, T: KLine): boolean {
  const a = klineOf(ks, ks.answer, true);
  if (T.K === a.K) return true;
  const eq = (c: StepCheck): boolean =>
    c.status === 'valid' && (c.relation === 'equivalent' || c.relation === 'identity');
  return eq(check(ks, a, T.s, T.raw, true)) || eq(check(ks, T, a.s, a.raw, true));
}

/**
 * Une condition « du cours » : le domaine de ln ou de √, le signe d'un membre égal à une
 * racine carrée — vraie dès que le départ a un sens, sans que le noyau la démontre.
 */
const structuralCondition = (c: SheetCondition): boolean => c.label !== 'Condition';

/**
 * La réponse équivaut-elle au départ ? (porte, moteur v49) Sans les conditions d'abord ;
 * sinon AVEC elles quand elles sont toutes « du cours » (le départ les entraîne) :
 * √(x + 3) = x − 3 ⇔ x = 6 se montre avec x − 3 ≥ 0.
 */
export function kernelAnswerFollowsWithConditions(ks: KernelSheetState): boolean {
  if (!ks.conds.length || !ks.conds.every(structuralCondition)) return false;
  const a = klineOf(ks, ks.answer, true);
  const c = checkAlgebraStatements(ks.start.s, a.s, {
    rules: ks.rules,
    hypotheses: [...ks.hyps, ...ks.conds.map((x) => x.cond)],
    goal: ks.goal,
    deadline: Date.now() + 500,
  });
  return c.status === 'valid' && (c.relation === 'equivalent' || c.relation === 'identity');
}

/** les conditions écrites, lisibles (« x > 0 ») ; '' sans condition */
function conditionsText(ks: KernelSheetState): string {
  return ks.conds
    .filter((c) => c.ok)
    .map((c) => statementText(c.s))
    .join(' et ');
}

/** La ligne est-elle une condition de la feuille ? (moteur v49) */
function conditionVerdict(
  ks: KernelSheetState,
  T: KLine,
  E: Line,
  A: string[],
): KernelVerdict | null {
  const syms = statementSymbols(T.s);
  if (!syms.has(ks.x) || syms.size !== 1 || relationsOf(T.s).length !== 1) return null;
  const i = ks.conds.findIndex((c) => c.K === T.K || bareEquivalent(ks, c.s, T.s, T.raw));
  if (i < 0) return null;
  const c = ks.conds[i]!;
  if (c.ok)
    return {
      kind: 'dup',
      atoms: A,
      line: E,
      title: 'Déjà écrit',
      message: 'Tu as déjà écrit cette condition.',
    };
  return {
    kind: 'step',
    atoms: A,
    line: E,
    hora: 1,
    final: false,
    rule: { id: KERNEL_STEP_IDS.condition, card: 'Équations', label: c.label, hora: 1, why: c.why },
    cond: i,
  };
}

/** deux relations disent la même chose, sous les hypothèses de l'énoncé seulement */
function bareEquivalent(ks: KernelSheetState, a: Statement, b: Statement, bRaw: string): boolean {
  const key = `cond:${statementText(a)}→${bRaw}`;
  let c = ks.cache.get(key);
  if (!c) {
    c = checkAlgebraStatements(a, b, {
      rules: ks.rules,
      hypotheses: ks.hyps,
      goal: ks.goal,
      deadline: Date.now() + 250,
    });
    ks.cache.set(key, c);
  }
  return c.status === 'valid' && (c.relation === 'equivalent' || c.relation === 'identity');
}

/**
 * Une ligne juste seulement avec une condition pas encore écrite (« x < e » avant
 * « x > 0 ») : la condition qu'il faut écrire d'abord ; null sinon.
 */
function conditionNeeded(ks: KernelSheetState, T: KLine): SheetCondition | null {
  const missing = ks.conds.filter((c) => !c.ok);
  if (!missing.length) return null;
  const tries: SheetCondition[][] = [
    ...missing.map((c) => [c]),
    ...(missing.length > 1 ? [missing] : []),
  ];
  for (const extra of tries)
    for (const from of sources(ks)) {
      const key = `need:${extra.map((c) => c.K).join('&')}|${ks.condsOk.length}|${from.raw}→${T.raw}`;
      let c = ks.cache.get(key);
      if (!c) {
        c = checkAlgebraStatements(from.s, T.s, {
          rules: ks.rules,
          hypotheses: [...ks.hyps, ...ks.condsOk, ...extra.map((e) => e.cond)],
          goal: ks.goal,
          deadline: Date.now() + 250,
        });
        ks.cache.set(key, c);
      }
      if (c.status === 'valid' && (c.relation === 'equivalent' || c.relation === 'identity'))
        return extra[0]!;
    }
  return null;
}

/** La condition découle-t-elle de la ligne de départ ? (porte, moteur v49) */
export function kernelConditionFollows(ks: KernelSheetState, text: string): boolean {
  const p = parseStatement(text);
  if (!p.ok || p.s.k !== 'rel') return false;
  const syms = statementSymbols(p.s);
  if (!syms.has(ks.x) || syms.size !== 1) return false;
  // une condition du cours (domaine de ln ou de √, signe d'une racine) : vraie par le cours
  const sc = sheetCondition(p.s, ks.start.s);
  if (sc && structuralCondition(sc)) return true;
  const c = checkAlgebraStatements(ks.start.s, p.s, {
    rules: ks.rules,
    hypotheses: ks.hyps,
    goal: ks.goal,
    deadline: Date.now() + 500,
  });
  return c.status === 'valid' && c.relation === 'implies';
}

/**
 * Feuille qui dénombre (moteur v27) : une ligne du corrigé, le départ ou la réponse
 * écrit « parmi » ou une factorielle.
 */
function countModeOf(spec: KernelSpec): CountMode | null {
  const texts = [spec.start, spec.answer, ...(spec.path ?? [])];
  if (!texts.some((t) => t.includes('!') || /(^|[^A-Za-zÀ-ÿ])parmi([^A-Za-zÀ-ÿ]|$)/.test(t)))
    return null;
  if (spec.goal.kind !== 'simplify') return { answer: null };
  const a = readStatement(spec.answer);
  return { answer: a.k === 'rel' ? valueSide(a, spec.goal.target) : null };
}
/** `kline` d'une ligne de la feuille (méthode, trigonométrie, dénombrement, seuil). */
function klineOf(ks: KernelSheetState, s: Statement, trusted: boolean, auxWritten = false): KLine {
  return kline(s, ks.x, trusted, ks.poly, ks.trig, {
    count: ks.count,
    intBound: ks.intBound,
    form: ks.form,
    ...(ks.formAnswer ? { formAnswer: ks.formAnswer } : {}),
    ...(ks.readable ? { v47: true, intLarge: ks.intLarge } : {}),
    ...(ks.auxiliary ? { aux: ks.auxiliary, auxWritten } : {}),
  });
}
/** Une ligne lue dans la feuille : angles en degrés, inconnue auxiliaire remplacée (v47) */
function readIn(ks: KernelSheetState, s: Statement): Statement {
  const a = inSheet(ks.angles, s);
  return ks.auxiliary ? fromAux(a, ks.auxiliary, ks.x) : a;
}
/** la ligne écrit-elle l'inconnue auxiliaire (« X ») ? */
function writesAux(ks: KernelSheetState, s: Statement): boolean {
  return !!ks.auxiliary && statementSymbols(s).has(ks.auxiliary.name);
}

function trigModeOf(spec: KernelSpec, x: string): TrigMode {
  const params = new Set(
    (spec.hypotheses ?? [])
      .filter((h) => h.kind === 'integer' && /^[^\s+−×÷()]+$/.test(h.of))
      .map((h) => h.of),
  );
  const domain = spec.domain ? readStatement(spec.domain) : null;
  const dom = domain ? trigDomainOf(domain, x) : null;
  return {
    domain,
    dom,
    bounded: !!dom && dom.lo !== null && dom.hi !== null,
    params,
    text: spec.domain ?? '',
  };
}

/* ------------------------------ mesures ------------------------------ */

const relationsOf = (s: Statement): Relation[] => relationsIn(s);
function keyOf(s: Statement): string {
  return statementKey(mapStatement(s, (t) => canon(t)));
}
/** membre réduit (forme normale) ; null si le calcul échoue (division par zéro…) */
function reduced(t: Term): Term | null {
  const n = normalForm(t);
  return n.ok ? canon(rfToTerm(rfSimplify(n.rf))) : null;
}
const has = (t: Term, x: string): boolean => symbolsOf(t).has(x);
function occurrences(t: Term, x: string): number {
  switch (t.k) {
    case 'sym':
      return t.n === x ? 1 : 0;
    case 'num':
      return 0;
    case 'add':
    case 'mul':
      return t.c.reduce((s, c) => s + occurrences(c, x), 0);
    case 'div':
    case 'pow':
    case 'binom':
      return occurrences(t.a, x) + occurrences(t.b, x);
    case 'fn':
      return occurrences(t.a, x);
  }
}
/** produits de sommes encore à développer */
/** Opérations qui restent appliquées à x (0 : « x … » seul à gauche, sans x à droite). */
function isolation(r: Relation, x: string, v47 = false): number {
  const inL = has(r.l, x);
  const inR = has(r.r, x);
  if (!inL && !inR) return 0;
  let c = 0;
  if (inL && inR) c += 6;
  if (!inL) c += 1; // il faudra échanger les membres
  // moteur v47 : les termes en x d'une somme se regroupent (« x ln 2 − x ln 3 » est
  // « (ln 2 − ln 3) x »), une constante écrite avec ln, exp, log compte pour un bout
  const side = v47 ? collectX(inL ? r.l : r.r, x) : inL ? r.l : r.r;
  c += xsize(side, x, v47) - 1 + 2 * (occurrences(side, x) - 1);
  return c;
}
/** moteur v47 : « a x + b x + c » regroupé en « (a + b) x + c » (a, b sans x) */
function collectX(t: Term, x: string): Term {
  if (t.k !== 'add') return t;
  const coefOf = (u: Term): Term | null => {
    if (u.k === 'sym' && u.n === x) return { k: 'num', v: Q.ONE };
    if (u.k !== 'mul') return null;
    const xs = u.c.filter((c) => has(c, x));
    if (xs.length !== 1 || !(xs[0]!.k === 'sym' && xs[0]!.n === x)) return null;
    const rest = u.c.filter((c) => c !== xs[0]);
    return rest.length === 1 ? rest[0]! : { k: 'mul', c: rest };
  };
  const coefs: Term[] = [];
  const others: Term[] = [];
  for (const u of t.c) {
    const k = coefOf(u);
    if (k) coefs.push(k);
    else others.push(u);
  }
  if (coefs.length < 2) return t;
  const lin: Term = {
    k: 'mul',
    c: [
      { k: 'add', c: coefs },
      { k: 'sym', n: x },
    ],
  };
  return others.length ? { k: 'add', c: [lin, ...others] } : lin;
}
/** moteur v47 : termes « c × x » d'une somme écrite, à regrouper (« x ln 2 − x ln 3 ») */
function collectableX(t: Term, x: string): number {
  if (t.k !== 'add') return 0;
  const flat = (u: Term): Term[] => (u.k === 'mul' ? u.c.flatMap(flat) : [u]);
  const n = t.c.filter((u) => {
    if (u.k === 'sym') return u.n === x;
    if (u.k !== 'mul') return false;
    const xs = flat(u).filter((c) => has(c, x));
    return xs.length === 1 && xs[0]!.k === 'sym' && xs[0]!.n === x;
  }).length;
  return Math.max(0, n - 1);
}
/** une constante non nombre qui s'écrit avec une fonction (ln 2, ln 3 − ln 2) */
const fnConst = (t: Term): boolean =>
  isConst(t) && !isNumber(t) && hasFnNode(t, (f) => f === 'ln' || f === 'log' || f === 'exp');
/** un terme écrit avec un « − » devant */
const negated = (t: Term): boolean =>
  (t.k === 'mul' && t.c[0]?.k === 'num' && Q.sign(t.c[0].v) < 0) ||
  (t.k === 'num' && Q.sign(t.v) < 0);
/** moteur v47 : calculs encore à faire sur les constantes ln, log, exp d'une écriture */
function lnConstDebt(t: Term): number {
  let d = 0;
  // ln(1 ÷ 2), ln(0,5) : ln d'un rationnel non entier, à écrire −ln 2 (ou ln a − ln b)
  if (t.k === 'fn' && (t.f === 'ln' || t.f === 'log') && isNumber(t.a)) {
    const v = canon(t.a);
    if (v.k === 'num' && v.v.d !== 1n) d += 1;
  }
  // −ln 2 ÷ (−0,05) : une constante ln divisée par un nombre (sauf ±1)
  if (t.k === 'div' && fnConst(t.a) && isNumber(t.b)) {
    const b = canon(t.b);
    if (!(b.k === 'num' && b.v.d === 1n && (b.v.n === 1n || b.v.n === -1n))) d += 1;
  }
  // −ln 3 ÷ (ln 2 − ln 3) : les signes se simplifient
  if (
    t.k === 'div' &&
    negated(t.a) &&
    fnConst(t.b) &&
    t.b.k === 'add' &&
    negated(t.b.c[t.b.c.length - 1]!)
  )
    d += 1;
  switch (t.k) {
    case 'add':
    case 'mul':
      return d + t.c.reduce((n, c) => n + lnConstDebt(c), 0);
    case 'div':
    case 'pow':
    case 'binom':
      return d + lnConstDebt(t.a) + lnConstDebt(t.b);
    case 'fn':
      return d + lnConstDebt(t.a);
    default:
      return d;
  }
}
/** moteur v47 : ln, exp, log de l'inconnue (« ln(x) + ln(x − 1) » en a deux à réunir) */
function transcendentalOfX(s: Statement, x: string): number {
  const count = (t: Term): number => {
    switch (t.k) {
      case 'fn':
        return (['ln', 'exp', 'log'].includes(t.f) && has(t.a, x) ? 1 : 0) + count(t.a);
      case 'add':
      case 'mul':
        return t.c.reduce((n, c) => n + count(c), 0);
      case 'div':
      case 'pow':
      case 'binom':
        return count(t.a) + count(t.b);
      default:
        return 0;
    }
  };
  return relationsOf(s).reduce((n, r) => n + count(r.l) + count(r.r), 0);
}
/** Taille d'un membre, une constante trigonométrique (cos(40°), écrite cos(2π ÷ 9) une
 *  fois réduite) comptant pour un seul bout (moteur v20) ; moteur v47 : toute constante
 *  écrite avec une fonction (ln 2 − ln 3) aussi. */
function xsize(t: Term, x: string, v47 = false): number {
  if (!has(t, x) && hasFnNode(t, (f) => (v47 ? true : TRIG_FNS.has(f)))) return 1;
  switch (t.k) {
    case 'add':
    case 'mul':
      return 1 + t.c.reduce((s, c) => s + xsize(c, x, v47), 0);
    case 'div':
    case 'pow':
    case 'binom':
      return 1 + xsize(t.a, x, v47) + xsize(t.b, x, v47);
    case 'fn':
      return 1 + xsize(t.a, x, v47);
    default:
      return 1;
  }
}
/** Fractions à chasser : une somme qui contient x sur un dénominateur, ou x au
 *  dénominateur. « x ÷ 2 » ou « −2x ÷ (−2) » n'en sont pas : c'est l'inconnue qu'on
 *  divise, à un calcul de la fin. */
function fractionsWith(t: Term, x: string): number {
  const sumWithX = (u: Term): boolean =>
    has(u, x) && (u.k === 'add' || (u.k === 'mul' && u.c.some((c) => c.k === 'add' && has(c, x))));
  switch (t.k) {
    case 'div':
      return (sumWithX(t.a) || has(t.b, x) ? 1 : 0) + fractionsWith(t.a, x) + fractionsWith(t.b, x);
    case 'add':
    case 'mul':
      return t.c.reduce((s, c) => s + fractionsWith(c, x), 0);
    case 'pow':
    case 'binom':
      return fractionsWith(t.a, x) + fractionsWith(t.b, x);
    case 'fn':
      return fractionsWith(t.a, x);
    default:
      return 0;
  }
}
const isConst = (t: Term): boolean => symbolsOf(t).size === 0;
/** sans lettre ET calculable en un nombre (π n'en est pas un) */
const isNumber = (t: Term): boolean => isConst(t) && canon(t).k === 'num';
const gcd = (a: bigint, b: bigint): bigint => (b === 0n ? (a < 0n ? -a : a) : gcd(b, a % b));
/** un nombre écrit tel quel : entier, décimal, fraction irréductible, et leur opposé */
function literal(t: Term): boolean {
  if (t.k === 'num') return true;
  if (t.k === 'div' && t.a.k === 'num' && t.b.k === 'num') {
    const a = t.a.v;
    const b = t.b.v;
    if (a.d !== 1n || b.d !== 1n || b.n <= 1n) return false;
    return gcd(a.n, b.n) === 1n;
  }
  if (
    t.k === 'mul' &&
    t.c.length === 2 &&
    t.c[0]!.k === 'num' &&
    t.c[0]!.v.n === -1n &&
    t.c[0]!.v.d === 1n
  )
    return literal(t.c[1]!);
  return false;
}
/** coefficient numérique en tête d'un terme (entier), sinon null */
function intCoef(t: Term): bigint | null {
  if (t.k === 'num') return t.v.d === 1n ? t.v.n : null;
  if (t.k === 'mul' && t.c[0]?.k === 'num' && t.c[0].v.d === 1n) return t.c[0].v.n;
  return null;
}
/** forme d'un monôme sans son coefficient (termes semblables : 3x et −x) */
function monomial(t: Term): string | null {
  if (isConst(t)) return null;
  const c = canon(t);
  if (c.k === 'mul' && c.c[0]!.k === 'num') {
    // « 6x » et « x » sont des termes semblables : la partie littérale seule
    const rest = c.c.slice(1);
    return termKey(rest.length === 1 ? rest[0]! : { k: 'mul', c: rest });
  }
  return termKey(c);
}
/** Calculs encore à faire dans l'écriture : nombres à combiner, termes semblables, fraction à simplifier. */
const isExp = (t: Term): boolean => t.k === 'fn' && t.f === 'exp';
/** une exponentielle, ou une puissance d'exponentielle (« (e^x)² ») */
const expish = (t: Term): boolean => isExp(t) || (t.k === 'pow' && isExp(t.a));
/** exponentielles d'un facteur ou d'un produit */
const expCount = (t: Term): number =>
  expish(t) ? 1 : t.k === 'mul' ? t.c.filter(expish).length : 0;
function calcDebt(t: Term): number {
  // un nombre qui se calcule (« 8 − 2 », « 6 ÷ 3 ») ; π, √2 restent tels quels.
  // Une opération compte pour un calcul : 10² + 10² (3) → 100 + 100 (1) avance (v21)
  if (isConst(t) && canon(t).k === 'num') return literal(t) ? 0 : Math.max(1, numOps(t));
  // une constante qui vaut un nombre une fois calculée : ln(1), exp(0), exp(ln(4)) ;
  // cos(60°), arccos(1 ÷ 2) sont des valeurs exactes, comptées avec les racines (v20)
  if (isConst(t) && t.k === 'fn' && !TRIG_FNS.has(t.f)) {
    const r = reduced(t);
    if (r && r.k === 'num') return 1;
  }
  // règles de calcul sur l'exponentielle encore à appliquer :
  // exp(a) ÷ exp(b), exp(a) × exp(b), (exp(a))ⁿ s'écrivent avec une seule exponentielle.
  // Chaque règle pèse 2 (moteur v38) : l'appliquer crée au plus un calcul dans l'exposant
  // (« e^(3x) × e^(−x + 2) » → « e^(3x − x + 2) » avance, puis « e^(2x + 2) »), et on
  // compte chaque exponentielle d'un produit, une puissance d'exponentielle, une
  // exponentielle de chaque côté d'un quotient (« (e^(3x) × e^(−x)) ÷ e^(x + 1) »).
  let expRule = 0;
  if (t.k === 'div' && expCount(t.a) > 0 && expCount(t.b) > 0) expRule = 2;
  if (t.k === 'mul') expRule = 2 * Math.max(0, t.c.filter(expish).length - 1);
  if (t.k === 'pow' && isExp(t.a)) expRule = 2;
  return expRule + calcDebtOf(t);
}
/** Opérations d'un calcul sur des nombres : « 10² + 10² » 3, « 100 + 100 » 1, « 200 » 0. */
function numOps(t: Term): number {
  if (literal(t)) return 0;
  switch (t.k) {
    case 'add':
    case 'mul':
      return t.c.length - 1 + t.c.reduce((n, c) => n + numOps(c), 0);
    case 'div':
    case 'pow':
    case 'binom':
      return 1 + numOps(t.a) + numOps(t.b);
    case 'fn':
      return 1 + numOps(t.a);
    default:
      return 0;
  }
}
function calcDebtOf(t: Term): number {
  switch (t.k) {
    case 'add': {
      let d = Math.max(0, t.c.filter(isNumber).length - 1);
      const seen = new Map<string, number>();
      t.c.forEach((c) => {
        const m = monomial(c);
        if (m !== null) seen.set(m, (seen.get(m) ?? 0) + 1);
      });
      seen.forEach((n) => (d += n - 1));
      // des multiples de π à réunir : « π + π ÷ 3 » s'écrit « 4π ÷ 3 » (moteur v24)
      const pis = t.c.filter((c) => isConst(c) && !isNumber(c) && piCoef(c) !== null).length;
      d += Math.max(0, pis - 1);
      return d + t.c.reduce((s, c) => s + calcDebt(c), 0);
    }
    case 'mul': {
      const nums = t.c.filter(isNumber);
      let d = Math.max(0, nums.length - 1);
      // 16 × (√3 ÷ 2) : le nombre et la fraction irrationnelle se calculent (moteur v20)
      if (nums.length && t.c.some((c) => c.k === 'div' && isConst(c) && !isNumber(c))) d += 1;
      const keys = t.c.filter((c) => !isConst(c)).map((c) => termKey(canon(c)));
      if (new Set(keys).size < keys.length) d += 1; // x × x
      return d + t.c.reduce((s, c) => s + calcDebt(c), 0);
    }
    case 'div': {
      let d = calcDebt(t.a) + calcDebt(t.b);
      if (t.b.k === 'num' && t.b.v.d === 1n) {
        const b = t.b.v.n;
        const k = intCoef(t.a);
        if (b === 1n || b === -1n || (k !== null && gcd(k, b) > 1n)) d += 1;
      }
      return d;
    }
    case 'pow':
      return calcDebt(t.a) + calcDebt(t.b);
    case 'fn':
      return calcDebt(t.a);
    default:
      return 0;
  }
}

/* ---- racines carrées et valeurs exactes (moteur v20) ---- */

/** Calculs encore à faire sur les constantes irrationnelles d'une écriture. */
export interface RadDebt {
  /** racine d'un nombre à simplifier : √12 (= 2√3), √(1 ÷ 2) */
  sqrt: number;
  /** valeur exacte connue : cos(30°) (= √3 ÷ 2), tan(45°) */
  value: number;
  /** racine au dénominateur : 14 ÷ √3 */
  radDen: number;
  /** racine au dénominateur dans une somme : 1 ÷ (√2 − 1), l'expression conjuguée */
  conj: number;
  /** fraction de fraction : 7 ÷ (√3 ÷ 2) */
  compound: number;
  /** même racine plusieurs fois : 2√3 + 3√3 */
  like: number;
  /** fraction à simplifier : (2√3 + 2) ÷ 2 */
  gcd: number;
}
const RAD_KINDS = ['sqrt', 'value', 'radDen', 'conj', 'compound', 'like', 'gcd'] as const;
/** calculs qui ne s'ajoutent pas à une règle dans la même ligne */
const RULE_PLUS_CALC = ['sqrt', 'value', 'radDen', 'conj'] as const;
type RadKind = (typeof RAD_KINDS)[number];
/** poids dans la dette : une valeur exacte puis une racine au dénominateur se calculent
 *  en plusieurs pas, chacun doit faire baisser la dette */
const RAD_WEIGHT: Record<RadKind, number> = {
  sqrt: 3,
  value: 6,
  radDen: 4,
  conj: 7,
  compound: 1,
  like: 1,
  gcd: 1,
};
const TRIG_FNS = new Set(['cos', 'sin', 'tan', 'arccos', 'arcsin', 'arctan']);
const radZero = (): RadDebt => ({
  sqrt: 0,
  value: 0,
  radDen: 0,
  conj: 0,
  compound: 0,
  like: 0,
  gcd: 0,
});
function hasFnNode(t: Term, pred: (f: string) => boolean): boolean {
  switch (t.k) {
    case 'add':
    case 'mul':
      return t.c.some((c) => hasFnNode(c, pred));
    case 'div':
    case 'pow':
    case 'binom':
      return hasFnNode(t.a, pred) || hasFnNode(t.b, pred);
    case 'fn':
      return pred(t.f) || hasFnNode(t.a, pred);
    default:
      return false;
  }
}
const isSqrt = (f: string): boolean => f === 'sqrt';
function hasSum(t: Term): boolean {
  switch (t.k) {
    case 'add':
      return true;
    case 'mul':
      return t.c.some(hasSum);
    case 'div':
    case 'pow':
    case 'binom':
      return hasSum(t.a) || hasSum(t.b);
    default:
      return false;
  }
}
/** Les calculs restants sur les constantes (racines, valeurs exactes) d'un membre. */
function radDebt(t: Term, acc: RadDebt = radZero()): RadDebt {
  switch (t.k) {
    case 'add':
    case 'mul':
      t.c.forEach((c) => radDebt(c, acc));
      break;
    case 'div':
    case 'pow':
    case 'binom':
      radDebt(t.a, acc);
      radDebt(t.b, acc);
      break;
    case 'fn':
      radDebt(t.a, acc);
      break;
    default:
      return acc;
  }
  if (!isConst(t) || isNumber(t)) return acc;
  if (t.k === 'fn' && t.f === 'sqrt') {
    // seulement un nombre ÉCRIT : √(4 × 3) montre déjà le carré à sortir
    const a = t.a;
    if (a.k === 'num' && a.v.n > 0n) {
      const r = Q.sqrtRat(a.v);
      // √12 = 2√3, √(1 ÷ 2) = √2 ÷ 2 ; √3 est déjà simplifiée, √49 est un calcul
      if (r && r.rad !== 1n && !(a.v.d === 1n && r.rad === a.v.n)) acc.sqrt++;
    }
  } else if (t.k === 'fn' && TRIG_FNS.has(t.f)) {
    const r = reduced(t);
    if (r && !hasFnNode(r, (f) => TRIG_FNS.has(f))) acc.value++;
  } else if (t.k === 'div') {
    if (t.a.k === 'div' || t.b.k === 'div') acc.compound++;
    if (hasFnNode(t.b, isSqrt)) {
      const r = reduced(t.b);
      if (r && hasFnNode(r, isSqrt)) {
        if (hasSum(canon(t.b))) acc.conj++;
        else acc.radDen++;
      }
    }
    const b = canon(t.b);
    if (t.a.k === 'add' && b.k === 'num' && b.v.d === 1n && b.v.n > 1n) {
      let g = b.v.n;
      for (const c of t.a.c) g = gcd(g, intCoef(canon(c)) ?? 1n);
      if (g > 1n) acc.gcd++;
    }
  } else if (t.k === 'add') {
    const seen = new Map<string, number>();
    t.c.forEach((c) => {
      if (!isConst(c) || isNumber(c)) return;
      const cc = canon(c);
      const key =
        cc.k === 'mul' && cc.c[0]!.k === 'num'
          ? termKey({ k: 'mul', c: cc.c.slice(1) })
          : termKey(cc);
      seen.set(key, (seen.get(key) ?? 0) + 1);
    });
    seen.forEach((n) => (acc.like += n - 1));
  }
  return acc;
}
const radWeight = (d: RadDebt): number => RAD_KINDS.reduce((s, k) => s + RAD_WEIGHT[k] * d[k], 0);
function radSum(a: RadDebt, b: RadDebt): RadDebt {
  const out = radZero();
  RAD_KINDS.forEach((k) => (out[k] = a[k] + b[k]));
  return out;
}
/** une fraction de constantes reste écrite (le dénominateur se voit encore) */
function hasConstDiv(s: Statement): boolean {
  const walk = (t: Term): boolean => {
    if (t.k === 'div' && isConst(t)) return true;
    switch (t.k) {
      case 'add':
      case 'mul':
        return t.c.some(walk);
      case 'div':
      case 'pow':
      case 'binom':
        return walk(t.a) || walk(t.b);
      case 'fn':
        return walk(t.a);
      default:
        return false;
    }
  };
  return relationsOf(s).some((r) => walk(r.l) || walk(r.r));
}

const FLIP: Record<Rel, Rel> = { '=': '=', '<': '>', '>': '<', '≤': '≥', '≥': '≤' };
const STRICT_TOGGLE: Record<Rel, Rel> = { '=': '=', '<': '≤', '≤': '<', '>': '≥', '≥': '>' };
/** Distance à la réponse d'une relation du premier degré : l'isolation habituelle. */
function linearH(r: Relation, x: string, v47 = false): number {
  const l = reduced(r.l);
  const rr = reduced(r.r);
  if (!l || !rr) return isolation(r, x, v47);
  return (
    isolation({ rel: r.rel, l, r: rr }, x, v47) +
    2 * (fractionsWith(r.l, x) + fractionsWith(r.r, x))
  );
}
/**
 * `poly` (moteur v23, méthode « factor ») : la distance se mesure à la FORME de la ligne
 * (kernel-poly.ts) ; sinon, à l'isolation de l'inconnue.
 */
function kline(
  s: Statement,
  x: string,
  trusted: boolean,
  poly = false,
  trig: TrigMode | null = null,
  mode: KlineMode = {},
): KLine {
  const rels = relationsOf(s);
  let h = 0;
  let calc = 0;
  let expand = 0;
  let rad = radZero();
  const nk: string[] = [];
  let nkOk = rels.length > 0;
  for (const r of rels) {
    const l = reduced(r.l);
    const rr = reduced(r.r);
    if (!l || !rr) {
      nkOk = false;
      h += isolation(r, x, mode.v47);
      continue;
    }
    // les fractions qui contiennent l'inconnue se lisent sur la ligne ÉCRITE (la forme
    // réduite les a déjà chassées) : on préfère multiplier pour s'en débarrasser
    h +=
      isolation({ rel: r.rel, l, r: rr }, x, mode.v47) +
      2 * (fractionsWith(r.l, x) + fractionsWith(r.r, x));
    calc += calcDebt(r.l) + calcDebt(r.r);
    // moteur v47 : calculs sur les constantes ln (ln(1 ÷ 2) = −ln 2, −ln 2 ÷ (−0,05) = 20 ln 2)
    // et termes en x à regrouper quand leurs coefficients sont des constantes ln
    if (mode.v47) {
      calc += lnConstDebt(r.l) + lnConstDebt(r.r);
      h += collectableX(r.l, x) + collectableX(r.r, x);
    }
    rad = radSum(rad, radSum(radDebt(r.l), radDebt(r.r)));
    expand += unexpanded(r.l) + unexpanded(r.r);
    // même ligne, sens normalisé : a > b s'écrit b < a ; l'égalité est symétrique
    const [a, b, rel] = r.rel === '>' || r.rel === '≥' ? [rr, l, FLIP[r.rel]] : [l, rr, r.rel];
    const ka = termKey(a);
    const kb = termKey(b);
    nk.push(rel === '=' ? [ka, kb].sort().join('=') : `${ka}${rel}${kb}`);
  }
  // un ensemble (moteur v23) : ses clauses comptent, pas seulement ses relations
  // (« x ≥ 2 ou x ≤ 3 » n'est pas « 2 ≤ x ≤ 3 »)
  if (s.k === 'set' && nkOk) {
    let i = 0;
    const groups = s.clauses.map((c) =>
      nk
        .slice(i, (i += c.length))
        .sort()
        .join('&'),
    );
    nk.splice(0, nk.length, ...groups);
  }
  if (s.k === 'expr') {
    const e = reduced(s.e);
    nkOk = !!e;
    if (e) {
      nk.push(termKey(e));
      calc += calcDebt(s.e);
      rad = radSum(rad, radDebt(s.e));
      expand += unexpanded(s.e);
    }
  }
  calc += radWeight(rad);
  // coefficients encore divisibles, ou fractionnaires (moteur v23) : diviser par 2
  // « 2x² + 28x − 480 = 0 » avance
  let coef = 0;
  if (poly) {
    h = polyH(s, x, (r) => linearH(r, x, mode.v47));
    // moteur v47 : deux logarithmes de l'inconnue à réunir (« ln(x) + ln(x − 1) »)
    if (mode.v47) h += 4 * Math.max(0, transcendentalOfX(s, x) - 1);
    if (s.k === 'rel') coef = coefficientDebt(s, x);
  }
  // équation trigonométrique (moteur v24) : la forme de la ligne (kernel-trig.ts)
  if (trig) h = trigH(s, x, trig.params, trig.bounded, (r, v) => linearH(r, v));
  // dénombrement (moteur v27) : « k parmi n » et factorielles à écrire, simplifier, calculer
  let cnt: CountDebt | undefined;
  let answerForm: boolean | undefined;
  if (mode.count) {
    cnt = countDebtOf(s, rels);
    const ans = mode.count.answer;
    if (ans) {
      const r = valueSide(s, x);
      answerForm = !!r && hasAnswerForm(r, ans);
      h = answerForm ? 0 : 1 + (r ? extraArgs(r, ans) : 9);
    } else if (
      cnt.binom + cnt.known + cnt.facts > 0 &&
      rels.some((r) => has(r.l, x) || has(r.r, x))
    )
      // équation en n (« 2 parmi n = 45 ») : « parmi » puis les factorielles s'écrivent
      // d'abord, avant toute forme polynomiale (kernel-poly.ts : au plus 50)
      h = cnt.binom + cnt.known > 0 ? 60 : 55;
  }
  // seuil (moteur v27) : la réponse est l'inconnue face à un ENTIER (« n ≥ 4 ») ; un
  // signe « − » devant l'inconnue reste à enlever (« −0,5^n > −0,1 » → « 0,5^n < 0,1 »)
  if (mode.intBound) {
    const b = integerBound(s, x);
    answerForm = b !== null && calc === 0 && !(mode.intLarge && (b.rel === '<' || b.rel === '>'));
    if (!answerForm) h += 1;
    for (const r of rels) {
      const side = has(r.l, x) ? reduced(r.l) : has(r.r, x) ? reduced(r.r) : null;
      if (side && negativeLead(side)) h += 1;
    }
  }
  // écriture à développer ou à factoriser (moteur v33) : la forme décide de l'avancée
  let fm: FormShape | undefined;
  if (mode.form) {
    const r = valueSide(s, x);
    if (!r) {
      h = 50;
      answerForm = false;
    } else {
      fm = formShape(r, mode.form, mode.formAnswer);
      calc += fm.pow;
      // un produit à réduire : ses calculs comptent dans la distance
      h = fm.product && fm.done && calc > 0 ? 3 : fm.h;
      answerForm = fm.done && calc === 0;
    }
  }
  // inconnue auxiliaire (moteur v47) : tant que la ligne s'écrit en X (ou en eˣ), sa forme
  // se mesure en X — écrite en eˣ +4, écrite en X +3, résolue en X mais écrite en eˣ
  // (« eˣ = 1 ou eˣ = 2 ») 2 ; revenue à x : la mesure en x
  if (mode.aux) {
    const sA = auxStatement(s, mode.aux, x);
    if (sA) {
      const X = mode.aux.name;
      const hA = polyH(sA, X, (r) => linearH(r, X, true));
      h = mode.auxWritten
        ? hA + 3
        : isSolvedSet(sA, X)
          ? 2 + (mode.aux.of === 'exp' ? impossibleExpClauses(sA, X) : 0)
          : hA + (mode.aux.of === 'exp' && expAtomsOnly(s, x) ? 3.5 : 4);
      if (sA.k === 'rel') coef = coefficientDebt(sA, X);
    }
  }
  return {
    s,
    raw: statementKey(s),
    K: keyOf(s),
    NK: nkOk ? `${s.k}:${nk.sort().join(';')}` : null,
    h,
    calc,
    expand,
    // une somme à factoriser (moteur v33) : seule sa forme compte (développer, calculer
    // dans une somme n'en rapproche pas)
    debt:
      mode.form === 'factored' && fm && !fm.product
        ? 0
        : // forme canonique (moteur v43) : le carré (x + p)² reste écrit, seuls les calculs comptent ;
          // une seule fraction (moteur v46) : le dénominateur peut rester factorisé
          mode.form === 'canonical' || mode.form === 'fraction'
          ? calc
          : calc + 3 * expand + coef + (cnt ? countWeight(cnt) : 0),
    rad,
    trusted,
    ...(mode.auxWritten ? { auxWritten: true } : {}),
    ...(cnt ? { cnt } : {}),
    ...(answerForm !== undefined ? { answerForm } : {}),
    ...(fm ? { fm } : {}),
  };
}

/** une écriture qui commence par un signe « − » (forme réduite : −1 ÷ exp(…), −2x + 4) */
function negativeLead(t: Term): boolean {
  const top = t.k === 'div' ? t.a : t;
  const first = top.k === 'add' ? top.c[0]! : top;
  if (first.k === 'num') return Q.sign(first.v) < 0;
  return first.k === 'mul' && first.c[0]?.k === 'num' && Q.sign(first.c[0].v) < 0;
}
/** « N = … » : le membre de droite (la valeur de la grandeur cherchée), sinon null */
function valueSide(s: Statement, x: string): Term | null {
  if (s.k !== 'rel' || s.rel !== '=') return null;
  if (s.l.k === 'sym' && s.l.n === x) return s.r;
  return null;
}
/** un nombre écrit tel quel (entier, décimal, son opposé) */
function plainNumber(t: Term): boolean {
  return t.k === 'num';
}
/** la ligne écrit-elle la réponse ? (un nombre : écrit tel quel ; sinon : même écriture) */
function hasAnswerForm(r: Term, ans: Term): boolean {
  if (plainNumber(ans)) return plainNumber(r);
  return termKey(canon(r)) === termKey(canon(ans));
}
/** « n ≥ 4 », « 4 ≤ n », « n < 7 » : l'inconnue face à un entier (moteur v27) */
function integerBound(s: Statement, x: string): { rel: Rel; v: bigint } | null {
  if (s.k !== 'rel' || s.rel === '=') return null;
  const int = (t: Term): bigint | null => {
    const c = canon(t);
    return c.k === 'num' && c.v.d === 1n ? c.v.n : null;
  };
  if (s.l.k === 'sym' && s.l.n === x) {
    const v = int(s.r);
    return v === null ? null : { rel: s.rel, v };
  }
  if (s.r.k === 'sym' && s.r.n === x) {
    const v = int(s.l);
    return v === null ? null : { rel: FLIP[s.rel], v };
  }
  return null;
}
const better = (a: KLine, b: KLine): boolean => a.h < b.h || (a.h === b.h && a.debt < b.debt);

/** La ligne la plus avancée parmi celles de confiance. */
function frontier(ks: KernelSheetState): KLine {
  let best = ks.start;
  for (const l of ks.lines) if (l.trusted && better(l, best)) best = l;
  return best;
}

/** « x … » seul à gauche, rien à calculer à droite : la forme d'une réponse. */
function solvedForm(l: KLine, x: string, trig: TrigMode | null = null): boolean {
  // dénombrement à simplifier, seuil entier (moteur v27) : la forme de la réponse décide
  if (l.answerForm !== undefined) return l.answerForm;
  // une réponse peut rester factorisée (C = 5 ÷ 9 × (F − 32)), jamais à calculer
  if (l.calc > 0) return false;
  // équation trigonométrique (moteur v24) : des valeurs de x, ou des familles sur ℝ
  if (trig) return trigSolved(l.s, x, trig.params, trig.bounded);
  // un ensemble (moteur v23) : chaque relation met x face à une constante, d'un côté ou
  // de l'autre (« −1 < x ≤ 2 »)
  if (l.s.k === 'set') return isSolvedSet(l.s, x);
  const rels = relationsOf(l.s);
  return rels.length > 0 && rels.every((r) => r.l.k === 'sym' && r.l.n === x && !has(r.r, x));
}

/* ------------------------------ vérification ------------------------------ */

function check(
  ks: KernelSheetState,
  from: KLine,
  to: Statement,
  toRaw: string,
  bare = false,
): StepCheck {
  // moteur v49 : les conditions écrites sont des hypothèses (sauf pour comparer à la réponse)
  const conds = bare ? [] : ks.condsOk;
  const key = conds.length ? `${conds.length}|${from.raw}→${toRaw}` : `${from.raw}→${toRaw}`;
  const hit = ks.cache.get(key);
  if (hit) return hit;
  const t0 = Date.now();
  const c = checkAlgebraStatements(from.s, to, {
    rules: ks.rules,
    hypotheses: conds.length ? [...ks.hyps, ...conds] : ks.hyps,
    goal: ks.goal,
    deadline: t0 + 250,
    // une seule profondeur de cas : sert au message « ça dépend du signe de m »
    branches: true,
    ...(ks.angles ? { angles: ks.angles } : {}),
    // équation trigonométrique en radians (moteur v24) : solutions exactes, intervalle
    ...(ks.trig && !ks.angles ? { trig: { domain: ks.trig.domain } } : {}),
  });
  ks.cache.set(key, c);
  return c;
}

/** Sources de confiance, la plus récente d'abord, le départ en dernier. */
function sources(ks: KernelSheetState): KLine[] {
  return [...ks.lines.filter((l) => l.trusted).reverse(), ks.start];
}

/* ------------------------------ textes ------------------------------ */

const lower = (s: string): string => s.charAt(0).toLowerCase() + s.slice(1);
/** un terme isolé, ou entre parenthèses s'il en contient plusieurs */
function wrapTerm(t: string): string {
  // « (x + 2)² » est déjà un seul bout (moteur v23)
  return /^[^\s]+$/.test(t) || /^\(.*\)$/.test(t) || /^\([^()]*\)[²³]$/.test(t) ? t : `(${t})`;
}
/** « −2 » → « 2 » à retrancher ; null si le terme n'est pas un opposé simple */
function oppositeOf(t: string): string | null {
  if (!t.startsWith('−')) return null;
  const rest = t.slice(1);
  return /\s[+−]\s/.test(rest) ? null : rest;
}

/** Texte d'une condition, pour l'élève : « m > 1 », « a ≠ 0 », « n entier ». */
export function conditionText(c: { kind: string; of: string | readonly string[] }): string {
  const of = typeof c.of === 'string' ? c.of : c.of.join(', ');
  // « m − 1 > 0 » se lit « m > 1 » (une lettre plus ou moins un nombre)
  const m = /^([^\s+−]+) ([+−]) (\d+(?:,\d+)?)$/.exec(of);
  const cmp: Record<string, string> = {
    positive: '>',
    negative: '<',
    nonzero: '≠',
    nonnegative: '≥',
  };
  // un angle (moteur v21) : « 0° < Â < 90° »
  if (c.kind === 'acute') return `0° < ${of} < 90°`;
  if (c.kind === 'angle') return `0° < ${of} < 180°`;
  if (cmp[c.kind]) {
    if (m && !/^\d/.test(m[1]!)) {
      const bound = m[2] === '−' ? m[3]! : `−${m[3]!}`;
      return `${m[1]} ${cmp[c.kind]} ${bound}`;
    }
    return `${of} ${cmp[c.kind]} 0`;
  }
  if (c.kind === 'integer') return `${of} est un entier`;
  if (c.kind === 'natural') return `${of} est un entier naturel`;
  if (c.kind === 'zero') return `${of} = 0`;
  return of;
}

/** Hypothèses de la feuille, écrites pour l'élève (tête de feuille). */
export function kernelHypothesisTexts(model: ScratchModel): string[] {
  // l'intervalle de l'énoncé (moteur v24) d'abord : « −π < x ≤ π »
  return [
    ...(model.kernel?.domain ? [model.kernel.domain] : []),
    ...(model.kernel?.hypotheses ?? []).map((h) => conditionText(h)),
  ];
}

const EXPLOG_INEQUALITIES = new Set([
  'explog.exp_compare',
  'explog.exp_to_ln_below',
  'explog.exp_to_ln_above',
  'explog.ln_to_exp_below',
  'explog.ln_to_exp_above',
  'explog.ln_compare',
  'explog.apply_ln_compare',
  'log.pow10_to_log_below',
  'log.pow10_to_log_above',
  'log.log_to_pow10_below',
  'log.log_to_pow10_above',
  'log.log_compare',
]);
function isInequality(ruleId: string): boolean {
  return (
    ruleId.startsWith('inequality.') ||
    EXPLOG_INEQUALITIES.has(ruleId) ||
    ruleId === 'radical.sqrt_compare' ||
    ruleId === 'condition.sqrt_below'
  );
}

export interface StepInfo {
  id: string;
  card: string;
  label: string;
  hora: number;
  why: string;
}

/**
 * Le facteur k tel que l'élève l'a ÉCRIT : « ln(0,5) » plutôt que sa forme réduite
 * « −ln(2) ». Cherché parmi les dénominateurs et les facteurs de la ligne écrite.
 */
/**
 * moteur v47 : libellés lisibles (« Diviser par 0,4 », « Diviser par e^(−6) ») pour une
 * feuille en v47 ou plus (`readable` de l'état) ; les feuilles plus anciennes gardent les
 * libellés de leur publication. Posé par `kernelJudge` le temps d'un jugement.
 */
let readableLabels = false;

/** « exp(x) » → « eˣ » écrit « e^x », « exp(−6) » → « e^(−6) » (libellés lisibles, v47) */
export function readableText(text: string): string {
  let out = '';
  let i = 0;
  while (i < text.length) {
    const at = text.indexOf('exp(', i);
    if (at < 0 || (at > 0 && /[A-Za-z]/.test(text[at - 1]!))) {
      if (at < 0) {
        out += text.slice(i);
        break;
      }
      out += text.slice(i, at + 4);
      i = at + 4;
      continue;
    }
    let depth = 0;
    let end = -1;
    for (let j = at + 3; j < text.length; j++) {
      if (text[j] === '(') depth++;
      else if (text[j] === ')' && --depth === 0) {
        end = j;
        break;
      }
    }
    if (end < 0) {
      out += text.slice(i);
      break;
    }
    const arg = readableText(text.slice(at + 4, end));
    out += text.slice(i, at) + (/^[A-Za-zα-ω0-9,]+$/.test(arg) ? `e^${arg}` : `e^(${arg})`);
    i = end + 1;
  }
  return out;
}

/** un facteur numérique écrit en décimal (« 0,4 » et non « 2 ÷ 5 ») ; null : pas un décimal */
function decimalFactorText(k: string | undefined): string | null {
  if (!k) return null;
  const pk = parseStatement(k);
  if (!pk.ok || pk.s.k !== 'expr') return null;
  const n = normalForm(pk.s.e);
  if (!n.ok) return null;
  const c = canon(rfToTerm(rfSimplify(n.rf)));
  if (c.k !== 'num' || c.v.d === 1n) return null;
  const t = withDecimals(c);
  return t.k === 'sym' ? t.n : null;
}

/**
 * Multiplier par k, c'est DIVISER par un facteur écrit qui vaut 1 ÷ k (« C × e^(−6) =
 * e^(−6) » → « C = 1 » : on divise par e^(−6)) ; null : rien de tel.
 */
function inverseWrittenFactor(from: Statement | undefined, k: string | undefined): string | null {
  if (!from || !k) return null;
  const pk = parseStatement(k);
  if (!pk.ok || pk.s.k !== 'expr') return null;
  const inv = normalForm({ k: 'div', a: { k: 'num', v: Q.ONE }, b: pk.s.e });
  if (!inv.ok) return null;
  const want = termKey(rfToTerm(rfSimplify(inv.rf)));
  const found: Term[] = [];
  const visit = (t: Term): void => {
    if (t.k === 'mul') found.push(...t.c);
    if (t.k === 'add' || t.k === 'mul') t.c.forEach(visit);
  };
  relationsOf(from).forEach((r) => {
    visit(r.l);
    visit(r.r);
  });
  for (const f of found) {
    if (f.k === 'num') continue;
    const a = normalForm(f);
    if (a.ok && termKey(rfToTerm(rfSimplify(a.rf))) === want) return termText(withDecimals(f));
  }
  return null;
}

/** moteur v47 : la ligne écrite divise par d = 1 ÷ k (« t = −ln(2) ÷ (−0,05) ») */
function writtenDivisor(written: Statement | undefined, k: string | undefined): string | null {
  if (!written || !k) return null;
  const pk = parseStatement(k);
  if (!pk.ok || pk.s.k !== 'expr') return null;
  const inv = normalForm({ k: 'div', a: { k: 'num', v: Q.ONE }, b: pk.s.e });
  if (!inv.ok) return null;
  const want = termKey(rfToTerm(rfSimplify(inv.rf)));
  const found: Term[] = [];
  const visit = (t: Term): void => {
    if (t.k === 'div') found.push(t.b);
    if (t.k === 'add' || t.k === 'mul') t.c.forEach(visit);
    else if (t.k === 'div') {
      visit(t.a);
      visit(t.b);
    }
  };
  relationsOf(written).forEach((r) => {
    visit(r.l);
    visit(r.r);
  });
  for (const f of found) {
    const a = normalForm(f);
    if (a.ok && termKey(rfToTerm(rfSimplify(a.rf))) === want) return termText(withDecimals(f));
  }
  return null;
}

function writtenFactor(
  written: Statement | undefined,
  k: string | undefined,
  nums = false,
): string | null {
  if (!written || !k) return null;
  const pk = parseStatement(k);
  if (!pk.ok || pk.s.k !== 'expr') return null;
  const target = pk.s.e;
  const found: Term[] = [];
  const visit = (t: Term): void => {
    if (t.k === 'div') found.push(t.b);
    if (t.k === 'mul') found.push(...t.c);
    if (t.k === 'add' || t.k === 'mul') t.c.forEach(visit);
    else if (t.k === 'div' || t.k === 'pow') {
      visit(t.a);
      visit(t.b);
    } else if (t.k === 'fn') visit(t.a);
  };
  relationsOf(written).forEach((r) => {
    visit(r.l);
    visit(r.r);
  });
  for (const f of found) {
    if (f.k === 'num' && !nums) continue;
    const a = normalForm(f);
    const b = normalForm(target);
    if (!a.ok || !b.ok) continue;
    if (termKey(rfToTerm(rfSimplify(a.rf))) === termKey(rfToTerm(rfSimplify(b.rf))))
      return termText(withDecimals(f));
  }
  return null;
}

/**
 * Le terme RETRANCHÉ tel qu'il est écrit dans la ligne (« 0,5x » et non « x ÷ 2 ») :
 * un terme (non numérique) d'un membre dont la valeur est −c. null : rien de tel.
 */
function writtenAddend(written: Statement | undefined, c: string | undefined): string | null {
  if (!written || !c) return null;
  const pc = parseStatement(c);
  if (!pc.ok || pc.s.k !== 'expr') return null;
  const want = normalForm({ k: 'mul', c: [{ k: 'num', v: Q.rat(-1) }, pc.s.e] });
  if (!want.ok) return null;
  const key = termKey(rfToTerm(rfSimplify(want.rf)));
  for (const r of relationsOf(written))
    for (const side of [r.l, r.r])
      for (const u of side.k === 'add' ? side.c : [side]) {
        if (u.k === 'num') continue;
        const a = normalForm(u);
        if (a.ok && termKey(rfToTerm(rfSimplify(a.rf))) === key) return addendText(u);
      }
  return null;
}

/** « 0,5x » : un coefficient décimal collé à ce qu'il multiplie (sinon l'écriture habituelle). */
function addendText(u: Term): string {
  if (u.k === 'mul' && u.c.length >= 2 && u.c[0]!.k === 'num') {
    const coef = withDecimals(u.c[0]!);
    const rest: Term = u.c.length === 2 ? u.c[1]! : { k: 'mul', c: u.c.slice(1) };
    const r = termText(withDecimals(rest));
    if (coef.k === 'sym' && /^[A-Za-zα-ωΔ(√]/.test(r)) return coef.n + r;
  }
  return termText(withDecimals(u));
}

/** Les nombres décimaux écrits comme l'élève les écrit (« 1,5 » et non « 3 ÷ 2 »). */
function withDecimals(t: Term): Term {
  switch (t.k) {
    case 'num': {
      const { n, d } = t.v;
      if (d === 1n) return t;
      let r = d;
      let places = 0;
      while (r % 10n === 0n || r % 2n === 0n || r % 5n === 0n) {
        if (r % 10n === 0n) r /= 10n;
        else if (r % 2n === 0n) r /= 2n;
        else r /= 5n;
        places++;
        if (places > 12) return t;
      }
      if (r !== 1n) return t;
      const v = Number(n) / Number(d);
      return { k: 'sym', n: formatNumber(v) };
    }
    case 'add':
    case 'mul':
      return { ...t, c: t.c.map(withDecimals) } as Term;
    case 'div':
    case 'pow':
    case 'binom':
      return { ...t, a: withDecimals(t.a), b: withDecimals(t.b) } as Term;
    case 'fn':
      return { ...t, a: withDecimals(t.a) };
    default:
      return t;
  }
}

/** ln(q) d'un nombre : « (car 0,5 < 1) » / « (car 1,5 > 1) » ; null sinon. */
function lnSignReason(k: string): string | null {
  const m = /^ln\(([\d,]+)\)$/.exec(k);
  if (!m) return null;
  const q = Number(m[1]!.replace(',', '.'));
  if (!Number.isFinite(q) || q <= 0 || q === 1) return null;
  return q < 1 ? ` (car ${m[1]} < 1)` : ` (car ${m[1]} > 1)`;
}

/** cos, sin, tan d'un angle aigu en degrés : « (car 0° < 40° < 90°) » ; null sinon. */
function trigSignReason(k: string): string | null {
  const m = /^(cos|sin|tan)\(([\d,]+)°\)$/.exec(k);
  if (!m) return null;
  const a = Number(m[2]!.replace(',', '.'));
  return a > 0 && a < 90 ? ` (car 0° < ${m[2]}° < 90°)` : null;
}

/**
 * Multiplier par un quotient dont le numérateur contient une grandeur (« AC ÷ cos(40°) »),
 * ou diviser par un quotient dont le dénominateur en contient une (« cos(40°) ÷ AC ») :
 * c'est le produit en croix (moteur v20), nommé comme en classe.
 */
function crossProduct(kText: string | undefined, multiply: boolean): boolean {
  if (!kText) return false;
  const p = parseStatement(kText);
  if (!p.ok || p.s.k !== 'expr' || p.s.e.k !== 'div') return false;
  return symbolsOf(multiply ? p.s.e.a : p.s.e.b).size > 0;
}

/** Libellé, « pourquoi » et HORA d'une règle appliquée, à partir de ses valeurs. */
function ruleStep(c: StepCheck, written?: Statement, from?: Statement): StepInfo {
  const qid = c.ruleId!;
  const id = qid.replace(/@\d+$/, '');
  const p = c.explanation?.params ?? {};
  const ineq = isInequality(id);
  const card = id.startsWith('counting.')
    ? COUNT_CARD
    : id.startsWith('explog.') || id.startsWith('log.')
      ? 'Exponentielle et logarithme'
      : id.startsWith('radical.') || id.startsWith('condition.')
        ? 'Racines carrées'
        : ineq
          ? 'Inéquations'
          : id.startsWith('equation.')
            ? 'Équations'
            : 'Calcul littéral';
  const keep = ineq ? 'le sens ne change pas.' : 'les solutions ne changent pas.';
  // le facteur tel qu'il est écrit : dans la ligne, sinon au dénominateur de la ligne d'avant
  // (« ÷ (1 + x²)² » chassé : « Multiplier par (1 + x²)² », lot 6.18)
  const kText =
    writtenFactor(written, p.v_k, readableLabels) ??
    writtenFactor(from, p.v_k, readableLabels) ??
    (readableLabels ? decimalFactorText(p.v_k) : null) ??
    p.v_k;
  const k = kText ? (readableLabels ? readableText(wrapTerm(kText)) : wrapTerm(kText)) : '';
  // moteur v47 : multiplier par k = diviser par un facteur écrit qui vaut 1 ÷ k
  const invText =
    readableLabels && /multiply/.test(id)
      ? (inverseWrittenFactor(from, p.v_k) ?? writtenDivisor(written, p.v_k))
      : null;
  const inv = invText ? readableText(wrapTerm(invText)) : null;
  // le signe de k : « (car 0,5 < 1) » pour ln(0,5), « (d’après l’énoncé) » pour une lettre
  const letters = kText
    ? kText.replace(/\b(ln|exp|cos|sin|tan|arccos|arcsin|arctan)\(/g, '(').replace(/π/g, '')
    : '';
  const byStatement = kText
    ? (lnSignReason(kText) ??
      trigSignReason(kText) ??
      (/[A-Za-zα-ω]/.test(letters) ? ' (d’après l’énoncé)' : ''))
    : '';
  switch (id) {
    case 'equation.add_both_sides':
    case 'inequality.add_both_sides': {
      const c0 = p.v_c ?? '';
      // un terme écrit avec un coefficient décimal (« 0,5x ») se nomme comme il est écrit
      const seen = /÷/.test(c0) ? (writtenAddend(from, c0) ?? writtenAddend(written, c0)) : null;
      const opp = seen && !seen.startsWith('−') ? seen : oppositeOf(c0);
      const label = opp ? `Retrancher ${opp}` : `Ajouter ${wrapTerm(c0)}`;
      const verb = opp
        ? `On retranche ${opp} aux deux membres`
        : `On ajoute ${wrapTerm(c0)} aux deux membres`;
      return { id: qid, card, label, hora: 2, why: `${verb} : ${keep}` };
    }
    case 'inequality.divide_positive':
      return {
        id: qid,
        card,
        label: `Diviser par ${k}`,
        hora: 2,
        why: `On divise par ${k}, positif${byStatement} : le sens ne change pas.`,
      };
    case 'inequality.multiply_positive':
      if (inv)
        return {
          id: qid,
          card,
          label: `Diviser par ${inv}`,
          hora: 2,
          why: `On divise par ${inv}, positif : le sens ne change pas.`,
        };
      return {
        id: qid,
        card,
        label: `Multiplier par ${k}`,
        hora: 2,
        why: `On multiplie par ${k}, positif${byStatement} : le sens ne change pas.`,
      };
    case 'inequality.divide_negative':
      return {
        id: qid,
        card,
        label: `Diviser par ${k}, le sens change`,
        hora: 3,
        why: `On divise par ${k}, négatif${byStatement} : le sens de l’inégalité change.`,
      };
    case 'inequality.multiply_negative':
      if (inv)
        return {
          id: qid,
          card,
          label: `Diviser par ${inv}, le sens change`,
          hora: 3,
          why: `On divise par ${inv}, négatif : le sens de l’inégalité change.`,
        };
      return {
        id: qid,
        card,
        label: `Multiplier par ${k}, le sens change`,
        hora: 3,
        why: `On multiplie par ${k}, négatif${byStatement} : le sens de l’inégalité change.`,
      };
    case 'equation.divide_both_sides':
    case 'equation.multiply_both_sides':
      if (crossProduct(p.v_k, id === 'equation.multiply_both_sides'))
        return {
          id: qid,
          card,
          label: 'Produit en croix',
          hora: 2,
          why: 'Deux quotients égaux : a = b ÷ c équivaut à a × c = b, donc à c = b ÷ a (a et c non nuls). Les solutions ne changent pas.',
        };
      if (id === 'equation.multiply_both_sides' && inv)
        return {
          id: qid,
          card,
          label: `Diviser par ${inv}`,
          hora: 2,
          why: `On divise les deux membres par ${inv}, non nul : ${keep}`,
        };
      if (id === 'equation.multiply_both_sides')
        return {
          id: qid,
          card,
          label: `Multiplier par ${k}`,
          hora: 2,
          why: `On multiplie les deux membres par ${k}, non nul${byStatement} : ${keep}`,
        };
      return {
        id: qid,
        card,
        label: `Diviser par ${k}`,
        hora: 2,
        why: `On divise les deux membres par ${k}, non nul${byStatement} : ${keep}`,
      };
    case 'equation.swap_sides':
      return {
        id: qid,
        card,
        label: 'Échanger les membres',
        hora: 1,
        why: 'On échange les deux membres de l’égalité.',
      };
    case 'explog.exp_equal':
    case 'explog.exp_compare':
      return {
        id: qid,
        card,
        label: 'Comparer les exposants',
        hora: 2,
        why: ineq
          ? 'La fonction exponentielle est strictement croissante : exp(a) < exp(b) équivaut à a < b, le sens ne change pas.'
          : 'exp(a) = exp(b) équivaut à a = b : on égale les exposants.',
      };
    case 'explog.exp_to_ln':
    case 'explog.exp_to_ln_below':
    case 'explog.exp_to_ln_above':
      return {
        id: qid,
        card,
        label: 'Composer par ln',
        hora: 2,
        why: ineq
          ? 'ln est strictement croissante sur ]0 ; +∞[ : exp(a) < b (b > 0) équivaut à a < ln(b).'
          : 'exp(a) = b, avec b > 0, équivaut à a = ln(b).',
      };
    case 'explog.ln_to_exp':
    case 'explog.ln_to_exp_below':
    case 'explog.ln_to_exp_above':
      return {
        id: qid,
        card,
        label: 'Composer par exp',
        hora: 2,
        why: ineq
          ? 'exp est strictement croissante : ln(a) < b équivaut à a < exp(b), pour a > 0.'
          : 'ln(a) = b équivaut à a = exp(b).',
      };
    case 'explog.ln_equal':
    case 'explog.ln_compare':
      return {
        id: qid,
        card,
        label: 'Comparer sous le ln',
        hora: 2,
        why: ineq
          ? 'ln est strictement croissante sur ]0 ; +∞[ : ln(a) < ln(b) équivaut à a < b, pour a > 0 et b > 0.'
          : 'ln(a) = ln(b) équivaut à a = b, pour a > 0 et b > 0.',
      };
    case 'explog.apply_ln_equal':
    case 'explog.apply_ln_compare':
      return {
        id: qid,
        card,
        label: 'Composer par ln',
        hora: 2,
        why: ineq
          ? 'Les deux membres sont strictement positifs et ln est croissante : le sens ne change pas. ln(qⁿ) = n ln(q).'
          : 'Les deux membres sont strictement positifs : on compose par ln. ln(qⁿ) = n ln(q).',
      };
    // moteur v47 : logarithme décimal, logarithme d'un produit ou d'un quotient
    case 'log.pow10_to_log':
    case 'log.pow10_to_log_below':
    case 'log.pow10_to_log_above':
      return {
        id: qid,
        card,
        label: 'Composer par log',
        hora: 2,
        why: ineq
          ? 'log est strictement croissante sur ]0 ; +∞[ : 10^a < b (b > 0) équivaut à a < log(b).'
          : '10^a = b, avec b > 0, équivaut à a = log(b) : log est la réciproque de x ↦ 10^x.',
      };
    case 'log.log_to_pow10':
    case 'log.log_to_pow10_below':
    case 'log.log_to_pow10_above':
      return {
        id: qid,
        card,
        label: 'Écrire avec une puissance de 10',
        hora: 2,
        why: ineq
          ? 'x ↦ 10^x est strictement croissante : log(a) < b équivaut à a < 10^b, pour a > 0.'
          : 'log(a) = b équivaut à a = 10^b.',
      };
    case 'log.log_equal':
    case 'log.log_compare':
      return {
        id: qid,
        card,
        label: 'Comparer sous le log',
        hora: 2,
        why: ineq
          ? 'log est strictement croissante sur ]0 ; +∞[ : log(a) < log(b) équivaut à a < b, pour a > 0 et b > 0.'
          : 'log(a) = log(b) équivaut à a = b, pour a > 0 et b > 0.',
      };
    case 'log.ln_product':
      return {
        id: qid,
        card,
        label: 'Logarithme d’un produit',
        hora: 2,
        why: 'ln(a) + ln(b) = ln(a × b), pour a > 0 et b > 0 (d’après l’énoncé).',
      };
    case 'log.ln_quotient':
      return {
        id: qid,
        card,
        label: 'Logarithme d’un quotient',
        hora: 2,
        why: 'ln(a) − ln(b) = ln(a ÷ b), pour a > 0 et b > 0 (d’après l’énoncé).',
      };
    case 'radical.sqrt_equal':
      return {
        id: qid,
        card,
        label: 'Élever au carré',
        hora: 2,
        why: '√a = b, avec b ≥ 0, équivaut à a = b² : on élève les deux membres au carré.',
      };
    case 'radical.square_root': {
      const a = p.v_a ? wrapTerm(p.v_a) : 'la grandeur';
      return {
        id: qid,
        card,
        label: 'Prendre la racine carrée',
        hora: 2,
        why: `${a} est positive (d’après l’énoncé) : a² = b équivaut alors à a = √b.`,
      };
    }
    case 'condition.sqrt_below':
      return {
        id: qid,
        card,
        label: 'Élever au carré',
        hora: 2,
        why: 'La fonction carré est croissante sur [0 ; +∞[ : avec a ≥ 0 (la condition) et b ≥ 0, √a < b équivaut à a < b², le sens ne change pas.',
      };
    case 'radical.sqrt_compare':
      return {
        id: qid,
        card,
        label: 'Élever au carré',
        hora: 2,
        why: 'La fonction carré est croissante sur [0 ; +∞[ : √a > b, avec b ≥ 0, équivaut à a > b², le sens ne change pas.',
      };
    // dénombrement (moteur v27)
    case 'counting.binom_def':
      return binomCount(written) > binomCount(from)
        ? {
            id: qid,
            card,
            label: 'Reconnaître k parmi n',
            hora: 2,
            why: 'n! ÷ (k! × (n − k)!) s’écrit « k parmi n » : le nombre de façons de choisir k éléments parmi n.',
          }
        : {
            id: qid,
            card,
            label: 'Écrire avec les factorielles',
            hora: 2,
            why: 'k parmi n = n! ÷ (k! × (n − k)!).',
          };
    case 'counting.binom_two':
      return {
        id: qid,
        card,
        label: '2 parmi n = n(n − 1) ÷ 2',
        hora: 2,
        why: 'Choisir 2 éléments parmi n : n choix pour le premier, n − 1 pour le second, et l’ordre ne compte pas (÷ 2).',
      };
    case 'counting.symmetry':
      return {
        id: qid,
        card,
        label: 'Symétrie',
        hora: 2,
        why: 'Choisir les k éléments que l’on prend, c’est choisir les n − k que l’on laisse : k parmi n = (n − k) parmi n.',
      };
    case 'counting.pascal':
    case 'counting.pascal_down':
      return {
        id: qid,
        card,
        label: 'Relation de Pascal',
        hora: 3,
        why: 'k parmi n + (k + 1) parmi n = (k + 1) parmi (n + 1) : chaque case du triangle de Pascal est la somme des deux cases au-dessus.',
      };
    case 'counting.fact_rec':
      return factCount(written) > factCount(from)
        ? {
            id: qid,
            card,
            label: 'Regrouper en factorielle',
            hora: 2,
            why: 'n × (n − 1)! = n!.',
          }
        : {
            id: qid,
            card,
            label: 'n! = n × (n − 1)!',
            hora: 2,
            why: 'On sort le premier facteur de la factorielle : n! = n × (n − 1)!.',
          };
    default: {
      const ruleLabel = p.label ?? 'Règle du cours';
      const distrib = /distributivit/i.test(ruleLabel);
      return {
        id: qid,
        card,
        label: distrib ? 'Développer' : ruleLabel.slice(0, 60),
        hora: 2,
        why: ruleLabel + '.',
      };
    }
  }
}

/** nombre de « parmi » écrits dans une ligne (moteur v27) */
function binomCount(s: Statement | undefined): number {
  return s ? countNodes(s, (t) => t.k === 'binom') : 0;
}
/** nombre de factorielles écrites dans une ligne (moteur v27) */
function factCount(s: Statement | undefined): number {
  return s ? countNodes(s, (t) => t.k === 'fn' && t.f === 'fact') : 0;
}
function countNodes(s: Statement, pred: (t: Term) => boolean): number {
  const walk = (t: Term): number => {
    const own = pred(t) ? 1 : 0;
    switch (t.k) {
      case 'add':
      case 'mul':
        return own + t.c.reduce((n, c) => n + walk(c), 0);
      case 'div':
      case 'pow':
      case 'binom':
        return own + walk(t.a) + walk(t.b);
      case 'fn':
        return own + walk(t.a);
      default:
        return own;
    }
  };
  return (
    (s.k === 'expr' ? walk(s.e) : 0) + relationsOf(s).reduce((n, r) => n + walk(r.l) + walk(r.r), 0)
  );
}

/** Libellé d'un pas reconnu par une règle d'un paquet (réutilisé par la récurrence, moteur v26). */
export const kernelRuleStep = ruleStep;

/** Retrouver un angle (moteur v21) : de cos(Â) = c à Â = arccos(c). */
function inverseTrigStep(c: StepCheck, x: string): StepInfo {
  const f = c.explanation?.params?.f ?? 'cos';
  const inv = `arc${f}`;
  return {
    id: KERNEL_STEP_IDS.inverse,
    card: 'Trigonométrie',
    label: `Utiliser ${inv}`,
    hora: 2,
    why: `${x} est un angle de la figure, mesuré en degrés : ${f}(${x}) = c équivaut à ${x} = ${inv}(c) (touche ${f}⁻¹ de la calculatrice).`,
  };
}

/** « ajouter un même terme » avec une SOMME (−2x + 6) : deux opérations en une */
function addsSeveralTerms(c: StepCheck): boolean {
  if (!/\.add_both_sides@/.test(c.ruleId ?? '')) return false;
  const p = parseStatement(c.explanation?.params?.v_c ?? '');
  return p.ok && p.s.k === 'expr' && canon(p.s.e).k === 'add';
}

/** Libellé d'un pas de calcul (même ligne, mieux écrite). */
function computeStep(
  from: KLine,
  to: KLine,
  fromS: Statement,
  toS: Statement,
  x?: string,
): StepInfo | null {
  const sum = (s: Statement): number =>
    relationsOf(s).reduce((a, r) => a + unexpanded(r.l) + unexpanded(r.r), 0) +
    (s.k === 'expr' ? unexpanded(s.e) : 0);
  if (sum(toS) < sum(fromS))
    return {
      id: KERNEL_STEP_IDS.expand,
      card: 'Calcul littéral',
      label: 'Développer',
      hora: 2,
      why: 'On développe : la ligne ne change pas, elle s’écrit autrement.',
    };
  // moteur v47 : « x ln 2 − x ln 3 » → « x(ln 2 − ln 3) » : x en facteur
  if (readableLabels && x) {
    const col = (s: Statement): number =>
      relationsOf(s).reduce((a, r) => a + collectableX(r.l, x) + collectableX(r.r, x), 0);
    // x × (somme constante) écrit dans la ligne (« 2x + 3x » → « 5x » reste un calcul)
    const factored = (t: Term): boolean =>
      (t.k === 'mul' &&
        t.c.some((c) => c.k === 'sym' && c.n === x) &&
        t.c.some((c) => c.k === 'add' && isConst(c))) ||
      ((t.k === 'add' || t.k === 'mul') && t.c.some(factored));
    if (col(toS) < col(fromS) && relationsOf(toS).some((r) => factored(r.l) || factored(r.r)))
      return {
        id: KERNEL_STEP_IDS.compute,
        card: 'Calcul littéral',
        label: `Mettre ${x} en facteur`,
        hora: 1,
        why: `On regroupe les termes en ${x} : a${x} + b${x} = (a + b)${x}. Il ne reste qu’une division pour isoler ${x}.`,
      };
  }
  if (to.calc < from.calc) {
    const dec = (k: RadKind): boolean => to.rad[k] < from.rad[k];
    if (dec('value'))
      return {
        id: KERNEL_STEP_IDS.compute,
        card: 'Trigonométrie',
        label: 'Valeur exacte',
        hora: 1,
        why: 'On remplace par sa valeur exacte, à connaître : cos 30° = √3 ÷ 2, sin 30° = 1 ÷ 2, tan 45° = 1…',
      };
    if (dec('conj'))
      return {
        id: KERNEL_STEP_IDS.compute,
        card: 'Racines carrées',
        label: 'Multiplier par l’expression conjuguée',
        hora: 2,
        why: '(a − b)(a + b) = a² − b² : on multiplie le haut et le bas par l’expression conjuguée, la racine quitte le dénominateur.',
      };
    if (dec('radDen'))
      return {
        id: KERNEL_STEP_IDS.compute,
        card: 'Racines carrées',
        label: 'Rendre le dénominateur rationnel',
        hora: 2,
        why: 'On multiplie le haut et le bas par la racine : √3 × √3 = 3, il n’y a plus de racine au dénominateur.',
      };
    if (dec('sqrt'))
      return {
        id: KERNEL_STEP_IDS.compute,
        card: 'Racines carrées',
        label: 'Simplifier la racine',
        hora: 1,
        why: 'On sort les carrés de la racine : √12 = √(4 × 3) = 2√3.',
      };
    if (dec('like'))
      return {
        id: KERNEL_STEP_IDS.compute,
        card: 'Racines carrées',
        label: 'Réduire',
        hora: 1,
        why: 'On ajoute les coefficients d’une même racine : 2√3 + 3√3 = 5√3.',
      };
    return {
      id: KERNEL_STEP_IDS.compute,
      card: 'Calcul',
      label: 'Calculer',
      hora: 1,
      why: 'On calcule et on réduit chaque membre.',
    };
  }
  if (to.h < from.h && !swapped(fromS, toS))
    return {
      id: KERNEL_STEP_IDS.compute,
      card: 'Calcul',
      label: 'Calculer',
      hora: 1,
      why: 'On applique les règles de calcul (puissances, exponentielle) : la ligne ne change pas, elle s’écrit plus simplement.',
    };
  if (to.h < from.h)
    return {
      id: KERNEL_STEP_IDS.swap,
      card: 'Calcul',
      label: 'Échanger les membres',
      hora: 1,
      why: 'On lit la même relation dans l’autre sens : l’inconnue passe à gauche.',
    };
  return null;
}

/** La ligne d'arrivée est-elle celle de départ, membres échangés (« 2 > x » → « x < 2 ») ? */
function swapped(fromS: Statement, toS: Statement): boolean {
  if (fromS.k !== 'rel' || toS.k !== 'rel') return false;
  const same = (a: Term, b: Term): boolean => {
    const ra = reduced(a);
    const rb = reduced(b);
    return !!ra && !!rb && termKey(ra) === termKey(rb);
  };
  return toS.rel === FLIP[fromS.rel] && same(fromS.l, toS.r) && same(fromS.r, toS.l);
}

/* ------------------------------ juge ------------------------------ */

function knownAtoms(ks: KernelSheetState): string[][] {
  if (!ks.known) {
    const texts = [...referencePath(ks), ks.spec.answer];
    ks.known = texts.flatMap((t) => {
      if (isSignTableText(t)) return [[t]];
      try {
        return [normalize(lineAtoms(parseText(t)))];
      } catch {
        return [];
      }
    });
  }
  return ks.known;
}
function strictPrefixOfKnown(ks: KernelSheetState, A: string[]): boolean {
  return knownAtoms(ks).some((k) => k.length > A.length && A.every((a, i) => k[i] === a));
}

export function kernelJudge(
  ks: KernelSheetState,
  chips: Chip[],
  opts?: { verify?: boolean },
): KernelVerdict {
  const before = readableLabels;
  readableLabels = ks.readable;
  try {
    return judgeKernelLine(ks, chips, opts);
  } finally {
    readableLabels = before;
  }
}

function judgeKernelLine(
  ks: KernelSheetState,
  chips: Chip[],
  opts?: { verify?: boolean },
): KernelVerdict {
  // moteur v42 : une ligne « tableau de signes » (un seul bout)
  const tableAt = chips.findIndex((c) => c.atoms.some(isSignTableText));
  if (tableAt >= 0) {
    if (chips.length !== 1 || chips[0]!.atoms.length !== 1)
      return {
        kind: 'syntax',
        atoms: chips.flatMap((c) => c.atoms),
        title: 'Le tableau seul sur sa ligne',
        message: 'Un tableau de signes se met seul sur sa ligne : retire les autres bouts.',
        badChip: tableAt === 0 ? 1 : tableAt,
      };
    return signTableVerdict(ks, chips[0]!.atoms[0]!);
  }
  const norm = normalizeChips(chips);
  const A = norm.atoms;
  if (!A.length) return { kind: 'empty', atoms: A, badChip: -1 };
  // moteur v23 : chaînes de comparaisons « −1 < x ≤ 2 »
  const syn = checkSyntax(A, { chains: ks.poly || !!ks.trig });
  const prefix = strictPrefixOfKnown(ks, A);
  if (!syn.ok) {
    if (prefix) return { kind: 'building', atoms: A, complete: false };
    return {
      kind: 'syntax',
      atoms: A,
      title: 'Ça commence mal',
      message: syn.msg,
      badChip: norm.src[syn.index] !== undefined ? norm.src[syn.index]! : chips.length - 1,
    };
  }
  if (!syn.complete || !syn.hasEq)
    return { kind: prefix ? 'building' : 'open', atoms: A, complete: false };
  let E: Line;
  try {
    E = parseLine(A, { chains: ks.poly || !!ks.trig });
  } catch {
    return { kind: prefix ? 'building' : 'open', atoms: A, complete: false };
  }
  const v = evaluate(ks, E, A);
  if (prefix && !opts?.verify) {
    if (v.kind === 'step') return { ...v, kind: 'ready' };
    return { kind: 'building', atoms: A, complete: true };
  }
  // la ligne n'est peut-être pas finie : on attend que l'élève demande la vérification
  if (!opts?.verify && (v.kind === 'unjustified' || v.kind === 'false'))
    return { kind: 'pending', atoms: A, complete: true };
  return v;
}

function evaluate(ks: KernelSheetState, E: Line, A: string[]): KernelVerdict {
  const before = readableLabels;
  readableLabels = ks.readable;
  try {
    return evaluateLine(ks, E, A);
  } finally {
    readableLabels = before;
  }
}

function evaluateLine(ks: KernelSheetState, E: Line, A: string[]): KernelVerdict {
  if (ks.sys) return evaluateSystem(ks, ks.sys, E, A);
  // moteur v48 : « 1000 × 0,8^11 ≈ 85,9 » teste un rang
  if (ks.test) {
    const tv = testVerdict(ks, E, A);
    if (tv) return tv;
  }
  const ap = approxIn(ks, approximationOf(E));
  if (ap) return approximationVerdict(ks, ap.l, ap.r, E, A);
  const read0 = statementOfLine(E);
  const read = read0.ok ? { ok: true as const, s: readIn(ks, read0.s) } : read0;
  if (!read.ok || read.s.k === 'expr')
    return {
      kind: 'unjustified',
      atoms: A,
      line: E,
      title: 'Une relation par ligne',
      message:
        'Écris une égalité ou une inégalité (avec « = », « < », « > », « ≤ » ou « ≥ »), une seule par ligne.',
    };
  const T = klineOf(ks, read.s, true, read0.ok && writesAux(ks, read0.s));
  // piège connu
  const trap = ks.traps.find((t) => t.K === T.K);
  if (trap)
    return {
      kind: 'trap',
      atoms: A,
      line: E,
      title: 'Piège : ' + lower(trap.title),
      message: trap.why,
      trap: { line: trap.line, title: trap.title, why: trap.why },
      badChip: -1,
    };
  // Δ, x₁, x₂ (moteur v23) : grandeurs du trinôme, jugées à part
  if (ks.poly) {
    const aux = auxVerdict(ks, read.s, E, A);
    if (aux) return aux;
  }
  if (T.raw === ks.start.raw && !T.auxWritten)
    return {
      kind: 'given',
      atoms: A,
      line: E,
      title: 'C’est l’énoncé',
      message: 'Cette ligne est déjà en tête de ta feuille : transforme-la.',
    };
  if (ks.lines.some((l) => l.raw === T.raw && !!l.auxWritten === !!T.auxWritten))
    return {
      kind: 'dup',
      atoms: A,
      line: E,
      title: 'Déjà écrit',
      message: 'Tu as déjà cette ligne sur ta feuille.',
    };
  // moteur v48 : la conclusion d'un seuil par tests (« n ≥ 11 »)
  if (ks.test) {
    const b = integerBound(read.s, ks.x);
    if (b) return testConclusion(ks, b, T, E, A);
  }
  // moteur v49 : une condition de la feuille (« x > 0 » : ln(x) existe)
  if (ks.conds.length) {
    const cv = conditionVerdict(ks, T, E, A);
    if (cv) return cv;
  }

  let ok: { from: KLine; c: StepCheck } | null = null;
  let implies: StepCheck | null = null;
  let invalid: StepCheck | null = null;
  let conditional: StepCheck | null = null;
  for (const from of sources(ks)) {
    const c = check(ks, from, T.s, T.raw);
    if (c.status === 'valid' && (c.relation === 'equivalent' || c.relation === 'identity')) {
      ok = { from, c };
      break;
    }
    if (c.status === 'valid') {
      // implication : la ligne a des solutions en plus (toutes les sources sont équivalentes)
      implies = c;
      break;
    }
    if (c.status === 'invalid') {
      invalid = c;
      break;
    }
    if (c.status === 'conditional') conditional ??= c;
  }
  if (ok) return stepVerdict(ks, ok.from, ok.c, T, E, A);
  // moteur v49 : juste, mais seulement avec une condition pas encore écrite
  if ((implies || conditional) && ks.conds.length) {
    const need = conditionNeeded(ks, T);
    if (need)
      return {
        kind: 'unjustified',
        atoms: A,
        line: E,
        title: 'La condition d’abord',
        message: `Ta ligne n’est juste qu’avec une condition sur ${ks.x}. ${need.why.replace(/ :.*$/, '.')} Écris d’abord cette condition sur sa ligne.`,
      };
  }
  if (implies)
    return {
      kind: 'false',
      atoms: A,
      line: E,
      title: 'Des solutions en trop',
      message:
        (implies.explanation?.params?.at
          ? `${implies.explanation.params.at} vérifie ta ligne, mais pas la précédente : `
          : 'Ta ligne découle de la précédente, mais elle a plus de solutions : ') +
        'on doit garder exactement les mêmes solutions.' +
        (relationsOf(T.s).some((r) => r.rel === '≤' || r.rel === '≥')
          ? ' Attention aux inégalités larges (≤, ≥) et strictes (<, >).'
          : ''),
    };
  if (invalid) return invalidVerdict(ks, invalid, T, E, A);
  if (conditional) return conditionalVerdict(conditional, E, A);
  return {
    kind: 'useless',
    atoms: A,
    line: E,
    unknown: true,
    title: 'Je ne sais pas encore vérifier ce passage',
    message:
      'Ta ligne est peut-être juste, mais je ne sais pas la vérifier. Garde-la sans HORA, ou écris le passage autrement.',
    kern: { s: T.s, trusted: false, ...(T.auxWritten ? { auxWritten: true } : {}) },
  };
}

/**
 * « S ≈ v » : une valeur approchée d'un nombre (« ln(100) ÷ ln(1,5) ≈ 11,4 ») ou de
 * l'inconnue une fois sa valeur exacte écrite (« x = ln(4) », puis « x ≈ 1,39 »).
 * Elle aide à se repérer ; elle n'est jamais une ligne de confiance (la réponse
 * attendue reste exacte) et ne rapporte pas de HORA.
 */
function approximationVerdict(
  ks: KernelSheetState,
  l: Term,
  v: Term,
  E: Line,
  A: string[],
): KernelVerdict {
  let exact: Term | null = isConst(l) ? l : null;
  if (!exact && l.k === 'sym' && l.n === ks.x) {
    // la valeur exacte écrite sur la feuille : « x = ln(4) »
    for (const line of [...ks.lines].reverse()) {
      if (!line.trusted || line.s.k !== 'rel' || line.s.rel !== '=') continue;
      const { l: a, r: b } = line.s;
      if (a.k === 'sym' && a.n === ks.x && isConst(b)) exact = b;
      else if (b.k === 'sym' && b.n === ks.x && isConst(a)) exact = a;
      if (exact) break;
    }
    if (!exact)
      return {
        kind: 'unjustified',
        atoms: A,
        line: E,
        title: `Calcule d’abord ${ks.x}`,
        message: `Écris la valeur exacte de ${ks.x} (« ${ks.x} = … ») avant d’en donner une valeur approchée.`,
      };
  }
  if (!exact)
    return {
      kind: 'unjustified',
      atoms: A,
      line: E,
      title: 'Une valeur approchée de quoi ?',
      message: `« ≈ » sert à donner une valeur approchée d’un nombre, ou de ${ks.x} une fois sa valeur exacte écrite.`,
    };
  // réponse arrondie demandée (moteur v20) : l'arrondi de la réponse est le dernier pas
  if (ks.rounding && l.k === 'sym' && l.n === ks.x) {
    const r = roundingVerdictOf(ks, exact, v, E, A);
    if (r) return r;
  }
  const c = checkApproximation(null, exact, v, { rules: ks.rules, hypotheses: ks.hyps });
  const about = (): string => {
    const f = evalFloat(exact!, {});
    return Number.isFinite(f)
      ? `Ce nombre vaut environ ${formatNumber(Math.round(f * 1e4) / 1e4)}.`
      : '';
  };
  if (c.status === 'valid')
    return {
      kind: 'useless',
      atoms: A,
      line: E,
      title: 'Bonne valeur approchée',
      message:
        'Elle t’aide à te repérer, mais la réponse attendue reste exacte : garde-la si tu veux.',
    };
  if (c.status === 'invalid' && c.explanation?.code === 'truncated')
    return {
      kind: 'false',
      atoms: A,
      line: E,
      title: 'Arrondir, ce n’est pas couper',
      message:
        'Regarde le chiffre juste après : s’il vaut 5 ou plus, le dernier chiffre gardé augmente de 1.',
    };
  if (c.status === 'invalid')
    return {
      kind: 'false',
      atoms: A,
      line: E,
      title: 'Valeur approchée fausse',
      message: about() || 'Vérifie le calcul.',
    };
  return {
    kind: 'useless',
    atoms: A,
    line: E,
    unknown: true,
    title: 'Je ne sais pas encore vérifier ce passage',
    message: 'Je ne sais pas vérifier cette valeur approchée. Garde-la sans HORA si elle t’aide.',
  };
}

/** L'arrondi de la réponse (« AC ≈ 7,8 ») : le pas « Arrondir », ou ce qui cloche. */
function roundingVerdictOf(
  ks: KernelSheetState,
  exact: Term,
  v: Term,
  E: Line,
  A: string[],
): KernelVerdict | null {
  const r = ks.rounding!;
  const words = roundingWords(r.places);
  const p = decimalPlacesOf(v);
  if (p === null) return null;
  if (p > r.places) {
    const c = checkApproximation(null, exact, v, { rules: ks.rules, hypotheses: ks.hyps });
    if (c.status !== 'valid') return null;
    return {
      kind: 'useless',
      atoms: A,
      line: E,
      title: 'Garde moins de chiffres',
      message: `On demande ${ks.x} arrondi ${words}${r.places === 0 ? ' : aucun chiffre après la virgule.' : ` : ${r.places} chiffre${r.places > 1 ? 's' : ''} après la virgule.`}`,
    };
  }
  if (!isRequestedRounding(ks, exact, v)) {
    const c = checkApproximation(null, exact, v, {
      rules: ks.rules,
      hypotheses: ks.hyps,
      approximation: { places: r.places },
    });
    if (c.status === 'invalid' && c.explanation?.code === 'approximation_too_coarse')
      return {
        kind: 'useless',
        atoms: A,
        line: E,
        title: 'Pas assez précis',
        message: `On demande ${ks.x} arrondi ${words}.`,
      };
    return null;
  }
  if (ks.rounded !== null)
    return {
      kind: 'dup',
      atoms: A,
      line: E,
      title: 'Déjà écrit',
      message: `Tu as déjà arrondi ${ks.x}.`,
    };
  return {
    kind: 'step',
    atoms: A,
    line: E,
    hora: r.hora,
    final: true,
    rule: {
      id: KERNEL_STEP_IDS.round,
      card: 'Calcul',
      label: 'Arrondir',
      hora: r.hora,
      why: `On arrondit ${ks.x} ${words} : on regarde le chiffre suivant, s’il vaut 5 ou plus le dernier chiffre gardé augmente de 1.`,
    },
    round: evalFloat(v, {}),
  };
}

/** la ligne la plus avancée est un cube face à une constante (moteur v41) */
function frontierIsCube(ks: KernelSheetState): boolean {
  const f = frontier(ks).s;
  return f.k === 'rel' && relForm(f, ks.x) === 'cube';
}

function stepVerdict(
  ks: KernelSheetState,
  from: KLine,
  c: StepCheck,
  T: KLine,
  E: Line,
  A: string[],
): KernelVerdict {
  if (!ks.condsOk.length) return stepVerdictCore(ks, from, c, T, E, A);
  // moteur v49 : une ligne juste seulement avec les conditions n'est jamais la réponse ;
  // seule la réponse (dite sans les conditions) conclut
  const condDep = !answerLike(ks, T);
  // tenir compte de la condition : « x < e » (avec x > 0) → « 0 < x < e », la réponse
  if (!condDep && from.condDep && solvedForm(from, ks.x, ks.trig) && solvedForm(T, ks.x, ks.trig)) {
    const keep = keepStep(ks, from, T);
    const info: StepInfo = keep?.info
      ? {
          ...keep.info,
          why: keep.info.why.replace(
            / ne convient pas : .*$/,
            ` ne convient pas : il ne vérifie pas la condition ${conditionsText(ks)}.`,
          ),
        }
      : {
          id: KERNEL_STEP_IDS.conditionKeep,
          card: 'Équations',
          label: 'Tenir compte de la condition',
          hora: 1,
          why: `On ne garde que les ${ks.x} qui vérifient la condition ${conditionsText(ks)}.`,
        };
    return {
      kind: 'step',
      atoms: A,
      line: E,
      hora: info.hora,
      final: !roundingNeeded(ks),
      rule: info,
      kern: { s: T.s, trusted: true },
    };
  }
  const v = stepVerdictCore(ks, from, c, T, E, A);
  if (!condDep) return v;
  return { ...v, final: false, ...(v.kern ? { kern: { ...v.kern, condDep: true } } : {}) };
}

function stepVerdictCore(
  ks: KernelSheetState,
  from: KLine,
  c: StepCheck,
  T: KLine,
  E: Line,
  A: string[],
): KernelVerdict {
  const kern = { s: T.s, trusted: true, ...(T.auxWritten ? { auxWritten: true } : {}) };
  // v47 : remplacer X par eˣ (ou l'inverse) sur la ligne la plus avancée, même sans avancer
  const swap = !!ks.auxiliary && auxSwap(ks, from, T) && !better(frontier(ks), from);
  if (!better(T, frontier(ks)) && !swap)
    return {
      kind: 'useless',
      atoms: A,
      line: E,
      title: 'Juste, mais ça ne t’avance pas',
      message: ks.poly
        ? frontierIsCube(ks)
          ? 'Ta ligne est juste, mais elle ne rapproche pas de la réponse : la fonction cube est strictement croissante, A³ = c a une seule solution, le nombre dont le cube vaut c. Écris-la. Garde ta ligne si elle t’aide.'
          : 'Ta ligne est juste, mais elle ne rapproche pas de la réponse : tout d’un côté, factoriser (ou Δ et les racines), puis conclure. Garde-la si elle t’aide.'
        : ks.form === 'canonical'
          ? 'Ta ligne est juste, mais elle ne rapproche pas de la forme canonique a(x − α)² + β : mets a en facteur devant x² et x, complète le carré, développe, puis calcule. Garde-la si elle t’aide.'
          : ks.form === 'fraction'
            ? 'Ta ligne est juste, mais elle ne rapproche pas d’une seule fraction : réduis au même dénominateur, développe le numérateur, réduis-le, puis simplifie. Garde-la si elle t’aide.'
            : ks.form === 'factored'
              ? 'Ta ligne est juste, mais elle ne rapproche pas de la forme factorisée : fais apparaître le facteur commun (ou a² − b², a² + 2ab + b²), puis factorise. Garde-la si elle t’aide.'
              : ks.form === 'expanded'
                ? 'Ta ligne est juste, mais elle ne rapproche pas de la forme développée et réduite : développe un produit à la fois, puis réduis. Garde-la si elle t’aide.'
                : ks.trig
                  ? 'Ta ligne est juste, mais elle ne rapproche pas de la réponse : isole cos(x) (ou sin(x)), reconnais la valeur remarquable, puis écris les solutions. Garde-la si elle t’aide.'
                  : ks.count?.answer
                    ? 'Ta ligne est juste, mais elle ne rapproche pas de la réponse : écris « k parmi n » avec les factorielles, simplifie les quotients de factorielles, puis calcule. Garde-la si elle t’aide.'
                    : `Ta ligne est juste, mais elle ne rapproche pas de « ${ks.x} » seul. Garde-la si elle t’aide, ou repars de ta dernière ligne.`,
      kern,
    };
  // dénombrement (moteur v27) : une règle du paquet (Pascal, symétrie…), ou les calculs
  // sur « parmi » et les factorielles d'une ligne qui garde sa valeur
  if (ks.count && T.cnt && from.cnt) {
    const ruled = c.proof === 'rule' && !!c.ruleId?.startsWith('counting.');
    const same = (T.NK !== null && T.NK === from.NK) || sameConstValue(from.s, T.s, ks.x);
    const cs =
      !ruled && same && countChanged(from.cnt, T.cnt)
        ? countStep(from.cnt, T.cnt, {
            division: relationsOf(T.s).some((r) => hasDivision(r.l) || hasDivision(r.r)),
            plain: T.calc === 0 && countWeight(T.cnt) === 0,
          })
        : null;
    if (cs && 'reject' in cs) return { kind: 'unjustified', atoms: A, line: E, ...cs.reject };
    const info = ruled ? ruleStep(c, T.s, from.s) : cs?.info;
    if (info)
      return {
        kind: 'step',
        atoms: A,
        line: E,
        hora: info.hora,
        final: solvedForm(T, ks.x, ks.trig) && !roundingNeeded(ks),
        rule: info,
        kern,
      };
  }
  // équation trigonométrique (moteur v24) : les pas du cours
  if (ks.trig) {
    const t = trigStep(ks, from, c, T);
    if (t?.reject) return { kind: 'unjustified', atoms: A, line: E, ...t.reject };
    if (t?.info)
      return {
        kind: 'step',
        atoms: A,
        line: E,
        hora: t.info.hora,
        final: solvedForm(T, ks.x, ks.trig) && !roundingNeeded(ks),
        rule: t.info,
        kern,
      };
  }
  // inconnue auxiliaire (moteur v47) : poser X, les pas du cours en X, revenir à x
  if (ks.auxiliary) {
    const p = auxStep(ks, from, c, T);
    if (p?.reject) return { kind: 'unjustified', atoms: A, line: E, ...p.reject };
    if (p?.info)
      return {
        kind: 'step',
        atoms: A,
        line: E,
        hora: p.info.hora,
        final: solvedForm(T, ks.x, ks.trig) && !roundingNeeded(ks),
        rule: p.info,
        kern,
      };
  }
  // second degré, produit, quotient (moteur v23) : les pas du cours
  if (ks.poly) {
    const p = polyStep(ks, from, c, T);
    if (p?.reject) return { kind: 'unjustified', atoms: A, line: E, ...p.reject };
    if (p?.info)
      return {
        kind: 'step',
        atoms: A,
        line: E,
        hora: p.info.hora,
        final: solvedForm(T, ks.x, ks.trig) && !roundingNeeded(ks),
        rule: p.info,
        kern,
      };
  }
  // une seule fraction (moteur v46) : des fractions à dénominateurs différents réunies ET
  // réduites d'un coup (« 1 ÷ ((n + 1)(n + 2)) » depuis uₙ₊₁ − uₙ) : trop d'un coup
  if (
    ks.form === 'fraction' &&
    from.h >= 10 &&
    !from.fm?.frac?.inner &&
    T.h < 10 &&
    T.fm?.frac?.numExpand === 0
  ) {
    const fv = valueSide(from.s, ks.x);
    const tv = valueSide(T.s, ks.x);
    const want = fv ? combinedNumeratorTerms(fv) : null;
    if (want !== null && tv && numeratorTermCount(tv) < want)
      return {
        kind: 'unjustified',
        atoms: A,
        line: E,
        title: 'Une étape à la fois',
        message:
          'Ta ligne est juste, mais elle réduit au même dénominateur ET calcule le numérateur d’un coup. Écris d’abord la fraction avec le numérateur commun (a × d − c × b), puis développe et réduis.',
      };
  }
  // écriture à développer ou à factoriser (moteur v33) : les pas nommés par la forme
  if (ks.form) {
    const f = formStep(ks.form, ks.x, from, T);
    if (f)
      return {
        kind: 'step',
        atoms: A,
        line: E,
        hora: f.hora,
        final: solvedForm(T, ks.x, ks.trig),
        rule: f,
        kern,
      };
  }
  let info: StepInfo | null = null;
  if (c.proof === 'rule' && c.ruleId && addsSeveralTerms(c))
    return {
      kind: 'unjustified',
      atoms: A,
      line: E,
      title: 'Une opération à la fois',
      message: `Ta ligne est juste, mais elle ajoute ${wrapTerm(c.explanation?.params?.v_c ?? '')} d’un coup : déplace un terme à la fois, puis calcule.`,
    };
  const sameLine = (T.NK !== null && T.NK === from.NK) || sameConstValue(from.s, T.s, ks.x);
  // une identité du calcul littéral (« 4x + 3x » lu comme une factorisation) se nomme
  // par ce qu'elle fait à la ligne : développer, ou calculer et réduire
  if (c.proof === 'rule' && c.ruleId?.startsWith('algebra.') && sameLine)
    info = computeStep(from, T, from.s, T.s, ks.x) ?? ruleStep(c, T.s, from.s);
  else if (c.proof === 'rule' && c.ruleId) info = ruleStep(c, T.s, from.s);
  else if (c.explanation?.code === 'inverse_trig') info = inverseTrigStep(c, ks.x);
  else if (sameLine) info = computeStep(from, T, from.s, T.s, ks.x);
  if (info && (info.id === KERNEL_STEP_IDS.compute || info.id === KERNEL_STEP_IDS.expand)) {
    const tooMuch = radicalJump(from, T);
    if (tooMuch) return { kind: 'unjustified', atoms: A, line: E, ...tooMuch };
  } else if (info && c.proof === 'rule' && RULE_PLUS_CALC.some((k) => T.rad[k] < from.rad[k]))
    // une règle ET un calcul sur les racines (cos 30° remplacé, racine simplifiée) d'un coup
    return {
      kind: 'unjustified',
      atoms: A,
      line: E,
      title: 'Une opération à la fois',
      message:
        'Ta ligne est juste, mais elle fait une opération des deux côtés ET un calcul (valeur exacte, racine…) d’un coup. Écris-les l’un après l’autre.',
    };
  // seuil (moteur v27) : « n > 3,32… » → « n ≥ 4 », n entier
  if (!info && ks.intBound) info = integerBoundStep(ks, from, T);
  if (!info)
    return {
      kind: 'unjustified',
      atoms: A,
      line: E,
      title: 'Juste, mais trop d’un coup',
      message:
        'Ta ligne est juste, mais elle fait plusieurs opérations à la fois. Écris-les une par une : une opération des deux côtés, puis le calcul.',
    };
  // une réponse à arrondir n'est finie qu'une fois arrondie (moteur v20)
  const final = solvedForm(T, ks.x, ks.trig) && !roundingNeeded(ks);
  return {
    kind: 'step',
    atoms: A,
    line: E,
    hora: info.hora,
    final,
    rule: info,
    kern,
  };
}

/** le nombre mis en facteur dans « a(x² + bx) + c » (« 2 », « −1 »), sinon null */
function canonicalFactor(r: Term | null): string | null {
  if (!r) return null;
  const terms = r.k === 'add' ? r.c : [r];
  for (const u of terms) {
    if (u.k !== 'mul') continue;
    const nums = u.c.filter((f) => f.k === 'num');
    if (nums.length === 1 && u.c.some((f) => f.k === 'add')) return termText(nums[0]!);
  }
  return null;
}

/**
 * Les pas d'une écriture à développer ou à factoriser (moteur v33), nommés par ce qui
 * change dans la FORME ; null : le pas se nomme comme d'habitude (développer, calculer).
 */
function formStep(form: KernelForm, x: string, from: KLine, to: KLine): StepInfo | null {
  const f = from.fm;
  const t = to.fm;
  if (!f || !t) return null;
  const parens: StepInfo = {
    id: KERNEL_STEP_IDS.compute,
    card: 'Calcul littéral',
    label: 'Enlever les parenthèses',
    hora: 1,
    why: 'Des parenthèses précédées de « + » s’enlèvent sans rien changer : a + (b − c) = a + b − c.',
  };
  // une seule fraction (moteur v46) : même dénominateur, développer le numérateur, simplifier
  if (form === 'fraction') {
    const ff = f.frac;
    const tf = t.frac;
    if (!ff || !tf || to.h >= from.h) return null;
    // une fraction de fractions : le numérateur en une fraction, puis diviser
    if (ff.inner) {
      if (!tf.inner)
        return {
          id: KERNEL_STEP_IDS.compute,
          card: 'Fractions',
          label: 'Diviser une fraction',
          hora: 1,
          why: 'Diviser par c, c’est multiplier le dénominateur par c : (a ÷ b) ÷ c = a ÷ (b × c).',
        };
      if (ff.inner.h >= 10 && tf.inner.h < 10)
        return {
          id: KERNEL_STEP_IDS.compute,
          card: 'Fractions',
          label: 'Même dénominateur',
          hora: 2,
          why: 'Au numérateur : a ÷ b − c ÷ d = (a × d − c × b) ÷ (b × d).',
        };
      return null;
    }
    // de plusieurs fractions…
    if (from.h >= 10) {
      if (to.h >= 10)
        return {
          id: KERNEL_STEP_IDS.compute,
          card: 'Fractions',
          label: 'Réduire au même dénominateur',
          hora: 1,
          why: 'On multiplie le numérateur et le dénominateur de chaque fraction par ce qui manque : a ÷ b = (a × d) ÷ (b × d).',
        };
      return ff.dens === 1
        ? {
            id: KERNEL_STEP_IDS.compute,
            card: 'Fractions',
            label: 'Additionner les numérateurs',
            hora: 1,
            why: 'Même dénominateur : a ÷ d + b ÷ d = (a + b) ÷ d. Attention aux signes devant une fraction.',
          }
        : {
            id: KERNEL_STEP_IDS.compute,
            card: 'Fractions',
            label: 'Même dénominateur',
            hora: 2,
            why: 'a ÷ b − c ÷ d = (a × d − c × b) ÷ (b × d) : on réduit au même dénominateur, puis on écrit une seule fraction.',
          };
    }
    // … à une seule fraction : développer le numérateur, simplifier
    if (ff.reducible && !ff.visible && tf.visible)
      return {
        id: KERNEL_STEP_IDS.factor,
        card: 'Fractions',
        label: 'Faire apparaître le facteur commun',
        hora: 2,
        why: 'On factorise le numérateur (ou le dénominateur) : le facteur commun apparaît en haut et en bas.',
      };
    if (ff.reducible && !tf.reducible)
      return {
        id: KERNEL_STEP_IDS.compute,
        card: 'Fractions',
        label: 'Simplifier la fraction',
        hora: 1,
        why: 'Le numérateur et le dénominateur ont un facteur commun (non nul) : on divise les deux par ce facteur.',
      };
    if (tf.numExpand < ff.numExpand)
      return {
        id: KERNEL_STEP_IDS.expand,
        card: 'Calcul littéral',
        label: 'Développer le numérateur',
        hora: 2,
        why: 'On développe le numérateur, un produit à la fois ; le dénominateur peut rester factorisé.',
      };
    return null;
  }
  // forme canonique (moteur v43) : mettre a en facteur, compléter le carré, développer
  if (form === 'canonical') {
    if (to.h >= from.h) return null;
    if (to.h === 2 && from.h >= 3) {
      const a = canonicalFactor(valueSide(to.s, x));
      return {
        id: KERNEL_STEP_IDS.factor,
        card: 'Second degré',
        label: a ? `Mettre ${a} en facteur` : 'Mettre a en facteur',
        hora: 1,
        why: 'On met le coefficient de x² en facteur dans les deux termes en x : ax² + bx = a(x² + (b ÷ a)x). Le nombre seul reste à part.',
      };
    }
    if (to.h <= 1 && from.h >= 2)
      return {
        id: KERNEL_STEP_IDS.factor,
        card: 'Second degré',
        label: 'Compléter le carré',
        hora: 2,
        why: 'x² + 2px est le début de (x + p)² = x² + 2px + p² : x² + 2px = (x + p)² − p².',
      };
    if (to.h === 0 && from.h === 1)
      return {
        id: KERNEL_STEP_IDS.expand,
        card: 'Second degré',
        label: 'Développer',
        hora: 1,
        why: 'On distribue a : a((x + p)² − p²) = a(x + p)² − ap². Le carré reste écrit.',
      };
    return null;
  }
  if (form === 'factored') {
    if (t.product && !f.product)
      return {
        id: KERNEL_STEP_IDS.factor,
        card: 'Calcul littéral',
        label: 'Factoriser',
        hora: 2,
        why: formFactorWhy(f.pattern, valueSide(to.s, x)!),
      };
    if (!t.product && !f.product && to.h < from.h && t.pattern)
      return t.pattern === 'common'
        ? {
            id: KERNEL_STEP_IDS.factor,
            card: 'Calcul littéral',
            label: 'Faire apparaître le facteur commun',
            hora: 1,
            why: 'On écrit chaque terme comme un produit qui contient le même facteur : il ne reste plus qu’à le mettre en évidence.',
          }
        : {
            id: KERNEL_STEP_IDS.factor,
            card: 'Calcul littéral',
            label: 'Écrire les carrés',
            hora: 1,
            why:
              t.pattern === 'squares'
                ? 'On écrit chaque terme comme un carré : on reconnaît a² − b².'
                : 'On écrit les carrés et le double produit : on reconnaît a² + 2ab + b² (ou a² − 2ab + b²).',
          };
    // moteur v41 : un facteur qui se factorise encore (« (x − 1)(x² − 5x + 6) » →
    // « (x − 1)(x − 2)(x − 3) ») : factoriser ce facteur
    if (t.product && f.product && f.h > 3 && t.h < f.h)
      return {
        id: KERNEL_STEP_IDS.factor,
        card: 'Calcul littéral',
        label: 'Factoriser',
        hora: 2,
        why: 'Un facteur se factorise encore : on le factorise à son tour.',
      };
    if (t.nested < f.nested && to.expand >= from.expand) return parens;
    return null;
  }
  if (to.expand < from.expand) return null;
  // moteur v41 : une multiplication de sommes faite, d'autres restent (« (x − 1)(x + 1)(x − 2) »
  // → « (x² − 1)(x − 2) ») : c'est un développement
  if (t.h - t.nested < f.h - f.nested && t.nested <= f.nested)
    return {
      id: KERNEL_STEP_IDS.expand,
      card: 'Calcul littéral',
      label: 'Développer',
      hora: 2,
      why: 'On développe un produit à la fois : la ligne ne change pas, elle s’écrit autrement.',
    };
  if (t.nested < f.nested) return parens;
  // moteur v41 : « (3h + 3h² + h³) ÷ h » → « 3 + 3h + h² »
  const fv = valueSide(from.s, x);
  const tv = valueSide(to.s, x);
  if (t.pow < f.pow && fv?.k === 'div' && fv.a.k === 'add' && tv && tv.k !== 'div')
    return {
      id: KERNEL_STEP_IDS.compute,
      card: 'Calcul littéral',
      label: 'Simplifier la fraction',
      hora: 1,
      why: 'Chaque terme du numérateur contient le dénominateur (non nul) : on divise chaque terme par lui.',
    };
  // les règles des puissances seules (« a⁴ × a » → « a⁵ ») ; « 2x × 3x » → « 6x² » est un calcul
  if (t.pow < f.pow && to.calc - t.pow >= from.calc - f.pow)
    return {
      id: KERNEL_STEP_IDS.compute,
      card: 'Puissances',
      label: 'Règles des puissances',
      hora: 1,
      why: 'aⁿ × aᵐ = aⁿ⁺ᵐ, aⁿ ÷ aᵐ = aⁿ⁻ᵐ, (aⁿ)ᵐ = aⁿˣᵐ, (ab)ⁿ = aⁿbⁿ.',
    };
  return null;
}
/**
 * « x = constante » des deux côtés, même valeur (moteur v20) : 6 ÷ √3 et 2√3 n'ont pas la
 * même forme réduite (la racine reste au dénominateur), mais leur différence est nulle.
 */
function sameConstValue(a: Statement, b: Statement, x: string): boolean {
  const valueOf = (s: Statement): Term | null => {
    if (s.k !== 'rel' || s.rel !== '=') return null;
    if (s.l.k === 'sym' && s.l.n === x && isConst(s.r)) return s.r;
    return null;
  };
  const va = valueOf(a);
  const vb = valueOf(b);
  if (!va || !vb) return false;
  const d = normalForm(sub(va, vb));
  return d.ok && polyIsZero(d.rf.n);
}

/**
 * Le plus petit (ou le plus grand) entier (moteur v27) : de « n > c » (c une constante,
 * « ln(0,1) ÷ ln(0,5) ») à « n ≥ 4 », l'inconnue étant un entier. L'équivalence est
 * déjà prouvée par le noyau (hypothèse « entier naturel ») ; on nomme le pas.
 */
/* ---- seuil par tests de valeurs (moteur v48, méthode « test ») ---- */

/** une valeur : exacte (rationnelle) si possible, sinon en flottant */
interface TestValue {
  exact: Q.Rat | null;
  f: number;
}
function testValue(t: Term): TestValue | null {
  const r = reduced(t);
  if (r && r.k === 'num') return { exact: r.v, f: Q.ratToNumber(r.v) };
  const f = evalFloat(t, {});
  return Number.isFinite(f) ? { exact: null, f } : null;
}
/** a comparé à b : −1, 0, 1 (exact si les deux le sont, sinon à 10⁻¹² près) */
function cmpValues(a: TestValue, b: TestValue): -1 | 0 | 1 {
  if (a.exact && b.exact) return Q.cmp(a.exact, b.exact);
  const d = a.f - b.f;
  if (Math.abs(d) <= 1e-12 * Math.max(1, Math.abs(a.f), Math.abs(b.f))) return 0;
  return d < 0 ? -1 : 1;
}
const relHolds = (c: -1 | 0 | 1, rel: Rel): boolean =>
  rel === '<' ? c < 0 : rel === '≤' ? c <= 0 : rel === '>' ? c > 0 : rel === '≥' ? c >= 0 : c === 0;
/** les inégalités de la feuille « g ⋈ c » (g en n, c constante) : le départ et les lignes sûres */
function testSides(ks: KernelSheetState): { g: Term; c: Term; rel: Rel }[] {
  const out: { g: Term; c: Term; rel: Rel }[] = [];
  const x = ks.x;
  for (const l of [ks.start, ...ks.lines.filter((l) => l.trusted)]) {
    if (l.s.k !== 'rel' || l.s.rel === '=') continue;
    const { l: a, r: b, rel } = l.s;
    if (has(a, x) && !has(b, x)) out.push({ g: a, c: b, rel });
    else if (has(b, x) && !has(a, x)) out.push({ g: b, c: a, rel: FLIP[rel] });
  }
  return out;
}
/** l'inégalité de départ est-elle vérifiée au rang k ? (null : pas calculable) */
function holdsAt(ks: KernelSheetState, k: number): boolean | null {
  const side = testSides(ks)[0];
  if (!side) return null;
  const v = testValue(substitute(side.g, { [ks.x]: { k: 'num', v: Q.rat(k) } }));
  const c = testValue(side.c);
  return v && c ? relHolds(cmpValues(v, c), side.rel) : null;
}
/** entiers écrits dans un terme (les rangs possibles d'un test) */
function intLiterals(t: Term, out: number[] = []): number[] {
  if (t.k === 'num') {
    if (t.v.d === 1n && t.v.n >= 0n && t.v.n <= 100000n) out.push(Number(t.v.n));
    return out;
  }
  switch (t.k) {
    case 'add':
    case 'mul':
      t.c.forEach((c) => intLiterals(c, out));
      return out;
    case 'div':
    case 'pow':
    case 'binom':
      intLiterals(t.a, out);
      intLiterals(t.b, out);
      return out;
    case 'fn':
      return intLiterals(t.a, out);
    default:
      return out;
  }
}
/** sens du seuil : la réponse « n ≥ N » (vrai pour les grands rangs) ou « n ≤ N » */
function testDirection(ks: KernelSheetState): { up: boolean; n: number } | null {
  const b = integerBound(ks.answer, ks.x);
  if (!b) return null;
  const up = b.rel === '>' || b.rel === '≥';
  const n = Number(b.v) + (b.rel === '>' ? 1 : b.rel === '<' ? -1 : 0);
  return { up, n };
}
/** nombre de décimales d'un rationnel décimal écrit (null : pas décimal) */
function decimalsOf(q: Q.Rat): number | null {
  let d = q.d;
  let p = 0;
  while (p <= 9) {
    if (d === 1n) return p;
    if (d % 10n === 0n) d /= 10n;
    else if (d % 2n === 0n) d /= 2n;
    else if (d % 5n === 0n) d /= 5n;
    else return null;
    if (d === 1n) return p + 1;
    p += 1;
  }
  return null;
}

/**
 * Moteur v48 : une ligne « 1000 × 0,8^11 ≈ 85,9 » (ou « 2^10 = 1024 ») teste un rang : le
 * membre de gauche est l'inégalité de la feuille au rang 11, le membre de droite sa valeur
 * (arrondie juste, assez précise pour comparer). Un test qui resserre l'encadrement du
 * seuil est un pas (1 HORA).
 */
function testVerdict(ks: KernelSheetState, E: Line, A: string[]): KernelVerdict | null {
  const x = ks.x;
  const ap = approximationOf(E);
  let L: Term;
  let R: Term;
  let approx = false;
  if (ap) {
    L = ap.l;
    R = ap.r;
    approx = true;
  } else {
    const st = statementOfLine(E);
    if (!st.ok || st.s.k !== 'rel' || st.s.rel !== '=') return null;
    L = st.s.l;
    R = st.s.r;
  }
  if (has(L, x) || has(R, x)) return null;
  const vL = testValue(L);
  if (!vL) return null;
  // le rang testé : un entier écrit à gauche, pour lequel une inégalité de la feuille vaut L
  let found: { k: number; side: { g: Term; c: Term; rel: Rel } } | null = null;
  for (const side of testSides(ks)) {
    for (const k of intLiterals(L)) {
      const v = testValue(substitute(side.g, { [x]: { k: 'num', v: Q.rat(k) } }));
      if (v && cmpValues(v, vL) === 0) {
        found = { k, side };
        break;
      }
    }
    if (found) break;
  }
  if (!found)
    return {
      kind: 'unjustified',
      atoms: A,
      line: E,
      title: 'Teste un rang',
      message: `Remplace ${x} par un entier dans l’inégalité (« ${x} = 10 »), puis écris la valeur obtenue.`,
    };
  const { k, side } = found;
  const rv = testValue(R);
  const rNum = canon(R);
  if (!rv || rNum.k !== 'num')
    return {
      kind: 'unjustified',
      atoms: A,
      line: E,
      title: 'Écris la valeur',
      message: 'À droite, écris la valeur obtenue (un nombre, arrondi si besoin).',
    };
  if (approx) {
    const p = decimalsOf(rNum.v);
    const err = Math.abs(vL.f - rv.f);
    const half = p === null ? 0 : 0.5 * 10 ** -p;
    if (p === null || err > half * (1 + 1e-9) + 1e-12) {
      const truncated = p !== null && err < 2 * half && Math.abs(vL.f) > Math.abs(rv.f);
      return {
        kind: 'false',
        atoms: A,
        line: E,
        title: truncated ? 'Arrondir, ce n’est pas couper' : 'Valeur approchée fausse',
        message: truncated
          ? 'Regarde le chiffre suivant : s’il vaut 5 ou plus, le dernier chiffre gardé augmente de 1.'
          : `Recalcule la valeur pour ${x} = ${k}.`,
      };
    }
  } else if (cmpValues(vL, rv) !== 0)
    return {
      kind: 'false',
      atoms: A,
      line: E,
      title: vL.exact ? 'Calcul faux' : 'Écris « ≈ »',
      message: vL.exact
        ? `Recalcule la valeur pour ${x} = ${k}.`
        : 'Cette valeur n’est pas exacte : écris « ≈ » et arrondis.',
    };
  const c = testValue(side.c);
  if (!c) return null;
  const holds = relHolds(cmpValues(vL, c), side.rel);
  // la valeur écrite doit suffire à comparer au seuil
  if (relHolds(cmpValues(rv, c), side.rel) !== holds || (approx && cmpValues(rv, c) === 0))
    return {
      kind: 'useless',
      atoms: A,
      line: E,
      title: 'Pas assez précis pour comparer',
      message:
        'Ta valeur est juste, mais trop arrondie pour savoir de quel côté du seuil elle est : garde plus de chiffres.',
    };
  if (ks.tests.has(k))
    return {
      kind: 'dup',
      atoms: A,
      line: E,
      title: 'Déjà testé',
      message: `Tu as déjà testé ${x} = ${k}.`,
    };
  const dir = testDirection(ks);
  const tested = [...ks.tests.entries()];
  // l'encadrement : le plus grand rang qui ne convient pas, le plus petit qui convient (et
  // l'inverse pour « n ≤ N »)
  const tightens = !dir
    ? true
    : dir.up
      ? holds
        ? tested.every(([j, h]) => !h || j > k)
        : tested.every(([j, h]) => h || j < k)
      : holds
        ? tested.every(([j, h]) => !h || j < k)
        : tested.every(([j, h]) => h || j > k);
  const shown = approx ? readableText(termText(withDecimals(rNum))) : readableText(termText(rNum));
  const bound = readableText(termText(withDecimals(canon(side.c))));
  const info: StepInfo = {
    id: KERNEL_STEP_IDS.testValue,
    card: 'Seuils',
    label: `Tester ${x} = ${k}`,
    hora: 1,
    why: holds
      ? `Pour ${x} = ${k}, on trouve ${shown} : l’inégalité est vérifiée (seuil ${bound}).`
      : `Pour ${x} = ${k}, on trouve ${shown} : l’inégalité n’est pas vérifiée (seuil ${bound}).`,
  };
  if (!tightens)
    return {
      kind: 'useless',
      atoms: A,
      line: E,
      title: 'Juste, mais l’encadrement ne se resserre pas',
      message: `Tu as déjà un rang plus proche du seuil. Teste un rang entre ceux que tu as écrits.`,
      test: { k, holds },
    };
  return { kind: 'step', atoms: A, line: E, hora: 1, final: false, rule: info, test: { k, holds } };
}

/**
 * Moteur v48 : « n ≥ 11 » conclut un seuil par tests quand la feuille a montré que le rang
 * 10 ne convient pas et que le rang 11 convient (la suite est monotone : la porte vérifie
 * que le seuil est le bon pour tous les rangs).
 */
function testConclusion(
  ks: KernelSheetState,
  b: { rel: Rel; v: bigint },
  T: KLine,
  E: Line,
  A: string[],
): KernelVerdict {
  const x = ks.x;
  const dir = testDirection(ks);
  const up = b.rel === '>' || b.rel === '≥';
  const n = Number(b.v) + (b.rel === '>' ? 1 : b.rel === '<' ? -1 : 0);
  const lo = 0; // n entier naturel
  // la borne dit-elle la même chose que l'inégalité de départ, rang par rang ?
  const top = Math.max(n, dir?.n ?? n) + 40;
  for (let k = lo; k <= top; k++) {
    const h = holdsAt(ks, k);
    if (h === null) break;
    if (h !== (up ? k >= n : k <= n))
      return {
        kind: 'false',
        atoms: A,
        line: E,
        title: 'Ce n’est pas le bon seuil',
        message:
          'Ta borne ne dit pas la même chose que l’inégalité de départ : teste les rangs autour d’elle.',
      };
  }
  const inside = ks.tests.get(n);
  const outside = up ? ks.tests.get(n - 1) : ks.tests.get(n + 1);
  const edge = up ? n - 1 < lo : false;
  if (inside !== true || (outside !== false && !edge))
    return {
      kind: 'unjustified',
      atoms: A,
      line: E,
      title: 'Juste, mais montre-le',
      message: `Ta borne est juste : montre-la par les tests, un rang qui ne convient pas et le suivant qui convient.`,
    };
  const other = up ? n - 1 : n + 1;
  return {
    kind: 'step',
    atoms: A,
    line: E,
    hora: 2,
    final: true,
    rule: {
      id: KERNEL_STEP_IDS.integerBound,
      card: 'Seuils',
      label: up ? 'Le plus petit entier' : 'Le plus grand entier',
      hora: 2,
      why: edge
        ? `${x} = ${n} convient déjà : c’est le premier rang.`
        : `${x} = ${other} ne convient pas, ${x} = ${n} convient : la suite étant monotone, c’est le ${up ? 'premier' : 'dernier'} rang.`,
    },
    kern: { s: T.s, trusted: true },
  };
}

function integerBoundStep(ks: KernelSheetState, from: KLine, to: KLine): StepInfo | null {
  const b = integerBound(to.s, ks.x);
  if (!b || from.s.k !== 'rel' || from.s.rel === '=') return null;
  const f = from.s;
  const c =
    f.l.k === 'sym' && f.l.n === ks.x && isConst(f.r)
      ? { rel: f.rel, t: f.r }
      : f.r.k === 'sym' && f.r.n === ks.x && isConst(f.l)
        ? { rel: FLIP[f.rel], t: f.l }
        : null;
  if (!c) return null;
  const v = evalFloat(c.t, {});
  if (!Number.isFinite(v)) return null;
  const up = b.rel === '>' || b.rel === '≥';
  const approx = formatNumber(Math.trunc(v * 100) / 100);
  const exact = Number.isInteger(v) ? formatNumber(v) : `${approx}…`;
  const bound = formatNumber(Number(b.v));
  return {
    id: KERNEL_STEP_IDS.integerBound,
    card: 'Seuils',
    label: up ? 'Le plus petit entier' : 'Le plus grand entier',
    hora: 2,
    why: `${ks.x} est un entier : ${ks.x} ${c.rel} ${exact} équivaut à ${ks.x} ${b.rel} ${bound}.`,
  };
}

/**
 * Un calcul sur les racines à la fois (moteur v20) : simplifier une racine, remplacer
 * cos 30° par sa valeur, rendre un dénominateur rationnel, réduire… Deux de ces calculs
 * dans la même ligne, ou une racine qui quitte le dénominateur sans que l'on voie
 * comment, sont « trop d'un coup ». Les autres calculs gardent leur règle d'avant.
 */
function radicalJump(from: KLine, to: KLine): { title: string; message: string } | null {
  const kinds = RAD_KINDS.filter((k) => to.rad[k] < from.rad[k]);
  if (!kinds.length) return null;
  const others =
    (to.calc - radWeight(to.rad) < from.calc - radWeight(from.rad) ? 1 : 0) +
    (to.expand < from.expand ? 1 : 0) +
    // √12 + √27 = 5√3 : les racines simplifiées ET réduites dans la même ligne
    (kinds.includes('sqrt') && !kinds.includes('like') && radTerms(to.s) < radTerms(from.s)
      ? 1
      : 0);
  if (kinds.length + others > 1)
    return {
      title: 'Un calcul à la fois',
      message:
        'Ta ligne est juste, mais elle fait plusieurs calculs d’un coup (simplifier une racine, remplacer par une valeur exacte, réduire…). Écris-les un par un.',
    };
  if ((kinds.includes('radDen') || kinds.includes('conj')) && !hasConstDiv(to.s))
    return {
      title: 'Montre comment',
      message:
        'Ta ligne est juste, mais on ne voit pas comment la racine quitte le dénominateur : écris d’abord la fraction multipliée en haut et en bas.',
    };
  return null;
}

/** Termes irrationnels d'une somme de constantes (« √12 + √27 » : 2 ; « 5√3 » : 1). */
function radTerms(s: Statement): number {
  const walk = (t: Term): number => {
    switch (t.k) {
      case 'add':
        return t.c.reduce((n, c) => n + (isConst(c) && !isNumber(c) ? 1 : 0) + walk(c), 0);
      case 'mul':
        return t.c.reduce((n, c) => n + walk(c), 0);
      case 'div':
      case 'pow':
      case 'binom':
        return walk(t.a) + walk(t.b);
      case 'fn':
        return walk(t.a);
      default:
        return 0;
    }
  };
  return relationsOf(s).reduce((n, r) => n + walk(r.l) + walk(r.r), 0);
}

/* ---- réponse arrondie (moteur v20) ---- */

/** La valeur exacte de la réponse (« AC = 6 ÷ cos(40°) ») ; null si elle n'est pas un nombre. */
function answerValue(ks: KernelSheetState): Term | null {
  if (ks.answer.k !== 'rel' || ks.answer.rel !== '=') return null;
  const { l, r } = ks.answer;
  if (l.k === 'sym' && l.n === ks.x && isConst(r)) return r;
  if (r.k === 'sym' && r.n === ks.x && isConst(l)) return l;
  return null;
}
/** Chiffres après la virgule d'un nombre écrit (« 7,07 » → 2) ; null s'il n'est pas décimal. */
function decimalPlacesOf(v: Term): number | null {
  const c = canon(v);
  if (c.k !== 'num') return null;
  let q = c.v;
  for (let n = 0; n <= 12; n++) {
    if (q.d === 1n) return n;
    q = Q.mul(q, Q.rat(10));
  }
  return null;
}
/** Reste-t-il à arrondir ? (pas si la valeur exacte a déjà assez peu de chiffres) */
function roundingNeeded(ks: KernelSheetState): boolean {
  if (!ks.rounding || ks.rounded !== null) return false;
  const v = answerValue(ks);
  if (!v) return true;
  const p = decimalPlacesOf(v);
  return p === null || p > ks.rounding.places;
}
function roundingWords(p: number): string {
  return p === 0
    ? 'à l’unité'
    : p === 1
      ? 'au dixième'
      : p === 2
        ? 'au centième'
        : p === 3
          ? 'au millième'
          : `à ${p} chiffres après la virgule`;
}
/** « v » est-il l'arrondi demandé de la valeur exacte ? */
function isRequestedRounding(ks: KernelSheetState, exact: Term, v: Term): boolean {
  if (!ks.rounding) return false;
  const p = decimalPlacesOf(v);
  if (p === null || p > ks.rounding.places) return false;
  const c = checkApproximation(null, exact, v, {
    rules: ks.rules,
    hypotheses: ks.hyps,
    approximation: { places: ks.rounding.places },
  });
  return c.status === 'valid';
}

function invalidVerdict(
  ks: KernelSheetState,
  c: StepCheck,
  T: KLine,
  E: Line,
  A: string[],
): KernelVerdict {
  // le sens de l'inégalité : la même ligne dans l'autre sens serait-elle juste ?
  if (T.s.k === 'rel' && T.s.rel !== '=') {
    const flipped: Statement = { ...T.s, rel: FLIP[T.s.rel] };
    const fl = klineOf(ks, flipped, true);
    for (const from of sources(ks)) {
      const f = check(ks, from, fl.s, fl.raw);
      if (f.status !== 'valid' || f.relation !== 'equivalent') continue;
      const rid = f.ruleId ?? '';
      if (/_negative@/.test(rid))
        return {
          kind: 'false',
          atoms: A,
          line: E,
          title: 'Le sens change !',
          message: 'Tu divises (ou multiplies) par un nombre négatif : l’inégalité change de sens.',
        };
      if (/^explog\./.test(rid))
        return {
          kind: 'false',
          atoms: A,
          line: E,
          title: 'Le sens ne change pas',
          message:
            'exp et ln sont strictement croissantes : les appliquer aux deux membres garde le sens de l’inégalité.',
        };
      // moteur v41 : x³ ≤ 8 → x ≥ 2 (la fonction cube est croissante)
      if (ks.poly && from.s.k === 'rel' && relForm(from.s, ks.x) === 'cube')
        return {
          kind: 'false',
          atoms: A,
          line: E,
          title: 'Le sens ne change pas',
          message:
            'La fonction cube est strictement croissante : A³ ≤ c équivaut à A ≤ b, où b³ = c. Le sens de l’inégalité reste le même.',
        };
      if (/_positive@|add_both_sides@/.test(rid))
        return {
          kind: 'false',
          atoms: A,
          line: E,
          title: 'Le sens ne change pas',
          message: /_positive@/.test(rid)
            ? 'Diviser (ou multiplier) par un nombre positif garde le sens de l’inégalité.'
            : 'Ajouter ou retrancher un même terme garde le sens de l’inégalité.',
        };
      return {
        kind: 'false',
        atoms: A,
        line: E,
        title: 'Dans le mauvais sens',
        message:
          'La même ligne avec l’autre signe serait juste : le sens change quand on multiplie ou divise par un nombre négatif, et seulement là.',
      };
    }
  }
  // strict ou large : « x < −3 » au lieu de « x ≤ −3 »
  if (T.s.k === 'rel' && T.s.rel !== '=') {
    const toggled: Statement = { ...T.s, rel: STRICT_TOGGLE[T.s.rel] };
    const tl = klineOf(ks, toggled, true);
    const at = c.explanation?.params?.at;
    for (const from of sources(ks)) {
      const f = check(ks, from, tl.s, tl.raw);
      if (f.status !== 'valid' || f.relation !== 'equivalent') continue;
      return {
        kind: 'false',
        atoms: A,
        line: E,
        title: 'Strict ou large ?',
        message:
          (at ? `Avec ${at}, ta ligne précédente est vraie mais pas celle-ci. ` : '') +
          '« ≤ » et « ≥ » gardent la borne, « < » et « > » l’enlèvent : une inégalité stricte reste stricte, une large reste large.',
      };
    }
  }
  // un ensemble (moteur v23) : une borne gardée au lieu d'exclue, ou l'inverse
  if (T.s.k === 'set') {
    const clauses = T.s.clauses;
    const all = clauses.flatMap((cl, i) => cl.map((_, j) => [i, j] as const));
    // une borne à la fois, puis toutes ensemble (« −π ÷ 3 < x < π ÷ 3 » au lieu de ≤)
    const tries: (readonly [number, number] | null)[] = [...all.slice(0, 6), null];
    for (const at of tries) {
      if (at === null && all.length < 2) continue;
      const toggled: Statement = {
        k: 'set',
        clauses: clauses.map((cl, a) =>
          cl.map((r, b) =>
            at === null || (a === at[0] && b === at[1]) ? { ...r, rel: STRICT_TOGGLE[r.rel] } : r,
          ),
        ),
      };
      const tl = klineOf(ks, toggled, true);
      const ok = sources(ks).some((from) => {
        const f = check(ks, from, tl.s, tl.raw);
        return f.status === 'valid' && f.relation === 'equivalent';
      });
      if (ok)
        return {
          kind: 'false',
          atoms: A,
          line: E,
          title: 'Strict ou large ?',
          message:
            'Regarde chaque borne : elle est dans l’ensemble si la ligne précédente y est vraie (≤, ≥), exclue sinon (<, >). Une valeur interdite est toujours exclue.',
        };
    }
  }
  const p = { ...(c.explanation?.params ?? {}) };
  // un contre-exemple qui est LA réponse (« N = 1906884 vérifie ta ligne précédente ») la
  // donnerait sans calcul : on le tait (lot 5.6)
  if (typeof p.at === 'string' && revealsAnswer(ks, p.at)) delete p.at;
  const code = c.explanation?.code;
  if (
    ks.spec.goal.kind === 'simplify' &&
    !p.at &&
    (code === 'lost_solution' || code === 'counterexample')
  )
    return {
      kind: 'false',
      atoms: A,
      line: E,
      title: 'Ce n’est pas la même valeur',
      message: 'Ta ligne ne vaut pas la précédente : reprends le calcul.',
    };
  switch (code) {
    case 'counterexample':
      return {
        kind: 'false',
        atoms: A,
        line: E,
        title: 'Cette ligne ne découle pas de la précédente',
        message: p.at
          ? `Avec ${p.at}, ta ligne précédente est vraie mais pas celle-ci.`
          : 'Elle ne garde pas les mêmes solutions.',
      };
    case 'lost_solution':
      return {
        kind: 'false',
        atoms: A,
        line: E,
        title: 'Une solution se perd',
        message:
          (p.at
            ? `${p.at} vérifie ta ligne précédente, mais pas celle-ci.`
            : 'Des solutions de ta ligne précédente ne vérifient plus celle-ci.') +
          (ks.trig && !ks.angles ? trigLostTip(ks, T) : ''),
      };
    case 'division_by_zero':
      return {
        kind: 'false',
        atoms: A,
        line: E,
        title: 'Division par zéro',
        message: 'On ne divise jamais par zéro.',
      };
    case 'false_statement':
      return {
        kind: 'false',
        atoms: A,
        line: E,
        title: 'Cette ligne est fausse',
        message: 'Vérifie le calcul.',
      };
    default:
      return {
        kind: 'false',
        atoms: A,
        line: E,
        title: 'Cette ligne ne découle pas de la précédente',
        message: 'Elle ne garde pas les mêmes solutions : reprends ta dernière ligne.',
      };
  }
}
/** « x = v » est-il une valeur de la réponse (égalité, « ou », objectif simplify) ? */
function revealsAnswer(ks: KernelSheetState, at: string): boolean {
  if (at.includes(',')) return false;
  const p = parseStatement(at);
  if (!p.ok) return false;
  const v = relationsIn(p.s)[0];
  if (!v || v.rel !== '=' || v.l.k !== 'sym' || v.l.n !== ks.x) return false;
  return relationsIn(ks.answer).some(
    (r) => r.rel === '=' && r.l.k === 'sym' && r.l.n === ks.x && compareTerms(r.r, v.r) === 'equal',
  );
}

function conditionalVerdict(c: StepCheck, E: Line, A: string[]): KernelVerdict {
  if (c.branches?.length) {
    const of = c.explanation?.params?.of ?? 'ce nombre';
    return {
      kind: 'unjustified',
      atoms: A,
      line: E,
      title: `Ça dépend du signe de ${of}`,
      message: `Le sens de l’inégalité dépend du signe de ${of} : regarde ce que l’énoncé en dit avant de diviser ou de multiplier.`,
    };
  }
  const conds = (c.conditions ?? []).map(conditionText);
  const what = conds.length ? conds.join(' et ') : 'une condition';
  return {
    kind: 'unjustified',
    atoms: A,
    line: E,
    title: `Juste seulement si ${what}`,
    message: `Cette étape n’est juste que si ${what}, et l’énoncé ne le garantit pas.`,
  };
}

/* ------------------------------ second degré (moteur v23) ------------------------------ */

const POLY_CARD = 'Second degré';
const clausesOf = (s: Statement): Relation[][] =>
  s.k === 'set'
    ? s.clauses
    : s.k === 'or'
      ? s.items.map((i) => [i])
      : s.k === 'rel'
        ? [[{ rel: s.rel, l: s.l, r: s.r }]]
        : [];
/** Chaque relation du premier degré, ou déjà résolue (clauses d'un produit nul…). */
function linearClauses(s: Statement, x: string): boolean {
  const cs = clausesOf(s);
  return (
    cs.length > 0 &&
    cs.every((c) => c.every((r) => ['linear', 'solved'].includes(relForm(r, x, true))))
  );
}
/** Le membre non nul d'une relation (« … ≥ 0 ») ; null si les deux le sont. */
function nonZeroSide(r: Relation): Term | null {
  if (isZeroTerm(r.r)) return r.l;
  if (isZeroTerm(r.l)) return r.r;
  return null;
}
const fmt = (t: Term): string => termText(withDecimals(canon(t)));
const ratText = (q: Q.Rat): string => fmt({ k: 'num', v: q });

interface PolyStepResult {
  info?: StepInfo;
  reject?: { title: string; message: string };
}

/** Les trinômes de la feuille (départ et lignes de confiance), le plus récent d'abord. */
function sheetTrinomials(ks: KernelSheetState): { r: Relation; t: Trinomial }[] {
  const out: { r: Relation; t: Trinomial }[] = [];
  for (const l of sources(ks)) {
    if (l.s.k !== 'rel') continue;
    const t = trinomialOf(l.s, ks.x);
    if (t) out.push({ r: l.s, t });
  }
  return out;
}
/** v est-il une racine du trinôme ? (exact, racines carrées comprises) */
function isRootOf(t: Trinomial, v: Term): boolean {
  const e = normalForm({
    k: 'add',
    c: [
      {
        k: 'mul',
        c: [
          { k: 'num', v: t.a },
          { k: 'pow', a: v, b: { k: 'num', v: Q.rat(2) } },
        ],
      },
      { k: 'mul', c: [{ k: 'num', v: t.b }, v] },
      { k: 'num', v: t.c },
    ],
  });
  return e.ok && polyIsZero(e.rf.n);
}
const sameValue = (a: Term, b: Term): boolean => {
  const d = normalForm(sub(a, b));
  return d.ok && polyIsZero(d.rf.n);
};
/** Les racines écrites (x₁, x₂, x₀) couvrent-elles toutes celles du trinôme ? */
function rootsKnown(ks: KernelSheetState, t: Trinomial): boolean {
  const written = [...ks.aux.values()].filter((a) => auxKind(a.name, ks.x) === 'root');
  const ok = written.filter((a) => isRootOf(t, a.value));
  const distinct: Term[] = [];
  ok.forEach((a) => {
    if (!distinct.some((d) => sameValue(d, a.value))) distinct.push(a.value);
  });
  const sign = Q.sign(t.disc);
  if (sign < 0) {
    const d = ks.aux.get('Δ');
    return !!d && sameValue(d.value, { k: 'num', v: t.disc });
  }
  return distinct.length === (sign === 0 ? 1 : 2);
}

/** Factoriser : quelle factorisation ? (le pourquoi, pour l'élève) */
function factorWhy(ks: KernelSheetState, f: Relation, t: Relation): string {
  const fz = nonZeroSide(f) ?? sub(f.l, f.r);
  const tz = nonZeroSide(t) ?? sub(t.l, t.r);
  if (squareBase(tz, ks.x))
    return 'Identité remarquable : a² − 2ab + b² = (a − b)², a² + 2ab + b² = (a + b)².';
  if (relForm(f, ks.x) === 'canonical') return 'Identité remarquable : a² − b² = (a − b)(a + b).';
  const tri = trinomialOf(f, ks.x);
  if (tri && Q.isZero(tri.c)) return 'On met x en facteur : ax² + bx = x(ax + b).';
  if (tri && rootsKnown(ks, tri))
    return 'Avec les racines x₁ et x₂ : ax² + bx + c = a(x − x₁)(x − x₂).';
  if (fz.k === 'add') return 'On factorise : en développant, on retrouve la ligne précédente.';
  return 'On factorise : en développant, on retrouve la ligne précédente.';
}

/** Une relation vérifiée par le noyau, pour une clause d'un ensemble. */
function relCheck(ks: KernelSheetState, a: Relation, b: Relation): StepCheck {
  const from: Statement = { k: 'rel', ...a };
  const to: Statement = { k: 'rel', ...b };
  const key = `clause:${statementKey(from)}→${statementKey(to)}`;
  const hit = ks.cache.get(key);
  if (hit) return hit;
  const c = checkAlgebraStatements(from, to, {
    rules: ks.rules,
    hypotheses: ks.hyps,
    goal: ks.goal,
    deadline: Date.now() + 250,
    branches: false,
  });
  ks.cache.set(key, c);
  return c;
}
/** Un pas sur UNE relation d'une clause (règle des deux côtés, ou calcul). */
function relationStep(ks: KernelSheetState, a: Relation, b: Relation): StepInfo | null {
  const c = relCheck(ks, a, b);
  if (c.status !== 'valid' || (c.relation !== 'equivalent' && c.relation !== 'identity'))
    return null;
  const fa: Statement = { k: 'rel', ...a };
  const tb: Statement = { k: 'rel', ...b };
  if (c.proof === 'rule' && c.ruleId && !addsSeveralTerms(c)) return ruleStep(c, tb, fa);
  const F = kline(fa, ks.x, true);
  const T = kline(tb, ks.x, true);
  const same = (T.NK !== null && T.NK === F.NK) || sameConstValue(fa, tb, ks.x);
  return same ? computeStep(F, T, fa, tb) : null;
}
/** Une clause (une relation, ou une chaîne) transformée d'un pas. */
function clauseStepInfo(ks: KernelSheetState, f: Relation[], t: Relation[]): StepInfo | null {
  if (f.length !== t.length) return null;
  const steps = f.map((r, i) => relationStep(ks, r, t[i]!));
  if (steps.some((s) => !s)) return null;
  const first = steps[0]!;
  // une chaîne : la même opération sur chaque membre
  if (steps.some((s) => s!.label !== first.label)) return null;
  return steps.length > 1
    ? { ...first, why: `${first.why} On le fait sur chaque membre de la chaîne.` }
    : first;
}
const clauseKey = (c: Relation[]): string => keyOf({ k: 'set', clauses: [c] });
function permutations<T>(xs: T[]): T[][] {
  if (xs.length <= 1) return [xs];
  return xs.flatMap((x, i) =>
    permutations([...xs.slice(0, i), ...xs.slice(i + 1)]).map((p) => [x, ...p]),
  );
}
/** « x − 2 = 0 ou x − 3 = 0 » → « x = 2 ou x − 3 = 0 » : un pas sur une ou plusieurs clauses. */
function clauseStep(ks: KernelSheetState, F: KLine, T: KLine): PolyStepResult | null {
  const fc = clausesOf(F.s);
  const tc = clausesOf(T.s);
  if (fc.length !== tc.length || fc.length > 4) return null;
  const orders = fc.length <= 3 ? permutations(fc.map((_, i) => i)) : [fc.map((_, i) => i)];
  for (const order of orders) {
    const changed: { f: Relation[]; st: StepInfo }[] = [];
    let ok = true;
    for (let i = 0; i < tc.length && ok; i++) {
      const f = fc[order[i]!]!;
      const t = tc[i]!;
      if (clauseKey(f) === clauseKey(t)) continue;
      const st = clauseStepInfo(ks, f, t);
      if (!st) ok = false;
      else changed.push({ f, st });
    }
    if (!ok || !changed.length) continue;
    const text = (c: Relation[]): string => statementText({ k: 'set', clauses: [c] });
    if (changed.length === 1) {
      const { f, st } = changed[0]!;
      return { info: { ...st, why: `Dans « ${text(f)} » : ${lower(st.why)}` } };
    }
    const eq = changed.every((c) => c.f.every((r) => r.rel === '='));
    return {
      info: {
        id: POLY_STEP_IDS.clauses,
        card: eq ? 'Équations' : 'Inéquations',
        label: eq ? 'Résoudre chaque équation' : 'Résoudre chaque inégalité',
        hora: 2,
        why: changed.map((c) => `« ${text(c.f)} » : ${lower(c.st.label)}`).join(' ; ') + '.',
      },
    };
  }
  return null;
}

const CLEAR_DEN: StepInfo = {
  id: POLY_STEP_IDS.clearDenominator,
  card: 'Équations',
  label: 'Chasser le dénominateur',
  hora: 2,
  why: 'On multiplie les deux membres par le dénominateur, non nul pour les valeurs permises : plus de fraction, les solutions ne changent pas.',
};
const QUOTIENT_ZERO: StepInfo = {
  id: POLY_STEP_IDS.zeroProduct,
  card: 'Équations',
  label: 'Quotient nul',
  hora: 2,
  why: 'Un quotient est nul quand son numérateur est nul et son dénominateur ne l’est pas : on écarte la valeur interdite.',
};

/** Le produit nul écarte-t-il un facteur (moins de clauses que de facteurs en x) ? */
function dropsSome(ks: KernelSheetState, F: KLine, T: KLine): boolean {
  if (!hypothesesText(ks) || F.s.k !== 'rel') return false;
  const side = nonZeroSide({ rel: F.s.rel, l: F.s.l, r: F.s.r });
  if (!side) return false;
  const n = new Set(
    factorsOf(side)
      .filter((f) => has(f, ks.x))
      .map((f) => keyOf({ k: 'expr', e: f })),
  ).size;
  return clausesOf(T.s).length < n;
}

/** « −ℓ − 2 » → « ℓ + 2 » (une somme dont le premier terme est négatif) ; null sinon. */
function negatedSum(v: string): string | null {
  if (!v.startsWith('−')) return null;
  const p = parseStatement(v);
  if (!p.ok || p.s.k !== 'expr') return null;
  const n = normalForm({ k: 'mul', c: [{ k: 'num', v: Q.rat(-1) }, p.s.e] });
  if (!n.ok) return null;
  const t = termText(withDecimals(rfToTerm(rfSimplify(n.rf))));
  return t.startsWith('−') ? null : t;
}

/** Les conditions de l'énoncé, lisibles (« p < 0,5 », « ℓ > 0 ») ; '' sans hypothèse. */
function hypothesesText(ks: KernelSheetState): string {
  return (ks.spec.hypotheses ?? []).map((h) => conditionText(h)).join(', ');
}

/**
 * « p = 0,3 ou p = 0,7 » → « p = 0,3 » : on écarte une solution que la condition de
 * l'énoncé exclut (la ligne reste équivalente sous les hypothèses).
 */
function keepStep(ks: KernelSheetState, F: KLine, T: KLine): PolyStepResult | null {
  const fc = clausesOf(F.s);
  const tc = clausesOf(T.s);
  if (!tc.length || tc.length >= fc.length) return null;
  const keys = new Set(fc.map(clauseKey));
  if (!tc.every((c) => keys.has(clauseKey(c)))) return null;
  const kept = new Set(tc.map(clauseKey));
  const dropped = fc.filter((c) => !kept.has(clauseKey(c)));
  const text = (c: Relation[]): string => statementText({ k: 'set', clauses: [c] });
  const hyps = hypothesesText(ks);
  return {
    info: {
      id: POLY_STEP_IDS.keep,
      card: 'Équations',
      label: 'Garder ce qui convient',
      hora: 1,
      why:
        dropped.map((c) => `« ${text(c)} »`).join(' et ') +
        (hyps
          ? ` ne convient pas : d’après l’énoncé, ${hyps}.`
          : ' ne convient pas : on ne garde que les solutions permises.'),
    },
  };
}

/**
 * Les pas du second degré (moteur v23) : tout passer d'un côté, factoriser, forme
 * canonique, produit nul, racine carrée, tableau de signes, signe du trinôme, chasser
 * un dénominateur, un pas sur une clause. null : le juge habituel décide.
 */
/** moteur v47 : identifiants des pas de l'inconnue auxiliaire */
export const AUX_STEP_IDS = {
  pose: 'aux.pose@1',
  back: 'aux.back@1',
  compose: 'aux.compose@1',
  rewrite: 'aux.rewrite@1',
} as const;

/** le pas vérifié dans le monde de X (les libellés parlent de X, pas de eˣ) */
function auxCheck(ks: KernelSheetState, FA: KLine, TA: KLine): StepCheck {
  const key = `aux:${FA.raw}→${TA.raw}`;
  const hit = ks.cache.get(key);
  if (hit) return hit;
  const X = ks.auxiliary!.name;
  const c = checkAlgebraStatements(FA.s, TA.s, {
    rules: ks.rules,
    hypotheses: [],
    goal: { ...ks.goal, target: X } as Goal,
    deadline: Date.now() + 250,
    branches: true,
  });
  ks.cache.set(key, c);
  return c;
}

/** la même ligne, écrite une fois en X et une fois avec eˣ (ou ln(x)) */
function auxSwap(ks: KernelSheetState, F: KLine, T: KLine): boolean {
  const a = ks.auxiliary;
  if (!a || !!F.auxWritten === !!T.auxWritten) return false;
  const fA = auxStatement(F.s, a, ks.x);
  const tA = auxStatement(T.s, a, ks.x);
  if (!fA || !tA) return false;
  return (
    kline(fA, a.name, true, true, null, { v47: true }).K ===
    kline(tA, a.name, true, true, null, { v47: true }).K
  );
}

/**
 * Inconnue auxiliaire (moteur v47) : « On pose X = eˣ » (même ligne écrite en X), les pas
 * du cours en X (factoriser, produit nul, tableau de signes : polyStep sur les lignes
 * écrites en X), « Remplacer X par eˣ », puis revenir à x (composer par ln ou par exp).
 */
function auxStep(ks: KernelSheetState, F: KLine, c: StepCheck, T: KLine): PolyStepResult | null {
  const a = ks.auxiliary!;
  const X = a.name;
  const sup: Record<string, string> = { x: 'ˣ', t: 'ᵗ', n: 'ⁿ' };
  const atom = a.of === 'exp' ? (sup[ks.x] ? 'e' + sup[ks.x] : `e^${ks.x}`) : `ln(${ks.x})`;
  const fA = auxStatement(F.s, a, ks.x);
  const tA = auxStatement(T.s, a, ks.x);
  if (fA && tA) {
    const FA = kline(fA, X, true, true, null, { v47: true });
    const TA = kline(tA, X, true, true, null, { v47: true });
    const same = FA.K === TA.K;
    if (!same && !F.auxWritten && T.auxWritten)
      return {
        reject: {
          title: 'Une étape à la fois',
          message: `Pose d’abord ${X} = ${atom} : recopie ta ligne en écrivant ${X} à la place de ${atom}, puis transforme-la.`,
        },
      };
    if (!same && F.auxWritten && !T.auxWritten)
      return {
        reject: {
          title: 'Une étape à la fois',
          message: `Remplace d’abord ${X} par ${atom} dans ta ligne, puis transforme-la.`,
        },
      };
    if (same && !F.auxWritten && T.auxWritten)
      return {
        info: {
          id: AUX_STEP_IDS.pose,
          card: 'Équations',
          label: `Poser ${X} = ${atom}`,
          hora: 1,
          why: `On pose ${X} = ${atom} : la ligne devient une équation (ou une inéquation) en ${X}, qu’on sait résoudre.`,
        },
      };
    if (same && !F.auxWritten && !T.auxWritten && F.K !== T.K)
      return {
        info: {
          id: AUX_STEP_IDS.rewrite,
          card: 'Exponentielle et logarithme',
          label: `Écrire avec ${atom}`,
          hora: 1,
          why:
            a.of === 'exp'
              ? `e^(2${ks.x}) = (${atom})² : la ligne s’écrit avec ${atom} seulement, prête pour ${X}.`
              : `La ligne s’écrit avec ${atom} seulement, prête pour ${X}.`,
        },
      };
    if (same && F.auxWritten && !T.auxWritten)
      return {
        info: {
          id: AUX_STEP_IDS.back,
          card: 'Équations',
          label: `Remplacer ${X} par ${atom}`,
          hora: 1,
          why: `${X} = ${atom} : on revient à l’inconnue ${ks.x}.`,
        },
      };
    const cA = auxCheck(ks, FA, TA);
    const p = polyStep({ ...ks, x: X, auxiliary: null }, FA, cA, TA);
    if (p) return p;
    // une règle d'un paquet (ajouter, diviser…) : le libellé parle de X
    if (cA.proof === 'rule') {
      const info = ruleStep(cA, TA.s, FA.s);
      if (info) return { info };
    }
    return null;
  }
  // « eˣ = −1 ou eˣ = 2 » → « eˣ = 2 » : eˣ > 0, on garde ce qui convient
  if (fA && tA && !F.auxWritten && !T.auxWritten && a.of === 'exp') {
    const dropped = droppedClauses(fA, tA, X);
    if (dropped && dropped.length > 0 && dropped.every((d) => impossibleExpClause(d.r, X)))
      return {
        info: {
          id: POLY_STEP_IDS.keep,
          card: 'Exponentielle et logarithme',
          label: 'Garder ce qui convient',
          hora: 1,
          why: `${atom} > 0 : ${dropped
            .map((d) => `${atom} ${d.rel} ${readableText(termText({ k: 'num', v: d.c } as Term))}`)
            .join(' et ')} n’a pas de solution.`,
        },
      };
  }
  // revenir à x : chaque « eˣ = c » devient « x = ln(c) » (c > 0), « ln(x) = c » devient « x = eᶜ »
  if (fA && !tA && F.auxWritten && isSolvedSet(fA, X) && isSolvedSet(T.s, ks.x))
    return {
      reject: {
        title: 'Une étape à la fois',
        message: `Remplace d’abord ${X} par ${atom}, puis reviens à ${ks.x}.`,
      },
    };
  if (fA && !tA && isSolvedSet(fA, X) && isSolvedSet(T.s, ks.x))
    return {
      info: {
        id: AUX_STEP_IDS.compose,
        card: 'Exponentielle et logarithme',
        label: a.of === 'exp' ? 'Composer par ln' : 'Composer par exp',
        hora: 2,
        why:
          a.of === 'exp'
            ? `eˣ = c équivaut à x = ln(c) quand c > 0 ; eˣ = c n’a pas de solution quand c ≤ 0 (eˣ > 0). Pour une inégalité, ln est croissante : le sens ne change pas.`
            : `ln(x) = c équivaut à x = eᶜ ; exp est croissante : le sens d’une inégalité ne change pas.`,
      },
    };
  return null;
}

/** clauses « X ⋈ c » de f absentes de t (t ⊂ f) ; null si t n'en est pas une partie */
function droppedClauses(
  f: Statement,
  t: Statement,
  X: string,
): { rel: string; c: Q.Rat; r: Relation }[] | null {
  const vals = (s: Statement): { rel: string; c: Q.Rat; r: Relation }[] | null => {
    const out: { rel: string; c: Q.Rat; r: Relation }[] = [];
    for (const r of relationsOf(s)) {
      if (r.l.k !== 'sym' || r.l.n !== X) return null;
      const v = reduced(r.r);
      if (!v || v.k !== 'num') return null;
      out.push({ rel: r.rel, c: v.v, r });
    }
    return out;
  };
  const fv = vals(f);
  const tv = vals(t);
  if (!fv || !tv) return null;
  const same = (a: { rel: string; c: Q.Rat }, b: { rel: string; c: Q.Rat }) =>
    a.rel === b.rel && Q.eq(a.c, b.c);
  if (!tv.every((v) => fv.some((w) => same(v, w)))) return null;
  return fv.filter((v) => !tv.some((w) => same(v, w)));
}

/** un membre « somme ÷ d » devient « produit ÷ d » (même d), l'autre membre inchangé */
function factorsNumerator(f: Relation, t: Relation, x: string): boolean {
  const side = (a: Term, b: Term): 'same' | 'factored' | null => {
    if (termKey(canon(a)) === termKey(canon(b))) return 'same';
    if (a.k !== 'div' || b.k !== 'div') return null;
    if (termKey(canon(a.b)) !== termKey(canon(b.b))) return null;
    if (!has(a.a, x) || !has(a.b, x)) return null;
    if (a.a.k !== 'add' || b.a.k !== 'mul') return null;
    const fa = reduced(a.a);
    const fb = reduced(b.a);
    return fa && fb && termKey(fa) === termKey(fb) ? 'factored' : null;
  };
  const l = side(f.l, t.l);
  const r = side(f.r, t.r);
  return (l === 'factored' && r === 'same') || (l === 'same' && r === 'factored');
}

function polyStep(ks: KernelSheetState, F: KLine, c: StepCheck, T: KLine): PolyStepResult | null {
  const x = ks.x;
  const fRel: Relation | null = F.s.k === 'rel' ? { rel: F.s.rel, l: F.s.l, r: F.s.r } : null;
  const tRel: Relation | null = T.s.k === 'rel' ? { rel: T.s.rel, l: T.s.l, r: T.s.r } : null;
  if (!fRel) return keepStep(ks, F, T) ?? clauseStep(ks, F, T);
  const ff: PolyForm = relForm(fRel, x);
  const tf: PolyForm | null = tRel ? relForm(tRel, x) : null;
  const eq = fRel.rel === '=';
  const ineqCard = eq ? 'Équations' : 'Inéquations';
  const solved = solvedForm(T, x) || (T.s.k === 'or' && T.calc === 0 && T.h === 0);
  const linear = linearClauses(T.s, x);

  // tout passer du même côté (une somme retranchée d'un coup, pour avoir 0 d'un côté)
  if (c.proof === 'rule' && c.ruleId && addsSeveralTerms(c)) {
    if (tRel && nonZeroSide(tRel) && !nonZeroSide(fRel)) {
      const v = c.explanation?.params?.v_c ?? '';
      const opp = oppositeOf(v) ?? negatedSum(v);
      const verb = opp ? `retranche ${wrapTerm(opp)}` : `ajoute ${wrapTerm(v)}`;
      return {
        info: {
          id: POLY_STEP_IDS.allOnOneSide,
          card: ineqCard,
          label: 'Tout passer du même côté',
          hora: 2,
          why: `On ${verb} aux deux membres : l’un devient 0${eq ? '' : ', le sens ne change pas'}. On pourra factoriser.`,
        },
      };
    }
    // un seul terme écrit (« −(x + 2)² » qui change de membre), même s'il se développe
    const moved = movedTerm(F.s, c.explanation?.params?.v_c);
    if (moved)
      return {
        info: ruleStep(
          {
            ...c,
            explanation: {
              ...c.explanation!,
              params: { ...c.explanation!.params, v_c: moved },
            },
          },
          T.s,
        ),
      };
    return null;
  }
  const sameLine = T.NK !== null && T.NK === F.NK;
  // « (x − 2)(x + 2) ÷ (x − 2) = 0 » → « x + 2 = 0 » : simplifier la fraction, la valeur
  // interdite écartée (la forme réduite ne la voit plus)
  if (sameLine && eq && tRel && T.h < F.h && shapeHasDen(F.s, x) && !shapeHasDen(T.s, x))
    return {
      info:
        isZeroTerm(fRel.l) || isZeroTerm(fRel.r)
          ? QUOTIENT_ZERO
          : {
              id: POLY_STEP_IDS.clearDenominator,
              card: 'Équations',
              label: 'Simplifier la fraction',
              hora: 2,
              why: 'Le facteur commun ne s’annule pas pour les valeurs permises : on simplifie la fraction, en gardant la valeur interdite à l’écart.',
            },
    };
  // moteur v47 : « (x² − 1) ÷ (x + 2) » → « (x − 1)(x + 1) ÷ (x + 2) » : factoriser le
  // numérateur d'un quotient (avant : « Calculer »)
  if (ks.readable && sameLine && tRel && factorsNumerator(fRel, tRel, x))
    return {
      info: {
        id: POLY_STEP_IDS.factor,
        card: 'Calcul littéral',
        label: 'Factoriser le numérateur',
        hora: 2,
        why: 'On factorise le numérateur, le dénominateur ne change pas : chaque facteur aura sa ligne dans le tableau de signes.',
      },
    };
  if (sameLine && tRel && T.h < F.h) {
    // factoriser une SOMME (« x² + x − x » → « x² » est un calcul)
    const fz = nonZeroSide(fRel) ?? sub(fRel.l, fRel.r);
    const tz = nonZeroSide(tRel);
    const fr = reduced(fz);
    const isSum = !!fr && fr.k === 'add';
    // « 1 ÷ x − 2 » → « (1 − 2x) ÷ x » : une seule fraction, prête pour le tableau de signes
    if (tf === 'factored' && fz.k === 'add' && tz?.k === 'div' && shapeHasDen(F.s, x))
      return {
        info: {
          id: POLY_STEP_IDS.sameDenominator,
          card: 'Calcul littéral',
          label: 'Même dénominateur',
          hora: 2,
          why: 'On réduit au même dénominateur : une seule fraction, dont on étudie le signe du numérateur et du dénominateur.',
        },
      };
    if ((tf === 'factored' || tf === 'partial') && ff !== 'factored' && isSum)
      return {
        info: {
          id: POLY_STEP_IDS.factor,
          card: 'Calcul littéral',
          label: 'Factoriser',
          hora: 2,
          why: factorWhy(ks, fRel, tRel),
        },
      };
    if (tf === 'canonical' && (ff === 'expanded' || ff === 'unsorted'))
      return {
        info: {
          id: POLY_STEP_IDS.canonical,
          card: POLY_CARD,
          label: 'Forme canonique',
          hora: 2,
          why: 'On complète le carré : ax² + bx + c = a(x − α)² + β. Le carré se compare ensuite, ou se factorise avec a² − b².',
        },
      };
    return null;
  }
  if (sameLine) return null;

  // chasser le dénominateur (x au dénominateur d'une équation) ; face à 0 : quotient nul
  if (ff === 'rational' && tf && tf !== 'rational' && !shapeHasDen(T.s, x))
    return {
      info: isZeroTerm(fRel.l) || isZeroTerm(fRel.r) ? QUOTIENT_ZERO : CLEAR_DEN,
    };

  if (eq && T.s.k !== 'set' && linear) {
    // produit nul, quotient nul
    if (ff === 'factored') {
      const quotient = shapeHasDen(F.s, x);
      // un seul facteur (« x² = 0 », « (x − 3)² = 0 ») : une puissance nulle
      const side = nonZeroSide(fRel);
      const single =
        !quotient &&
        !!side &&
        new Set(
          factorsOf(side)
            .filter((f) => has(f, x))
            .map((f) => keyOf({ k: 'expr', e: f })),
        ).size === 1;
      return {
        info: {
          id: POLY_STEP_IDS.zeroProduct,
          card: 'Équations',
          label: quotient ? 'Quotient nul' : single ? 'Une puissance nulle' : 'Produit nul',
          hora: 2,
          why:
            (quotient
              ? 'Un quotient est nul quand son numérateur est nul et son dénominateur ne l’est pas : on garde les valeurs permises.'
              : single
                ? 'Une puissance est nulle seulement si le nombre l’est : A² = 0 équivaut à A = 0.'
                : 'Un produit est nul si et seulement si l’un de ses facteurs est nul : chaque facteur donne une équation.') +
            (dropsSome(ks, F, T)
              ? ` D’après l’énoncé, ${hypothesesText(ks)} : on écarte ce qui ne convient pas.`
              : ''),
        },
      };
    }
    // fonction cube (moteur v41) : une seule solution
    if (ff === 'cube') {
      const side = has(fRel.l, x) ? fRel.l : fRel.r;
      const base = cubeBase(side, x)!.base;
      return {
        info: {
          id: POLY_STEP_IDS.cubeRoot,
          card: 'Équations',
          label: 'Fonction cube : une solution',
          hora: 2,
          why: `La fonction cube est strictement croissante : ${fmt(base)}³ = c a une seule solution, le nombre dont le cube vaut c. Pas de solution négative en plus, comme pour un carré.`,
        },
      };
    }
    // racine carrée : deux solutions
    if (ff === 'square') {
      const side = has(fRel.l, x) ? fRel.l : fRel.r;
      const other = side === fRel.l ? fRel.r : fRel.l;
      const base = squareBase(side, x)!.base;
      const k = reduced(other);
      const zero = !!k && k.k === 'num' && Q.isZero(k.v);
      return {
        info: {
          id: POLY_STEP_IDS.squareRoot,
          card: 'Équations',
          label: zero ? 'Un carré nul' : 'Racine carrée : deux solutions',
          hora: 2,
          why: zero
            ? `${fmt(base)}² = 0 équivaut à ${fmt(base)} = 0.`
            : clausesOf(T.s).length === 1 && hypothesesText(ks)
              ? `${fmt(base)}² = c, avec c > 0 : ${fmt(base)} = √c ou ${fmt(base)} = −√c ; d’après l’énoncé, ${hypothesesText(ks)} : on garde une seule solution.`
              : `${fmt(base)}² = c, avec c > 0 : ${fmt(base)} = √c ou ${fmt(base)} = −√c. N’oublie pas la solution négative !`,
        },
      };
    }
  }

  // conclure avec Δ et les racines (trinôme non factorisé)
  const tri = trinomialOf(fRel, x);
  const unfactored = ff === 'expanded' || ff === 'canonical' || ff === 'unsorted';

  if (!eq) {
    // moteur v42 : le tableau de signes de cette ligne est dressé : on le lit
    if (solved && ks.tables.includes(F.K))
      return {
        info: {
          id: POLY_STEP_IDS.signTableRead,
          card: 'Inéquations',
          label: 'Lire le tableau de signes',
          hora: 1,
          why: 'Sur la dernière ligne du tableau, on garde les intervalles où l’expression a le signe demandé ; un 0 est gardé si l’inégalité est large, une valeur interdite (‖) jamais.',
        },
      };
    if (ff === 'factored' && solved)
      return {
        info: {
          id: POLY_STEP_IDS.signTable,
          card: 'Inéquations',
          label: 'Tableau de signes',
          hora: 3,
          why:
            'Chaque facteur change de signe en sa racine ; la règle des signes donne le signe du produit sur chaque intervalle.' +
            (shapeHasDen(F.s, x)
              ? ' La valeur qui annule le dénominateur est interdite : exclue.'
              : ''),
        },
      };
    if (ff === 'cube' && linear)
      return {
        info: {
          id: POLY_STEP_IDS.cubeCompare,
          card: 'Inéquations',
          label: 'Fonction cube',
          hora: 2,
          why: 'La fonction cube est strictement croissante : A³ ≤ c équivaut à A ≤ b, où b³ = c. Le sens ne change pas, une seule borne.',
        },
      };
    if (ff === 'square' && linear) {
      const upper = fRel.rel === '<' || fRel.rel === '≤';
      const xl = has(fRel.l, x);
      const below = xl ? upper : !upper; // « A² ≤ c » ou « c ≥ A² »
      return {
        info: {
          id: POLY_STEP_IDS.squareCompare,
          card: 'Inéquations',
          label: 'Fonction carré',
          hora: 3,
          why: below
            ? 'Pour c > 0 : A² ≤ c équivaut à −√c ≤ A ≤ √c (A² < c : bornes exclues).'
            : 'Pour c > 0 : A² ≥ c équivaut à A ≤ −√c ou A ≥ √c (A² > c : bornes exclues).',
        },
      };
    }
    if (solved && (unfactored || ff === 'partial')) {
      if (tri && rootsKnown(ks, tri))
        return {
          info: {
            id: POLY_STEP_IDS.trinomialSign,
            card: POLY_CARD,
            label: 'Signe du trinôme',
            hora: 3,
            why:
              Q.sign(tri.disc) > 0
                ? `a = ${ratText(tri.a)} : le trinôme est du signe de a à l’extérieur des racines, du signe contraire entre elles.`
                : Q.sign(tri.disc) === 0
                  ? `Δ = 0, a = ${ratText(tri.a)} : le trinôme est du signe de a partout, nul en sa racine double.`
                  : `Δ < 0, a = ${ratText(tri.a)} : le trinôme ne s’annule pas, il est partout du signe de a.`,
          },
        };
      return {
        reject: {
          title: 'Juste, mais trop d’un coup',
          message:
            'Pour lire le signe, factorise d’abord (facteur commun, identité remarquable), ou calcule Δ et les racines du trinôme.',
        },
      };
    }
    return null;
  }
  // équation : conclure avec les racines
  if (solved && (unfactored || ff === 'partial')) {
    if (tri && rootsKnown(ks, tri))
      return {
        info: {
          id: POLY_STEP_IDS.roots,
          card: POLY_CARD,
          label: 'Les solutions sont les racines',
          hora: 2,
          why:
            'Les solutions de ax² + bx + c = 0 sont les racines du trinôme, calculées avec Δ.' +
            (hypothesesText(ks) && clausesOf(T.s).length < (Q.isZero(tri.disc) ? 1 : 2)
              ? ` D’après l’énoncé, ${hypothesesText(ks)} : on écarte la racine qui ne convient pas.`
              : ''),
        },
      };
    return {
      reject: {
        title: 'Juste, mais trop d’un coup',
        message:
          'Factorise d’abord (puis produit nul), ou calcule Δ et les racines du trinôme : une étape par ligne.',
      },
    };
  }
  return null;
}
/**
 * Le terme ajouté aux deux membres, tel qu'il est ÉCRIT dans la ligne de départ : celui
 * qui y disparaît (« −(x + 2)² » → on ajoute « (x + 2)² »). null s'il n'y est pas.
 */
/* ------------------------------ équations trigonométriques (moteur v24) ------------------------------ */

const TRIG_CARD = 'Trigonométrie';
const relOf = (s: Statement): Relation | null =>
  s.k === 'rel' ? { rel: s.rel, l: s.l, r: s.r } : null;
/** la fonction (cos, sin, tan) du premier nœud qui contient x */
function trigFnOf(r: Relation, x: string): string | null {
  const n = trigNodesOfRelation(r, x)[0];
  return n?.k === 'fn' ? n.f : null;
}
const nodeText = (r: Relation, x: string): string => {
  const n = trigNodesOfRelation(r, x)[0];
  return n ? termText(n) : 'cos(x)';
};

/** Une solution perdue (moteur v24) : le rappel des deux familles, ou du cercle. */
function trigLostTip(ks: KernelSheetState, T: KLine): string {
  return hasFamilies(T.s, ks.trig!.params)
    ? ' Souvent, il y a deux familles : cos(a) = cos(b) donne a = b + 2kπ ou a = −b + 2kπ ; sin(a) = sin(b) donne a = b + 2kπ ou a = π − b + 2kπ.'
    : ' Sur un tour du cercle, un cosinus ou un sinus prend en général deux fois la même valeur.';
}

/** « x = c » avec c hors de l'intervalle de l'énoncé ; null sinon. */
function outsideValue(ks: KernelSheetState, s: Statement): string | null {
  const tm = ks.trig!;
  if (!tm.dom || hasFamilies(s, tm.params)) return null;
  for (const r of relationsIn(s)) {
    if (r.rel !== '=') continue;
    const c =
      r.l.k === 'sym' && r.l.n === ks.x ? r.r : r.r.k === 'sym' && r.r.n === ks.x ? r.l : null;
    if (!c || symbolsOf(c).size) continue;
    const q = piCoef(c);
    if (q && !inTrigDomain(q, tm.dom)) return `${ks.x} = ${piText(q)}`;
  }
  return null;
}

/** Les familles d'une ligne sont-elles écrites en x seul (« x = … + 2kπ ») ? */
function familiesSolved(s: Statement, x: string): boolean {
  return relationsIn(s).every((r) => r.rel === '=' && r.l.k === 'sym' && r.l.n === x);
}

const FAMILY_WHY: Record<string, string> = {
  cos: 'cos(a) = cos(b) équivaut à a = b + 2kπ ou a = −b + 2kπ, k entier (deux points du cercle, symétriques par rapport à l’axe horizontal).',
  sin: 'sin(a) = sin(b) équivaut à a = b + 2kπ ou a = π − b + 2kπ, k entier (deux points du cercle, symétriques par rapport à l’axe vertical).',
  tan: 'tan(a) = tan(b) équivaut à a = b + kπ, k entier : tan revient tous les π.',
};

/** « x = π ÷ 6 + 2kπ ou … » → « x = π ÷ 6 ou … » : quelles valeurs de k ? */
function intervalWhy(ks: KernelSheetState, F: KLine, T: KLine): string {
  const tm = ks.trig!;
  const fams: Family[] = [];
  for (const r of relationsIn(F.s)) {
    const f = solveEquality(r, ks.x, tm.params);
    if (f) fams.push(...f);
  }
  const byK = new Map<string, string[]>();
  for (const r of relationsIn(T.s)) {
    if (r.rel !== '=' || r.l.k !== 'sym' || r.l.n !== ks.x) continue;
    const q = piCoef(r.r);
    if (!q) continue;
    const k = fams.map((f) => familyIndex(q, f)).find((i) => i !== null);
    if (k === undefined || k === null) continue;
    const key = k.toString().replace('-', '−');
    byK.set(key, [...(byK.get(key) ?? []), `${ks.x} = ${piText(q)}`]);
  }
  const parts = [...byK.entries()].map(([k, vs]) => `k = ${k} donne ${vs.join(' et ')}`);
  const name = [...tm.params][0] ?? 'k';
  return parts.length
    ? `${parts.join(' ; ')}. Les autres valeurs de ${name} sortent de l’intervalle (${tm.text}).`.replace(
        /\bk = /g,
        `${name} = `,
      )
    : `On donne à ${name} les valeurs entières qui placent ${ks.x} dans l’intervalle (${tm.text}).`;
}

/** La même opération dans chaque clause (« 2x = … ou 2x = … » → « x = … ou x = … »). */
function trigClauseStep(ks: KernelSheetState, F: KLine, T: KLine): PolyStepResult | null {
  const fc = trigClauses(F.s);
  const tc = trigClauses(T.s);
  if (fc.length !== tc.length || fc.length > 4) return null;
  const orders = fc.length <= 3 ? permutations(fc.map((_, i) => i)) : [fc.map((_, i) => i)];
  for (const order of orders) {
    const changed: StepInfo[] = [];
    let ok = true;
    for (let i = 0; i < tc.length && ok; i++) {
      const f = fc[order[i]!]!;
      const t = tc[i]!;
      if (clauseKey(f) === clauseKey(t)) continue;
      const st = clauseStepInfo(ks, f, t);
      if (!st) ok = false;
      else changed.push(st);
    }
    if (!ok || !changed.length) continue;
    const first = changed[0]!;
    if (changed.some((c) => c.label !== first.label)) return null;
    return {
      info:
        changed.length > 1
          ? { ...first, why: `${first.why} On le fait dans chaque clause.` }
          : first,
    };
  }
  return null;
}

/**
 * Les pas d'une équation trigonométrique (moteur v24) : diviser par cos(x), angle
 * associé, valeur remarquable, familles de solutions, lire sur le cercle, garder celles
 * de l'intervalle, la même opération dans chaque clause. null : le juge habituel décide.
 */
function trigStep(ks: KernelSheetState, F: KLine, c: StepCheck, T: KLine): PolyStepResult | null {
  const tm = ks.trig!;
  const x = ks.x;
  const out = tm.bounded ? outsideValue(ks, T.s) : null;
  if (out)
    return {
      reject: {
        title: 'Hors de l’intervalle',
        message: `${out} n’est pas dans l’intervalle de l’énoncé (${tm.text}) : ne garde que les solutions qui y sont.`,
      },
    };
  const fr = relOf(F.s);
  const tr = relOf(T.s);
  const ff: TrigForm | null = fr ? trigRelForm(fr, x, tm.params) : null;
  const tf: TrigForm | null = tr ? trigRelForm(tr, x, tm.params) : null;
  const fFam = hasFamilies(F.s, tm.params);
  const tFam = hasFamilies(T.s, tm.params);
  const tValues = isValueLine(T.s, x, tm.params);
  const fValues = isValueLine(F.s, x, tm.params);
  // deux fonctions : diviser par cos(x) (tan = sin ÷ cos), ou un angle associé
  if (ff === 'mixed') {
    if (tr && tf !== 'mixed' && tf !== 'family' && tf !== 'linear' && trigFnOf(tr, x) === 'tan') {
      const cosNode = trigNodesOfRelation(fr!, x).find((n) => n.k === 'fn' && n.f === 'cos');
      const cosText = cosNode ? termText(cosNode) : `cos(${x})`;
      return {
        info: {
          id: TRIG_STEP_IDS.divideCos,
          card: TRIG_CARD,
          label: `Diviser par ${cosText}`,
          hora: 2,
          why: `${cosText} ne s’annule pas sur les solutions : s’il valait 0, le sinus vaudrait 1 ou −1 et l’égalité serait fausse. Et sin ÷ cos = tan.`,
        },
      };
    }
    if (tf === 'associated')
      return {
        info: {
          id: TRIG_STEP_IDS.associated,
          card: TRIG_CARD,
          label: 'Angles associés',
          hora: 2,
          why: 'Un angle associé change une fonction en l’autre : sin(a) = cos(π ÷ 2 − a) et cos(a) = sin(π ÷ 2 − a). Il reste deux cosinus (ou deux sinus) à comparer.',
        },
      };
    return {
      reject: {
        title: 'Une étape à la fois',
        message:
          'Ramène-toi d’abord à une seule fonction : divise par cos(x) (sin(x) ÷ cos(x) = tan(x)), ou utilise un angle associé (sin(x) = cos(π ÷ 2 − x)).',
      },
    };
  }
  // une feuille d'angle en degrés (moteur v21) : retrouver l'angle reste « Utiliser arctan »
  if (ks.angles) return null;
  const ineq = !!fr && fr.rel !== '=';
  const f0 = fr ? (trigFnOf(fr, x) ?? 'cos') : 'cos';
  // valeur remarquable : « sin(x) = 1 ÷ 2 » → « sin(x) = sin(π ÷ 6) »
  if (ff === 'isolated' && tf === 'reference') {
    const node = trigNodesOfRelation(fr!, x)[0]!;
    const value = termKey(canon(fr!.l)) === termKey(canon(node)) ? fr!.r : fr!.l;
    const ref = termKey(canon(tr!.l)) === termKey(canon(node)) ? tr!.r : tr!.l;
    return {
      info: {
        id: TRIG_STEP_IDS.knownValue,
        card: TRIG_CARD,
        label: 'Valeur remarquable',
        hora: 2,
        why: `${termText(value)} = ${termText(ref)} : une valeur remarquable du cercle trigonométrique.`,
      },
    };
  }
  if (ff === 'trig' && (tf === 'reference' || tFam || tValues))
    return {
      reject: {
        title: 'Une opération à la fois',
        message: `Isole d’abord ${nodeText(fr!, x)} (une opération des deux côtés), puis cherche la valeur remarquable.`,
      },
    };
  const ready = ff === 'isolated' || ff === 'reference' || ff === 'associated';
  // les familles de solutions
  if (ready && tFam && !ineq) {
    const n = trigClauses(T.s).length;
    const label =
      f0 === 'tan' || n === 1 ? 'Une famille de solutions' : 'Deux familles de solutions';
    return {
      info: {
        id: TRIG_STEP_IDS.families,
        card: TRIG_CARD,
        label,
        hora: 3,
        why: FAMILY_WHY[f0] ?? FAMILY_WHY.cos!,
      },
    };
  }
  // lire les solutions sur le cercle, dans l'intervalle
  if (ready && tValues && tm.bounded)
    return {
      info: {
        id: TRIG_STEP_IDS.circle,
        card: TRIG_CARD,
        label: 'Lire sur le cercle',
        hora: 3,
        why: ineq
          ? `Sur le cercle trigonométrique, on repère les points de l’intervalle (${tm.text}) où l’inégalité est vraie ; une borne est comprise avec ≤ ou ≥, exclue avec < ou >.`
          : `Sur le cercle trigonométrique, on repère les points de l’intervalle (${tm.text}) où ${nodeText(fr!, x)} prend cette valeur : ce sont les solutions.`,
      },
    };
  // garder les solutions de l'intervalle
  if (fFam && tValues && tm.bounded) {
    if (!familiesSolved(F.s, x))
      return {
        reject: {
          title: 'Une opération à la fois',
          message: `Écris d’abord les familles en ${x} seul (« ${x} = … + 2kπ »), puis garde celles de l’intervalle.`,
        },
      };
    return {
      info: {
        id: TRIG_STEP_IDS.interval,
        card: TRIG_CARD,
        label: 'Garder celles de l’intervalle',
        hora: 2,
        why: intervalWhy(ks, F, T),
      },
    };
  }
  // la même opération dans chaque clause
  if ((fFam && tFam) || (fValues && tValues)) return trigClauseStep(ks, F, T);
  return null;
}

function movedTerm(s: Statement, v: string | undefined): string | null {
  if (!v) return null;
  const p = parseStatement(v);
  if (!p.ok || p.s.k !== 'expr') return null;
  const target = p.s.e;
  for (const r of relationsOf(s))
    for (const side of [r.l, r.r])
      for (const u of side.k === 'add' ? side.c : [side]) {
        const opp = canon({ k: 'mul', c: [{ k: 'num', v: Q.rat(-1) }, u] });
        if (sameValue(opp, target) && canon(opp).k !== 'add') return fmt(opp);
      }
  return null;
}
/** x apparaît-il au dénominateur d'un membre ? */
function shapeHasDen(s: Statement, x: string): boolean {
  return relationsOf(s).some((r) => {
    if (writtenDen(r.l, x) || writtenDen(r.r, x)) return true;
    const n = normalForm(sub(r.l, r.r));
    if (!n.ok) return false;
    const d = rfSimplify(n.rf).d;
    for (const { m } of d.t.values()) if (m.some((f) => f.a.symbol === x)) return true;
    return false;
  });
}

/* ---- Δ et racines ---- */

/**
 * « Δ = (−5)² − 4 × 1 × 6 », « x₁ = (5 − √Δ) ÷ 2 », « Δ > 0 » : les grandeurs du trinôme
 * de la feuille (moteur v23). null : la ligne n'en est pas une.
 */
function auxVerdict(
  ks: KernelSheetState,
  s: Statement,
  E: Line,
  A: string[],
): KernelVerdict | null {
  if (s.k !== 'rel' || s.l.k !== 'sym') return null;
  const name = s.l.n;
  const kind = auxKind(name, ks.x);
  if (!kind) return null;
  const bad = (title: string, message: string): KernelVerdict => ({
    kind: 'unjustified',
    atoms: A,
    line: E,
    title,
    message,
  });
  const tris = sheetTrinomials(ks);
  if (!tris.length)
    return bad(
      'Quel trinôme ?',
      `Écris d’abord la ligne sous la forme ax² + bx + c = 0 (ou face à 0) : ${name} se calcule pour ce trinôme.`,
    );
  let rhs = s.r;
  if (symbolsOf(rhs).has('Δ')) {
    const d = ks.aux.get('Δ');
    if (!d) return bad('Calcule d’abord Δ', `Écris « Δ = … » avant de t’en servir.`);
    rhs = substitute(rhs, { Δ: d.value });
  }
  if (symbolsOf(rhs).size)
    return bad(
      'Avec des nombres',
      `${name} se calcule avec les nombres du trinôme : pas de lettre à droite.`,
    );
  const value = reduced(rhs);
  if (!value)
    return bad(
      'Ce calcul n’a pas de sens',
      'Vérifie le calcul (division par zéro, racine d’un négatif…).',
    );
  const debt = calcDebt(rhs) + radWeight(radDebt(rhs));
  const kern = (trusted: boolean, aux?: AuxValue) => ({
    s,
    trusted,
    auxLine: true,
    ...(aux ? { aux } : {}),
  });
  const t0 = tris[0]!.t;

  // « Δ > 0 » : le signe du discriminant
  if (s.rel !== '=') {
    const d = ks.aux.get('Δ');
    if (kind !== 'disc' || !d)
      return bad('Calcule d’abord Δ', 'Écris « Δ = … » puis compare-le à 0.');
    const diff = reduced(sub(d.value, rhs));
    const sign = diff && diff.k === 'num' ? Q.sign(diff.v) : null;
    if (sign === null) return null;
    const holds =
      s.rel === '<' ? sign < 0 : s.rel === '>' ? sign > 0 : s.rel === '≤' ? sign <= 0 : sign >= 0;
    if (!holds)
      return {
        kind: 'false',
        atoms: A,
        line: E,
        title: 'Ce n’est pas le signe de Δ',
        message: `Δ = ${fmt(d.value)} : relis la comparaison.`,
      };
    if (ks.lines.some((l) => l.raw === statementKey(s)))
      return {
        kind: 'dup',
        atoms: A,
        line: E,
        title: 'Déjà écrit',
        message: 'Tu as déjà comparé Δ à 0.',
      };
    const dv = reduced(d.value);
    const n = dv && dv.k === 'num' ? Q.sign(dv.v) : 0;
    return {
      kind: 'step',
      atoms: A,
      line: E,
      hora: 1,
      rule: {
        id: POLY_STEP_IDS.discriminantSign,
        card: POLY_CARD,
        label: 'Signe de Δ',
        hora: 1,
        why:
          n > 0
            ? 'Δ > 0 : le trinôme a deux racines.'
            : n === 0
              ? 'Δ = 0 : le trinôme a une racine double.'
              : 'Δ < 0 : le trinôme n’a pas de racine réelle.',
      },
      kern: kern(false),
    };
  }

  if (kind === 'disc') {
    const ok = tris.some((t) => value.k === 'num' && Q.eq(value.v, t.t.disc));
    if (!ok)
      return {
        kind: 'false',
        atoms: A,
        line: E,
        title: 'Ce n’est pas le discriminant',
        message: `Δ = b² − 4ac, avec a = ${ratText(t0.a)}, b = ${ratText(t0.b)}, c = ${ratText(t0.c)} : attention aux signes.`,
      };
    const prev = ks.aux.get('Δ');
    if (prev) {
      if (debt < prev.debt)
        return {
          kind: 'step',
          atoms: A,
          line: E,
          hora: 1,
          rule: {
            id: KERNEL_STEP_IDS.compute,
            card: 'Calcul',
            label: 'Calculer',
            hora: 1,
            why: 'On calcule Δ.',
          },
          kern: kern(false, { name, value: rhs, debt }),
        };
      return { kind: 'dup', atoms: A, line: E, title: 'Déjà calculé', message: 'Tu as déjà Δ.' };
    }
    return {
      kind: 'step',
      atoms: A,
      line: E,
      hora: 2,
      rule: {
        id: POLY_STEP_IDS.discriminant,
        card: POLY_CARD,
        label: 'Calculer le discriminant',
        hora: 2,
        why: 'Δ = b² − 4ac : son signe dit combien de racines a le trinôme.',
      },
      kern: kern(false, { name, value: rhs, debt }),
    };
  }

  // une racine : x₁, x₂, ou x₀ (racine double)
  const double = name === `${ks.x}₀`;
  const own = tris.filter((t) => isRootOf(t.t, rhs));
  if (!own.length)
    return {
      kind: 'false',
      atoms: A,
      line: E,
      title: 'Ce n’est pas une racine',
      message: double
        ? 'La racine double : x₀ = −b ÷ (2a).'
        : `x₁ = (−b − √Δ) ÷ (2a) et x₂ = (−b + √Δ) ÷ (2a) : vérifie les signes (b = ${ratText(t0.b)}).`,
    };
  if (double && !own.some((t) => Q.isZero(t.t.disc)))
    return bad(
      'Pas de racine double',
      'x₀ désigne la racine double, quand Δ = 0 : ici, écris x₁ et x₂.',
    );
  const other =
    name === `${ks.x}₁`
      ? ks.aux.get(`${ks.x}₂`)
      : name === `${ks.x}₂`
        ? ks.aux.get(`${ks.x}₁`)
        : null;
  if (other && sameValue(other.value, rhs) && own.some((t) => Q.sign(t.t.disc) > 0))
    return {
      kind: 'useless',
      atoms: A,
      line: E,
      title: `C’est la même racine que ${other.name}`,
      message: 'Les deux racines diffèrent par le signe devant √Δ.',
    };
  const prev = ks.aux.get(name);
  if (prev) {
    if (sameValue(prev.value, rhs) && debt < prev.debt)
      return {
        kind: 'step',
        atoms: A,
        line: E,
        hora: 1,
        rule: {
          id: KERNEL_STEP_IDS.compute,
          card: 'Calcul',
          label: 'Calculer',
          hora: 1,
          why: `On calcule ${name}.`,
        },
        kern: kern(false, { name, value: rhs, debt }),
      };
    return { kind: 'dup', atoms: A, line: E, title: 'Déjà écrit', message: `Tu as déjà ${name}.` };
  }
  return {
    kind: 'step',
    atoms: A,
    line: E,
    hora: 2,
    rule: {
      id: POLY_STEP_IDS.root,
      card: POLY_CARD,
      label: double ? 'Racine double' : 'Racine du trinôme',
      hora: 2,
      why: double
        ? 'Δ = 0 : une racine double, x₀ = −b ÷ (2a).'
        : 'Δ > 0 : x₁ = (−b − √Δ) ÷ (2a) et x₂ = (−b + √Δ) ÷ (2a).',
    },
    kern: kern(false, { name, value: rhs, debt }),
  };
}

/** Enregistre Δ ou une racine écrite (moteur v23). */
function commitAux(ks: KernelSheetState, a: AuxValue): void {
  ks.aux.set(a.name, a);
}

/* ------------------------------ systèmes (moteur v22) ------------------------------ */

const SYSTEM_CARD = 'Systèmes';

/** Une ligne d'un système vérifiée par le noyau, inconnue à isoler : u. */
function sysCheck(ks: KernelSheetState, from: Statement, to: Statement, u: string): StepCheck {
  const key = `sys:${u}:${statementKey(from)}→${statementKey(to)}`;
  const hit = ks.cache.get(key);
  if (hit) return hit;
  const c = checkAlgebraStatements(from, to, {
    rules: ks.rules,
    hypotheses: ks.hyps,
    goal: { kind: 'solve', target: u },
    deadline: Date.now() + 250,
    branches: true,
  });
  ks.cache.set(key, c);
  return c;
}

const quoted = (l: { text: string }): string => `« ${l.text} »`;

/**
 * « Transformer une ligne » : le geste du cours (règle des deux côtés, calcul) qui mène
 * de `from` à `to`, pour l'une des inconnues ; null si plusieurs opérations à la fois.
 */
function transformInfo(
  ks: KernelSheetState,
  from: Statement,
  to: Statement,
  us: string[],
): { info: StepInfo | null; several: string | null; useful: boolean } {
  const useful = us.some((u) => better(kline(to, u, true), kline(from, u, true)));
  for (const u of us) {
    const c = sysCheck(ks, from, to, u);
    if (c.status !== 'valid' || (c.relation !== 'equivalent' && c.relation !== 'identity'))
      continue;
    if (c.proof === 'rule' && c.ruleId && addsSeveralTerms(c))
      return { info: null, several: c.explanation?.params?.v_c ?? '', useful };
    const Fk = kline(from, u, true);
    const Tk = kline(to, u, true);
    const sameLine = (Tk.NK !== null && Tk.NK === Fk.NK) || sameConstValue(from, to, u);
    let info: StepInfo | null = null;
    if (c.proof === 'rule' && c.ruleId?.startsWith('algebra.') && sameLine)
      info = computeStep(Fk, Tk, from, to) ?? ruleStep(c, to, from);
    else if (c.proof === 'rule' && c.ruleId) info = ruleStep(c, to, from);
    else if (sameLine) info = computeStep(Fk, Tk, from, to);
    if (info) return { info, several: null, useful };
  }
  return { info: null, several: null, useful };
}

/** Ce que la ligne établit : une valeur calculée (« x = 9/5 »), une contradiction (« 0 = 1 »). */
function sysCommitOf(
  ks: KernelSheetState,
  T: Statement,
  origin: number,
): SysCommit & {
  origin: number;
} {
  const out: SysCommit & { origin: number } = { origin };
  if (T.k !== 'rel' || T.rel !== '=') return out;
  if (T.l.k === 'sym' && isConst(T.r)) {
    const c = canon(T.r);
    if (c.k === 'num' && calcDebt(T.r) === 0) out.value = { u: T.l.n, v: c.v };
  }
  const f = linearForm(T);
  if (f && isContradictionForm(f) && isConst(T.l) && isConst(T.r))
    if (calcDebt(T.l) + calcDebt(T.r) === 0) out.contradiction = true;
  return out;
}

/** Un multiple d'une ligne, en mots : « 3 × « x − y = 1 » », « − « x − y = 1 » ». */
function scaledLine(k: Q.Rat, s: { text: string }, first: boolean): string {
  const neg = Q.sign(k) < 0;
  const a = Q.abs(k);
  const factor = Q.eq(a, Q.ONE) ? '' : `${formatNumber(Q.ratToNumber(a))} × `;
  const sign = neg ? (first ? '−' : ' − ') : first ? '' : ' + ';
  return `${sign}${factor}${quoted(s)}`;
}

function systemVerdict(
  ks: KernelSheetState,
  ss: SystemState,
  move: SysMove,
  T: KLine,
  E: Line,
  A: string[],
  f: ReturnType<typeof linearForm> & object,
): KernelVerdict {
  const origin = moveOrigin(move);
  const commit = sysCommitOf(ks, T.s, origin);
  if (move.kind === 'redundant') commit.redundancy = redundancyKey(move.a.origin, move.b.origin);
  const kern = { s: T.s, trusted: true, sys: commit };
  const line = makeSysLine(T.s, T.K, origin, lineText(E));
  const finalOf = (): boolean => !!line && sysWouldReach(ss, line, commit);
  const us = [...unknownsOf(f)].sort();
  const step = (info0: StepInfo): KernelVerdict => {
    // la ligne retrouve une autre ligne venue d'autres équations : une équation de trop
    const red =
      ss.outcome === 'infinite' && move.kind !== 'redundant' && line
        ? redundancyOf(ss, line)
        : null;
    const info = red
      ? {
          ...info0,
          hora: Math.max(2, info0.hora),
          why: `${info0.why} On retrouve ${quoted(red.other)} : les deux équations disent la même chose, le système a une équation de trop.`,
        }
      : info0;
    return { kind: 'step', atoms: A, line: E, hora: info.hora, final: finalOf(), rule: info, kern };
  };
  switch (move.kind) {
    case 'transform': {
      const vars = us.length ? us : [...unknownsOf(move.from.f)].sort();
      const t0 = transformInfo(ks, move.from.s, T.s, vars.length ? vars : [ks.x]);
      const red = ss.outcome === 'infinite' && !!line && !!redundancyOf(ss, line);
      const t = { ...t0, useful: t0.useful || red || preparesCombination(ss, move.from, f) };
      if (t.several !== null)
        return {
          kind: 'unjustified',
          atoms: A,
          line: E,
          title: 'Une opération à la fois',
          message: `Ta ligne est juste, mais elle ajoute ${wrapTerm(t.several)} d’un coup : déplace un terme à la fois, puis calcule.`,
        };
      if (!t.info)
        return {
          kind: 'unjustified',
          atoms: A,
          line: E,
          title: 'Juste, mais trop d’un coup',
          message:
            'Ta ligne est juste, mais elle fait plusieurs opérations à la fois sur ' +
            quoted(move.from) +
            ' : une seule par ligne (une opération des deux côtés, puis le calcul).',
        };
      // une valeur ou une contradiction calculée avance toujours
      if (!t.useful && !commit.value && !commit.contradiction && !finalOf())
        return {
          kind: 'useless',
          atoms: A,
          line: E,
          title: 'Juste, mais ça ne t’avance pas',
          message:
            'Ta ligne est juste, mais elle ne rapproche d’aucune inconnue seule. Garde-la si elle t’aide, ou isole une inconnue pour la remplacer, ou combine deux lignes.',
          kern,
        };
      return step(t.info);
    }
    case 'substitute': {
      const { u, e } = move.sub.isolated!;
      if (!move.literal)
        return {
          kind: 'unjustified',
          atoms: A,
          line: E,
          title: 'Une opération à la fois',
          message: `Ta ligne est juste, mais elle remplace ${u} ET calcule d’un coup : écris d’abord ${quoted(move.into)} avec ${u} remplacé, puis calcule.`,
        };
      const before = unknownsOf(move.into.f).size;
      if (us.length >= before)
        return {
          kind: 'useless',
          atoms: A,
          line: E,
          title: 'Juste, mais ça ne t’avance pas',
          message: `Ta ligne est juste, mais elle a toujours autant d’inconnues : remplace une inconnue par une écriture qui ne la contient pas.`,
          kern,
        };
      const value = isConst(e);
      return step({
        id: KERNEL_STEP_IDS.substitute,
        card: SYSTEM_CARD,
        label: value ? 'Remplacer par sa valeur' : 'Remplacer',
        hora: 2,
        why: value
          ? `${u} est connu (${quoted(move.sub)}) : on remplace ${u} par sa valeur dans ${quoted(move.into)}.`
          : `${quoted(move.sub)} isole ${u} : on remplace ${u} dans ${quoted(move.into)}, il reste une inconnue de moins.`,
      });
    }
    case 'combine': {
      if (!move.integer)
        return {
          kind: 'unjustified',
          atoms: A,
          line: E,
          title: 'Juste, mais trop d’un coup',
          message:
            'Ta ligne est juste, mais elle combine deux lignes ET divise d’un coup : écris d’abord la combinaison avec des nombres entiers, puis simplifie.',
        };
      if (!move.eliminated.length)
        return {
          kind: 'useless',
          atoms: A,
          line: E,
          title: 'Juste, mais ça ne t’avance pas',
          message:
            'Ta ligne est juste, mais aucune inconnue n’a disparu : choisis les multiples des deux lignes pour qu’une inconnue s’élimine.',
          kern,
        };
      // le terme positif d'abord : « L₂ − 2 × L₁ » plutôt que « −2 × L₁ + L₂ »
      const [p, q] =
        Q.sign(move.lambdas[0]) < 0 && Q.sign(move.lambdas[1]) > 0
          ? [
              [move.lambdas[1], move.b],
              [move.lambdas[0], move.a],
            ]
          : [
              [move.lambdas[0], move.a],
              [move.lambdas[1], move.b],
            ];
      const combo =
        scaledLine(p[0] as Q.Rat, p[1] as SysLine, true) +
        scaledLine(q[0] as Q.Rat, q[1] as SysLine, false);
      const gone = move.eliminated.join(' et ');
      return step({
        id: KERNEL_STEP_IDS.combine,
        card: SYSTEM_CARD,
        label: 'Combiner deux lignes',
        hora: 2,
        why: commit.contradiction
          ? `${combo} : les inconnues disparaissent et il reste une égalité fausse. Le système n’a aucune solution.`
          : `${combo} : ${gone} disparaî${move.eliminated.length > 1 ? 'ssent' : 't'}.`,
      });
    }
    case 'redundant':
      return step({
        id: KERNEL_STEP_IDS.redundant,
        card: SYSTEM_CARD,
        label: 'Deux équations équivalentes',
        hora: 2,
        why: `${quoted(move.a)} et ${quoted(move.b)} disent la même chose : l’une est un multiple de l’autre, le système a une équation de trop.`,
      });
  }
}

function evaluateSystem(
  ks: KernelSheetState,
  ss: SystemState,
  E: Line,
  A: string[],
): KernelVerdict {
  const read = statementOfLine(E);
  if (!read.ok || read.s.k !== 'rel' || read.s.rel !== '=')
    return {
      kind: 'unjustified',
      atoms: A,
      line: E,
      title: 'Une équation par ligne',
      message: 'Dans un système, chaque ligne est une équation (avec « = »), une seule par ligne.',
    };
  const T = kline(read.s, ks.x, true);
  const trap = ks.traps.find((t) => t.K === T.K);
  if (trap)
    return {
      kind: 'trap',
      atoms: A,
      line: E,
      title: 'Piège : ' + lower(trap.title),
      message: trap.why,
      trap: { line: trap.line, title: trap.title, why: trap.why },
      badChip: -1,
    };
  const f = linearForm(T.s);
  // une équation de l'énoncé : recopiée, ou RETROUVÉE depuis une autre (redondance)
  const eq = ss.equations.findIndex((e) => e.K === T.K);
  if (eq >= 0) {
    const bit = 1 << eq;
    const from =
      f && ss.outcome === 'infinite'
        ? sysSources(ss).find((l) => l.origin !== bit && sameEquation(l.f, f))
        : undefined;
    if (from && !ks.lines.some((l) => l.raw === T.raw)) {
      const t = transformInfo(ks, from.s, T.s, [...unknownsOf(f!)].sort());
      if (t.info) {
        const origin = from.origin;
        const commit = sysCommitOf(ks, T.s, origin);
        const line = makeSysLine(T.s, T.K, origin, lineText(E));
        return {
          kind: 'step',
          atoms: A,
          line: E,
          hora: 2,
          final: !!line && sysWouldReach(ss, line, commit),
          rule: {
            id: KERNEL_STEP_IDS.redundant,
            card: SYSTEM_CARD,
            label: 'Deux équations équivalentes',
            hora: 2,
            why: `${t.info.label} : ${quoted(from)} redonne ${quoted(ss.equations[eq]!)}, une équation de l’énoncé. Les deux disent la même chose : le système a une équation de trop.`,
          },
          kern: { s: T.s, trusted: true, sys: commit },
        };
      }
    }
    return {
      kind: 'given',
      atoms: A,
      line: E,
      title: 'C’est l’énoncé',
      message:
        'Cette équation est déjà en tête de ta feuille : transforme-la, ou combine-la avec une autre.',
    };
  }
  // « 0 = 0 » peut s'écrire plusieurs fois : chaque fois une nouvelle relation entre équations
  const anotherZero = !!f && isTrivialForm(f) && findSysMoves(ss, T.s, f, T.K, keyOf).length > 0;
  if (ks.lines.some((l) => l.raw === T.raw) && !anotherZero)
    return {
      kind: 'dup',
      atoms: A,
      line: E,
      title: 'Déjà écrit',
      message: 'Tu as déjà cette ligne sur ta feuille.',
    };
  if (!f)
    return {
      kind: 'useless',
      atoms: A,
      line: E,
      unknown: true,
      title: 'Je ne sais pas encore vérifier ce passage',
      message:
        'Dans ce système, les équations sont du premier degré : une ligne avec un produit d’inconnues ou une inconnue au dénominateur ne peut pas en découler. Garde-la sans HORA, ou écris le passage autrement.',
      kern: { s: T.s, trusted: false, ...(T.auxWritten ? { auxWritten: true } : {}) },
    };
  if (!sysFollows(ss, f)) {
    const only = sysConstantOnlyWrong(ss, f);
    if (only)
      return {
        kind: 'false',
        atoms: A,
        line: E,
        title: 'Le nombre de l’autre côté ne suit pas',
        message:
          only.kind === 'combine'
            ? 'Les inconnues sont justes, mais pas le nombre de l’autre côté : quand tu combines deux lignes, fais la même opération sur les seconds membres.'
            : `Les inconnues sont justes, mais pas le nombre de l’autre côté : reprends ${quoted(only.lines[0]!)} et fais la même opération sur les deux membres.`,
      };
    const from = sysClosestSource(ss, f);
    return {
      kind: 'false',
      atoms: A,
      line: E,
      title: from
        ? `Cette ligne ne découle pas de ${quoted(from)}`
        : 'Cette ligne ne découle pas des équations',
      message: from
        ? 'Elle n’a pas les mêmes solutions : vérifie les signes, et fais la même opération des deux côtés.'
        : 'Elle n’est pas vraie pour les solutions du système : reprends ton dernier passage (signes, remplacement, seconds membres).',
    };
  }
  // le geste retenu : celui qui atteint le but, puis le plus simple (transformer une ligne
  // aux mêmes coefficients : déplacer un terme plutôt que multiplier), dans l'ordre
  const moves = findSysMoves(ss, T.s, f, T.K, keyOf);
  let first: KernelVerdict | null = null;
  let best: { v: KernelVerdict; rank: number } | null = null;
  for (const move of moves) {
    const v = systemVerdict(ks, ss, move, T, E, A, f);
    first ??= v;
    if (v.kind !== 'step') continue;
    const weight =
      move.kind === 'transform'
        ? sameMagnitudes(move.from.f, f)
          ? 1
          : 15
        : move.kind === 'combine'
          ? Math.min(9, Math.max(...move.lambdas.map((k) => Math.abs(Q.ratToNumber(k)))))
          : 1;
    const rank = (v.final ? 0 : 100) + weight;
    if (!best || rank < best.rank) best = { v, rank };
  }
  if (best) return best.v;
  if (first) return first;
  if (isTrivialForm(f))
    return {
      kind: 'useless',
      atoms: A,
      line: E,
      title: 'Toujours vrai',
      message:
        'Ta ligne est toujours vraie : elle ne dit rien sur les inconnues. Elle sert seulement quand elle vient de deux équations qui disent la même chose.',
    };
  return {
    kind: 'unjustified',
    atoms: A,
    line: E,
    title: 'Juste, mais trop d’un coup',
    message:
      'Ta ligne est juste, mais elle fait plusieurs gestes à la fois. Un seul par ligne : isoler une inconnue, la remplacer dans une autre équation, combiner deux lignes, ou calculer.',
  };
}

/** Mêmes coefficients au signe près (on a seulement déplacé des termes) ? */
function sameMagnitudes(
  a: { coef: ReadonlyMap<string, Q.Rat> },
  b: { coef: ReadonlyMap<string, Q.Rat> },
): boolean {
  if (a.coef.size !== b.coef.size) return false;
  return [...a.coef].every(([u, k]) => {
    const o = b.coef.get(u);
    return !!o && Q.eq(Q.abs(o), Q.abs(k));
  });
}

/** Enregistre une ligne d'un système. */
function commitSystemLine(ks: KernelSheetState, ss: SystemState, v: KernelVerdict): void {
  if (!v.kern) return;
  const l = kline(v.kern.s, ks.x, v.kern.trusted);
  ks.lines.push(l);
  const c = v.kern.sys;
  if (!v.kern.trusted || !c) return;
  const sl = makeSysLine(
    v.kern.s,
    l.K,
    c.origin,
    v.line ? lineText(v.line) : statementText(v.kern.s),
  );
  if (sl) commitSysLine(ss, sl, c);
  if (sysGoalReached(ss)) ks.reached = true;
}

/**
 * Système (moteur v22) : la ligne découle-t-elle des équations ? (porte : pièges, réponse)
 * Faux hors système ou pour une ligne illisible / non linéaire.
 */
export function kernelSystemFollows(ks: KernelSheetState, text: string): boolean {
  if (!ks.sys) return false;
  const p = parseStatement(text);
  if (!p.ok || p.s.k !== 'rel' || p.s.rel !== '=') return false;
  const f = linearForm(p.s);
  return !!f && !isTrivialForm(f) && sysFollows(ks.sys, f);
}

/** Un texte (choix du QCM) est-il vrai pour le système ? (une contradiction s'il n'a pas de solution) */
function systemTextHolds(ss: SystemState, text: string): boolean {
  const p = parseStatement(text);
  if (!p.ok || p.s.k !== 'rel' || p.s.rel !== '=') return false;
  const f = linearForm(p.s);
  if (!f) return false;
  if (ss.outcome === 'none') return isContradictionForm(f) && sysFollows(ss, f);
  return !isTrivialForm(f) && sysFollows(ss, f);
}

/* ------------------------------ feuille ------------------------------ */

/** Enregistre une ligne validée ou gardée. */
export function commitKernelLine(ks: KernelSheetState, v: KernelVerdict): void {
  if (v.table !== undefined) {
    if (!ks.tables.includes(v.table)) ks.tables.push(v.table);
    return;
  }
  if (ks.sys) return commitSystemLine(ks, ks.sys, v);
  if (v.test) {
    ks.tests.set(v.test.k, v.test.holds);
    return;
  }
  // moteur v49 : une condition écrite devient une hypothèse du noyau
  if (v.cond !== undefined) {
    const c = ks.conds[v.cond];
    if (c && !c.ok) {
      c.ok = true;
      ks.condsOk.push(c.cond);
    }
    return;
  }
  if (v.round !== undefined) {
    ks.rounded = v.round;
    ks.reached = true;
    return;
  }
  if (!v.kern) return;
  const l = klineOf(ks, v.kern.s, v.kern.trusted, !!v.kern.auxWritten);
  if (v.kern.auxLine) l.aux = true;
  if (v.kern.condDep) l.condDep = true;
  ks.lines.push(l);
  if (v.kern.aux) commitAux(ks, v.kern.aux);
  if (l.condDep) return;
  if (l.trusted && (v.kind === 'step' || v.kind === 'ready') && v.final) ks.reached = true;
  if (l.trusted && solvedForm(l, ks.x, ks.trig)) ks.reached = true;
}

export function kernelGoalReached(ks: KernelSheetState): boolean {
  return ks.reached && !roundingNeeded(ks);
}

/**
 * Le texte (choix du QCM) dit-il la même chose que la réponse ? `form` (moteur v33) :
 * une écriture à développer ou à factoriser doit en plus avoir la forme attendue.
 */
export function kernelEquivalentToAnswer(
  ks: KernelSheetState,
  text: string,
  form = false,
): boolean {
  if (ks.sys) return systemTextHolds(ks.sys, text);
  // une valeur arrondie de la réponse (moteur v20) : « AC ≈ 7,8 »
  const ap = approxIn(ks, approxOfText(text));
  if (ap) {
    const exact = answerValue(ks);
    return !!exact && ap.l.k === 'sym' && ap.l.n === ks.x && isRequestedRounding(ks, exact, ap.r);
  }
  const p = parseStatement(text);
  if (!p.ok) return false;
  const T = klineOf(ks, readIn(ks, p.s), true, writesAux(ks, p.s));
  // écriture à développer ou à factoriser (moteur v33) : la même valeur ne suffit pas,
  // il faut la forme attendue (« 7(x² − 2x) » n'est pas factorisé complètement)
  if (form && ks.form && !T.answerForm) return false;
  const a = klineOf(ks, ks.answer, true);
  if (T.K === a.K) return true;
  const eq = (c: StepCheck): boolean =>
    c.status === 'valid' && (c.relation === 'equivalent' || c.relation === 'identity');
  if (eq(check(ks, a, T.s, T.raw, true))) return true;
  // l'autre sens : « c = 5√2 → c² = 50 » n'est qu'une implication (élever au carré),
  // « c² = 50 → c = 5√2 » est une équivalence quand c > 0 (moteur v20)
  return eq(check(ks, T, a.s, a.raw, true));
}

/**
 * Lot 6.43 : quand le noyau ne sait pas comparer directement le départ et la réponse
 * (« e^(3x + 1) ≤ 1 ÷ e^(x − 2) » : l'étude exacte ne trouve pas les zéros d'une somme
 * d'exponentielles), le corrigé peut le montrer : chaque ligne ÉQUIVALENTE à la précédente,
 * la dernière équivalente à la réponse.
 */
export function kernelPathEquivalent(
  ks: KernelSheetState,
  start: string,
  path: readonly string[],
): boolean {
  if (ks.sys || !path.length) return false;
  const eq = (c: StepCheck): boolean =>
    c.status === 'valid' && (c.relation === 'equivalent' || c.relation === 'identity');
  const lineOf = (text: string) => {
    const p = parseStatement(text);
    return p.ok ? klineOf(ks, readIn(ks, p.s), true, writesAux(ks, p.s)) : null;
  };
  let prev = lineOf(start);
  if (!prev) return false;
  for (const t of path) {
    const next = lineOf(t);
    if (!next) return false;
    if (next.K !== prev.K && !eq(check(ks, prev, next.s, next.raw, true))) return false;
    prev = next;
  }
  return kernelEquivalentToAnswer(ks, path[path.length - 1]!);
}

/**
 * QCM à plusieurs bonnes réponses (lot 6.7) : le choix « x = v » est-il UNE des solutions
 * de la réponse (« x = −4 ou x = 3 », « x ≤ 1 ») ? Seulement un point « inconnue = nombre » :
 * un ensemble écrit en entier (« x = 1 ou x = 2 » pour « 1 ≤ x ≤ 2 ») doit dire la même
 * chose que la réponse, comme pour un QCM à une bonne réponse.
 */
export function kernelAnswerContains(ks: KernelSheetState, text: string): boolean {
  if (ks.sys || ks.form) return false;
  const p = parseStatement(text);
  if (!p.ok) return false;
  const s = p.s;
  if (s.k !== 'rel' || s.rel !== '=' || s.l.k !== 'sym' || s.l.n !== ks.x || !isConst(s.r))
    return false;
  const T = klineOf(ks, readIn(ks, s), true, writesAux(ks, s));
  const a = klineOf(ks, ks.answer, true);
  const c = check(ks, T, a.s, a.raw, true);
  return (
    c.status === 'valid' &&
    (c.relation === 'implies' || c.relation === 'equivalent' || c.relation === 'identity')
  );
}

/** La ligne (ou une écriture équivalente de la réponse, une fois atteinte) est-elle sur la feuille ? */
export function kernelHasLine(ks: KernelSheetState, line: Line): boolean {
  const ap = approxIn(ks, approximationOf(line));
  if (ap)
    return (
      ks.rounded !== null &&
      ap.l.k === 'sym' &&
      ap.l.n === ks.x &&
      Math.abs(evalFloat(ap.r, {}) - ks.rounded) < 1e-9
    );
  const s = statementOfLine(line);
  if (!s.ok) return false;
  const k = keyOf(readIn(ks, s.s));
  if (ks.lines.some((l) => l.trusted && l.K === k)) return true;
  return ks.reached && kernelEquivalentToAnswer(ks, statementText(s.s), true);
}

/** Valeurs données par les lignes « x = nombre » de la feuille. */
export function kernelStatedValues(ks: KernelSheetState): number[] {
  if (ks.sys) {
    const v = ks.sys.values.get(ks.x);
    return v ? [Q.ratToNumber(v)] : [];
  }
  const out: number[] = [];
  ks.lines.forEach((l) => {
    // « x = 2 ou x = 3 » (moteur v23) : chaque valeur
    if (ks.poly && l.trusted && l.s.k === 'or')
      l.s.items.forEach((i) => {
        const c = i.rel === '=' && i.l.k === 'sym' && i.l.n === ks.x ? canon(i.r) : null;
        if (c && c.k === 'num') out.push(Number(c.v.n) / Number(c.v.d));
      });
    if (!l.trusted || l.s.k !== 'rel' || l.s.rel !== '=') return;
    const r = l.s.l.k === 'sym' && l.s.l.n === ks.x ? l.s.r : null;
    const c = r ? canon(r) : null;
    if (c && c.k === 'num') out.push(Number(c.v.n) / Number(c.v.d));
  });
  return out;
}

/** « S ≈ v » lu dans un texte ; null si ce n'en est pas un. */
function approxOfText(text: string): { l: Term; r: Term } | null {
  if (!text.includes('≈')) return null;
  try {
    return approximationOf(parseText(text));
  } catch {
    return null;
  }
}

/* ------------------------------ corrigé et coup de pouce ------------------------------ */

const SEARCH_BUDGET = { ms: 400, nodes: 4000, depth: 10, branches: 0 };

function searchFrom(ks: KernelSheetState, s: Statement): string[] | null {
  const r: SearchResult = searchAlgebra(statementText(s), {
    rules: ks.rules,
    goal: ks.goal,
    hypotheses: ks.condsOk.length ? [...ks.hyps, ...ks.condsOk] : ks.hyps,
    budget: SEARCH_BUDGET,
  });
  return r.status === 'found' ? r.steps.map((st) => st.to) : null;
}

/** Le corrigé de référence : celui du modèle, sinon celui que trouve la recherche. */
export function referencePath(ks: KernelSheetState): string[] {
  // un système n'a pas de recherche automatique : le corrigé vient du modèle
  if (!ks.ref) ks.ref = ks.spec.path ?? (ks.sys ? null : searchFrom(ks, ks.start.s)) ?? [];
  return ks.ref;
}

/** Atomes d'un texte de ligne, tels que l'élève les taperait. */
export function kernelLineAtoms(text: string): string[] {
  // moteur v42 : un tableau de signes est un seul bout
  if (isSignTableText(text)) return [text];
  return normalize(lineAtoms(parseText(text)));
}

export interface KernelMove {
  line: Line;
  text: string;
  label: string;
  /** moteur v42 : le pas est un tableau de signes à dresser (`line` : la ligne étudiée) */
  table?: boolean;
}

/**
 * Le prochain pas : la ligne suivante du corrigé de référence quand l'élève y est,
 * sinon le 1er pas de la recherche depuis sa ligne la plus avancée.
 */
export function kernelNextMove(ks: KernelSheetState): KernelMove | null {
  if (ks.reached && !roundingNeeded(ks)) return null;
  const tryText = (text: string): KernelMove | null => {
    // moteur v42 : le tableau de signes du corrigé
    if (isSignTableText(text)) {
      const sub = signSubject(ks);
      const v = signTableVerdict(ks, text);
      if (!sub || v.kind !== 'step') return null;
      return { line: sub.line, text, label: v.rule?.label ?? '', table: true };
    }
    let line: Line;
    try {
      line = parseText(text);
    } catch {
      return null;
    }
    const v = evaluate(ks, line, kernelLineAtoms(text));
    if (v.kind !== 'step') return null;
    return { line, text, label: v.rule?.label ?? '' };
  };
  // la valeur exacte est écrite : il reste à l'arrondir (moteur v20)
  if (ks.reached) {
    const exact = answerValue(ks);
    if (!exact || !ks.rounding) return null;
    const f = evalFloat(exact, {});
    if (!Number.isFinite(f)) return null;
    // un angle s'arrondit en degrés : « Â ≈ 53,1° »
    const deg = ks.angles?.[ks.x] ? '°' : '';
    return tryText(`${ks.x} ≈ ${formatNumber(roundTo(f, ks.rounding.places))}${deg}`);
  }
  const ref = referencePath(ks);
  const front = frontier(ks);
  // la ligne du corrigé la plus avancée déjà écrite (aussi bien calculée)
  let at = -1;
  ref.forEach((t, i) => {
    // moteur v42 : le tableau de signes du corrigé, déjà dressé
    if (isSignTableText(t)) {
      const sub = signSubject(ks);
      if (sub && ks.tables.includes(sub.key)) at = i;
      return;
    }
    const p = parseStatement(t);
    if (!p.ok) return;
    const r = klineOf(ks, readIn(ks, p.s), true, writesAux(ks, p.s));
    // une ligne sur Δ ou une racine (moteur v23) compte aussi, même si elle n'est pas une source
    if (ks.lines.some((l) => (l.trusted || l.aux) && l.K === r.K && l.debt <= r.debt)) at = i;
    // moteur v49 : une condition déjà écrite
    if (ks.conds.some((c) => c.ok && c.K === r.K)) at = i;
  });
  for (let i = at + 1; i < ref.length; i++) {
    const m = tryText(ref[i]!);
    if (m) return m;
    // une ligne du corrigé hors de portée : on ne saute pas plus loin
    break;
  }
  if (ks.sys) return null;
  const steps = searchFrom(ks, front.s);
  if (steps?.length) return tryText(steps[0]!);
  return null;
}

/** Le corrigé ligne à ligne (porte de validation, palette). */
export function kernelSolvePath(model: ScratchModel): {
  lines: { line: Line; text: string; v: KernelVerdict }[];
  reached: boolean;
} {
  const ks = createKernelSheetState(model);
  const out: { line: Line; text: string; v: KernelVerdict }[] = [];
  for (const text of referencePath(ks)) {
    // moteur v42 : le tableau de signes (sa « ligne » : la ligne étudiée)
    if (isSignTableText(text)) {
      const sub = signSubject(ks);
      if (!sub) continue;
      const v = signTableVerdict(ks, text);
      out.push({ line: sub.line, text, v });
      if (v.kind === 'step') commitKernelLine(ks, v);
      continue;
    }
    let line: Line;
    try {
      line = parseText(text);
    } catch {
      continue;
    }
    const v = evaluate(ks, line, kernelLineAtoms(text));
    out.push({ line, text, v });
    if (v.kind === 'step') commitKernelLine(ks, v);
  }
  return { lines: out, reached: ks.reached };
}

/** Mesures d'une ligne (tests, panneau moteur) : distance à la réponse, calculs à faire. */
export function kernelLineInfo(
  text: string,
  x: string,
): { h: number; calc: number; expand: number; solved: boolean } | null {
  const p = parseStatement(text);
  if (!p.ok) return null;
  const l = kline(p.s, x, true);
  return { h: l.h, calc: l.calc, expand: l.expand, solved: solvedForm(l, x) };
}

/* ------------------------------ tableau de signes (moteur v42) ------------------------------ */

export interface SignSubject {
  /** la ligne étudiée (son écriture, sa clé) */
  line: Line;
  key: string;
  /** le membre comparé à 0 */
  side: Term;
  exp: ExpectedSignTable;
}

/**
 * La ligne dont on dresse le tableau de signes : la plus récente (ou le départ) qui
 * compare à 0 un membre dont chaque facteur a un signe connu. Seulement sur une feuille
 * « factor » sans hypothèse sur l'inconnue (le tableau va de −∞ à +∞).
 */
export function signSubject(ks: KernelSheetState): SignSubject | null {
  if (!ks.poly || ks.sys || ks.hyps.length) return null;
  const cands = [ks.start, ...ks.lines.filter((l) => l.trusted && !l.aux)].reverse();
  for (const l of cands) {
    if (l.s.k !== 'rel' || l.s.rel === '=') continue;
    const zl = isZeroTerm(l.s.l);
    const zr = isZeroTerm(l.s.r);
    if (zl === zr) continue;
    const side = zr ? l.s.l : l.s.r;
    const exp = expectedSignTable(side, ks.x);
    if (!exp) continue;
    let line: Line;
    try {
      line = parseText(statementText(l.s));
    } catch {
      continue;
    }
    return { line, key: l.K, side, exp };
  }
  return null;
}

/** Ce que l'éditeur de tableau propose : les lignes (facteurs, puis l'expression) et les valeurs. */
export interface SignTableSkeleton {
  x: string;
  /** la ligne étudiée, telle qu'elle s'écrit */
  subject: string;
  /** étiquettes des lignes du tableau, la dernière étant l'expression */
  labels: string[];
  /** valeurs de x à placer (les racines, et des nombres de la ligne) */
  candidates: string[];
  /** le tableau de cette ligne est déjà sur la feuille */
  done: boolean;
  /**
   * tableau de variations (lot 6.20) : la ligne de f — son étiquette (« f(x) »), des valeurs
   * à écrire sous les valeurs de x (l'énoncé donne f), les valeurs proposées
   */
  variation?: { label: string; values: boolean; candidates: string[] };
}

export function kernelSignTableSkeleton(ks: KernelSheetState): SignTableSkeleton | null {
  const sub = signSubject(ks);
  if (!sub) return null;
  return {
    x: ks.x,
    subject: lineText(sub.line),
    labels: sub.exp.rows.map((r) => r.label),
    candidates: signTableCandidates(sub.side, sub.exp),
    done: ks.tables.includes(sub.key),
  };
}

/** Une ligne « tableau de signes » : juste (2 HORA), ou la première case fausse. */
function signTableVerdict(ks: KernelSheetState, text: string): KernelVerdict {
  const A = [text];
  const t = parseSignTable(text);
  if (!t)
    return {
      kind: 'syntax',
      atoms: A,
      title: 'Tableau illisible',
      message: 'Je ne lis pas ce tableau : ouvre-le de nouveau depuis la palette.',
      badChip: 0,
    };
  // une ligne de flèches (tableau de variations, lot 6.20) n'a rien à faire ici
  if (t.f)
    return {
      kind: 'false',
      atoms: A,
      title: 'Un tableau de signes',
      message:
        'Ici, on étudie un signe : le tableau n’a pas de ligne de flèches. Ouvre-le de nouveau depuis la palette.',
      badChip: 0,
    };
  const sub = signSubject(ks);
  if (!sub)
    return {
      kind: 'unjustified',
      atoms: A,
      title: 'Factorise d’abord',
      message: ks.poly
        ? 'Le tableau de signes se dresse sur une ligne comparée à 0 dont chaque facteur a un signe connu : tout d’un côté, puis factorise (ou mets au même dénominateur).'
        : 'Ici, pas de tableau de signes : continue avec des lignes.',
    };
  if (ks.tables.includes(sub.key))
    return {
      kind: 'dup',
      atoms: A,
      title: 'Déjà dressé',
      message: 'Le tableau de cette ligne est déjà sur ta feuille : lis-le pour conclure.',
    };
  const issue = checkSignTable(sub.exp, t);
  if (issue)
    return {
      kind: 'false',
      atoms: A,
      title: issue.title,
      message: issue.message,
      badChip: 0,
      ...(issue.row !== undefined
        ? { cell: { row: issue.row, ...(issue.col !== undefined ? { col: issue.col } : {}) } }
        : {}),
    };
  return {
    kind: 'step',
    atoms: A,
    hora: 2,
    rule: {
      id: POLY_STEP_IDS.signTableFill,
      card: 'Inéquations',
      label: 'Tableau de signes',
      hora: 2,
      why: 'Chaque facteur change de signe en sa racine ; la règle des signes donne le signe de l’expression sur chaque intervalle. Une valeur qui annule le dénominateur est interdite (‖).',
    },
    table: sub.key,
  };
}
